import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  CSSProperties,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
} from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Dropdown,
  Input,
  Menu,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "@arco-design/web-react";
import type { SelectProps } from "@arco-design/web-react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  FilePlus2,
  LoaderCircle,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  RefreshCw,
  Route,
  SendHorizontal,
} from "lucide-react";

import { buildLegacyJumpUrl, useCurrentAuthUser } from "../../auth";
import type { AuthUser } from "../../auth";
import {
  canRetryItineraryPreviewLoad,
  ITINERARY_PERMISSIONS,
  ItineraryClientDetail,
  itineraryPreviewLoadErrorText,
  itineraryPreviewRuntime,
} from "../../itinerary";
import { derivePublicItinerary } from "../../itinerary/service/itineraryOutputService";
import { canUsePermission } from "../../../lib/auth";
import "../../itinerary/ui/itineraryEditor.css";
import {
  customizationRoleLabels,
  customizationSourceLabels,
  customizationStatusColors,
} from "../config";
import { fetchCustomizationCurrentUserTraceRole } from "../repo/customizationRepo";
import { useCustomizationWorkbenchRuntime } from "../runtime/useCustomizationWorkbenchRuntime";
import { CustomizationQuickItineraryDetails } from "./CustomizationQuickItineraryDetails";
import { CustomizationOpportunityBindingContent } from "./CustomizationOpportunityBindingContent";
import { CustomizationItineraryPreviewHost } from "./CustomizationItineraryPreviewHost";
import { DispatcherSelectionField, DispatcherSelectionModal, usePrefetchDispatcherSelectionCandidates } from "./DispatcherSelectionModal";
import type { DemandRequiredStatus } from "../service/customizationService";
import {
  buildDemandDisplayGroups,
  getDemandRequiredStatus,
  getDemandUpdatedAt,
  isDestinationDemandField,
  isSocialCustomization,
  isRequiredDemandField,
  normalizeDemandMissingFields,
  normalizeDemandReviewReasons,
} from "../service/customizationService";
import { resolveManualItineraryCreationBlock } from "../service/manualItineraryCreationGate";
import { filterProjectEditNameOptionsForSearch } from "../service/filterProjectEditNameOptionsForSearch";
import type {
  CustomizationDemandDisplayField,
  CustomizationDemandDisplayGroup,
  CustomizationDemandDraft,
  CreateCustomizationDemandResult,
  CustomizationDispatcherNotificationResult,
  CustomizationDispatcherSelectionCandidate,
  CustomizationDemandFieldOptions,
  CustomizationDemandVersionOption,
  CustomizationProjectEditOptions,
  CustomizationProjectFieldOptions,
  CustomizationProjectSnapshot,
  CustomizationProjectUpdatePayload,
  CustomizationReferenceDemandCandidate,
  CustomizationRouteLinkRecognitionResult,
  CustomizationTraceCreate,
  CustomizationTraceItem,
  CustomizationTraceRole,
} from "../types";
import "./CustomizationWorkbenchPage.css";

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

const fallbackStatusColor = { color: "#475569", bgColor: "#f8fafc" } as const;
const CUSTOMIZATION_WORKBENCH_WIDE_MIN = 1760;
const CUSTOMIZATION_WORKBENCH_COMPACT_MIN = 900;

type CustomizationWorkbenchLayoutMode = "wide" | "compact" | "stacked";

/** 读取行程计划智能体 iframe 基址，开发环境由本地 frontend/.env 控制。 */
function getLegacyItineraryBaseUrl(): string {
  const configuredBaseUrl = import.meta.env.VITE_CUSTOMIZATION_LEGACY_ITINERARY_BASE_URL
    ?.trim()
    .replace(/\/+$/, "");
  if (configuredBaseUrl && configuredBaseUrl !== "undefined") {
    return configuredBaseUrl;
  }
  return "";
}

type Props = {
  projectId: string;
  opportunityId?: number;
  legacyJiDiaoNo?: string;
  hideLinkedItineraryTraces?: boolean;
  onProjectChanged?: (projectId: string) => void;
  onNewCustomizationCreated?: (projectId: string) => void;
};

type DemandVersionRequiredStatusView = Pick<
  DemandRequiredStatus,
  "missingFields" | "reviewReasons" | "status"
>;

type ProjectEditFieldKey =
  | "primarySchoolId"
  | "customerId"
  | "contactId"
  | "majorNames"
  | "provinces"
  | "pointNames"
  | "stayAreaNames"
  | "destinationSummary"
  | "departureStartDate"
  | "durationDays"
  | "studentCount"
  | "teacherCount"
  | "budgetPerPerson"
  | "customerType"
  | "tripType"
  | "signType"
  | "dispatcherUserId"
  | "operatingCompany"
  | "teachingRequirements"
  | "specialRequirements"
  | "schoolPickupRequirement"
  | "invoiceRequirement"
  | "remark"
  | "teacherMealRequirement"
  | "teacherTicketRequirement"
  | "teacherTransportRequirement"
  | "teacherAccommodationRequirement"
  | "roundTripTransportNote"
  | "localTransportNote"
  | "accommodationNote"
  | "guideCount"
  | "guideNote"
  | "includeTickets"
  | "ticketNote"
  | "includeMeals"
  | "mealStandards"
  | "mealNote"
  | "companyVisitCount"
  | "companyVisitNote"
  | "intangibleHeritageCount"
  | "intangibleHeritageNote";

const allProjectEditFieldKeys: ProjectEditFieldKey[] = [
  "primarySchoolId",
  "customerId",
  "contactId",
  "provinces",
  "majorNames",
  "pointNames",
  "stayAreaNames",
  "departureStartDate",
  "durationDays",
  "studentCount",
  "teacherCount",
  "budgetPerPerson",
  "customerType",
  "tripType",
  "signType",
  "schoolPickupRequirement",
  "operatingCompany",
  "dispatcherUserId",
  "teachingRequirements",
  "specialRequirements",
  "invoiceRequirement",
  "remark",
  "teacherMealRequirement",
  "teacherTicketRequirement",
  "teacherTransportRequirement",
  "teacherAccommodationRequirement",
  "roundTripTransportNote",
  "localTransportNote",
  "accommodationNote",
  "guideCount",
  "guideNote",
  "includeTickets",
  "ticketNote",
  "includeMeals",
  "mealStandards",
  "mealNote",
  "companyVisitCount",
  "companyVisitNote",
  "intangibleHeritageCount",
  "intangibleHeritageNote",
];

const projectEditFieldLabels: Record<ProjectEditFieldKey, string> = {
  primarySchoolId: "出发高校/组织",
  customerId: "客户",
  contactId: "客户联系人",
  provinces: "目的地省份",
  majorNames: "所学专业",
  pointNames: "考察点",
  stayAreaNames: "目的地住宿点",
  destinationSummary: "目的地",
  departureStartDate: "出发日期",
  durationDays: "行程天数",
  studentCount: "学生人数",
  teacherCount: "老师人数",
  budgetPerPerson: "人均预算区间",
  customerType: "定制用户类型",
  tripType: "出行类型",
  signType: "签单类型",
  dispatcherUserId: "第一计调",
  operatingCompany: "操作公司",
  teachingRequirements: "教学要求",
  specialRequirements: "特殊需求",
  schoolPickupRequirement: "往返学校接送要求",
  invoiceRequirement: "发票要求",
  remark: "备注",
  teacherMealRequirement: "老师餐饮要求",
  teacherTicketRequirement: "老师门票要求",
  teacherTransportRequirement: "老师交通要求",
  teacherAccommodationRequirement: "老师住宿要求",
  roundTripTransportNote: "行程大交通工具",
  localTransportNote: "大巴车",
  accommodationNote: "团队住宿",
  guideCount: "领队/导游数",
  guideNote: "领队/导游要求",
  includeTickets: "是否含考察点门票",
  ticketNote: "考察点门票要求",
  includeMeals: "全程是否含餐",
  mealStandards: "团队餐饮类型",
  mealNote: "团队餐饮要求",
  companyVisitCount: "考察企业数",
  companyVisitNote: "考察企业要求",
  intangibleHeritageCount: "考察非遗数",
  intangibleHeritageNote: "考察非遗要求",
};

const projectIdentityEditFieldKeys: ProjectEditFieldKey[] = [
  "primarySchoolId",
  "customerId",
  "contactId",
];

const projectPeopleCountEditFieldKeys: ProjectEditFieldKey[] = [
  "studentCount",
  "teacherCount",
  "guideCount",
];

const projectEditRemoteOptionFieldKeys: ProjectEditFieldKey[] = [
  "primarySchoolId",
  "customerId",
  "contactId",
  "majorNames",
  "provinces",
  "stayAreaNames",
  "dispatcherUserId",
];

const projectEditDeferredOptionFieldKeys: ProjectEditFieldKey[] = [
  "customerId",
  "contactId",
];

const projectArrayEditFieldKeys: ProjectEditFieldKey[] = [
  "provinces",
  "majorNames",
  "pointNames",
  "stayAreaNames",
  "mealStandards",
];

const projectTextEditFieldKeys: ProjectEditFieldKey[] = [
  "destinationSummary",
  "departureStartDate",
  "budgetPerPerson",
  "customerType",
  "tripType",
  "signType",
  "operatingCompany",
  "teachingRequirements",
  "specialRequirements",
  "schoolPickupRequirement",
  "invoiceRequirement",
  "remark",
  "teacherMealRequirement",
  "teacherTicketRequirement",
  "teacherTransportRequirement",
  "teacherAccommodationRequirement",
  "roundTripTransportNote",
  "localTransportNote",
  "accommodationNote",
  "guideNote",
  "ticketNote",
  "mealNote",
  "companyVisitNote",
  "intangibleHeritageNote",
];

const projectInlineNumberEditFieldKeys: ProjectEditFieldKey[] = [
  "durationDays",
  "guideCount",
  "companyVisitCount",
  "intangibleHeritageCount",
];

const projectInlineTextInputEditFieldKeys: ProjectEditFieldKey[] = [
  "budgetPerPerson",
];

const projectInlineTextAreaEditFieldKeys: ProjectEditFieldKey[] = [
  "teachingRequirements",
  "specialRequirements",
  "invoiceRequirement",
  "remark",
  "teacherMealRequirement",
  "teacherTicketRequirement",
  "teacherTransportRequirement",
  "teacherAccommodationRequirement",
  "roundTripTransportNote",
  "localTransportNote",
  "accommodationNote",
  "guideNote",
  "ticketNote",
  "mealNote",
  "companyVisitNote",
  "intangibleHeritageNote",
];

const projectInlineEditFieldKeys = new Set<ProjectEditFieldKey>([
  ...projectInlineNumberEditFieldKeys,
  ...projectInlineTextInputEditFieldKeys,
  ...projectInlineTextAreaEditFieldKeys,
]);

type InlineProjectEditorState = {
  displayFieldKey: string;
  fieldKey: ProjectEditFieldKey;
};

const fallbackSchoolPickupRequirementOptions = ["不接送", "单接", "单送", "接送"];
const demandFieldCardCopyableStyle: CSSProperties = { userSelect: "text" };
type ProjectEditSelectVirtualListProps = {
  height: number;
  itemHeight: number;
  threshold: number;
  isStaticItemHeight: boolean;
};
type ProjectEditSelectTriggerProps = NonNullable<SelectProps["triggerProps"]>;
const projectEditSelectVirtualListProps: ProjectEditSelectVirtualListProps = {
  height: 288,
  itemHeight: 36,
  threshold: 20,
  isStaticItemHeight: true,
};
const projectEditSingleFieldSelectVirtualListProps: ProjectEditSelectVirtualListProps = {
  ...projectEditSelectVirtualListProps,
  height: 220,
};
const projectEditSelectTriggerProps: ProjectEditSelectTriggerProps = {
  autoFitPosition: true,
  boundaryDistance: { right: 8, bottom: 8 },
};
const projectEditSingleFieldSelectTriggerProps: ProjectEditSelectTriggerProps = {
  ...projectEditSelectTriggerProps,
  position: "top",
  boundaryDistance: { right: 8, top: 8 },
};
/** 多选勾选后清空搜索词，避免已选标签旁残留上次关键字。 */
const projectEditMultipleSelectSearchProps = { retainInputValueWhileSelect: false } as const;
const projectEditMultipleSelectMaxTagCount = {
  count: 32,
  render: (invisibleTagCount: number) => `+${invisibleTagCount}`,
  showPopover: false,
} as const;
const tracePreviewDebounceMs = 320;
const projectEditorFullFormMountDelayMs = 24;
const tracePreviewReferencePattern = /\bJD\d{6,}(?:_\d+)?\b/i;
const tracePreviewRouteLinkPattern =
  /https?:\/\/www\.yishulvxing\.com\/Form_New\/Article\/ArticleRouteDetailP_New\.aspx\?(?:[^#\s]*&)?(?:ID|ArticleId)=/i;
const projectEditObjectSelectFilterOption = (
  inputValue: string,
  option: { props?: { children?: unknown; value?: unknown } },
) => {
  const keyword = inputValue.trim().toLowerCase();
  if (!keyword) {
    return true;
  }
  return [option.props?.children, option.props?.value].some((item) =>
    String(item ?? "").toLowerCase().includes(keyword),
  );
};

type WorkbenchNotice = {
  title: string;
  message: string;
};

type ProjectEditOptionScopeForm = Pick<
  CustomizationProjectUpdatePayload,
  | "primarySchoolId"
  | "customerId"
  | "contactId"
  | "dispatcherUserId"
  | "provinces"
  | "pointNames"
  | "stayAreaNames"
>;

type ProjectEditPatch = Partial<CustomizationProjectUpdatePayload>;
type ProjectEditorDraftTarget = "full" | "single";
type ProjectEditOptionMergeScope = ProjectEditFieldKey[] | "all";

const emptyCollegeCandidates: CustomizationProjectEditOptions["collegeCandidates"] = [];
const emptyCustomerCandidates: CustomizationProjectEditOptions["customerCandidates"] = [];
const emptyContactCandidates: CustomizationProjectEditOptions["contactCandidates"] = [];
const emptyMajorCandidates: CustomizationProjectEditOptions["majorCandidates"] = [];
/** 判断编辑 patch 是否显式包含某个字段。 */
function hasProjectEditPatchKey<K extends keyof CustomizationProjectUpdatePayload>(
  patch: ProjectEditPatch,
  key: K,
): boolean {
  return Object.prototype.hasOwnProperty.call(patch, key);
}

/** 合并编辑草稿 patch，并处理高校切换带来的客户/联系人联动清空。 */
function applyProjectEditDraftPatch(
  current: CustomizationProjectUpdatePayload,
  patch: ProjectEditPatch,
): CustomizationProjectUpdatePayload {
  const next = { ...current, ...patch };
  if (hasProjectEditPatchKey(patch, "primarySchoolId")) {
    // 用空字符串显式清空，避免草稿合并删掉 undefined 后旧客户被写回。
    next.customerId = "";
    next.customerName = "";
    next.contactId = "";
    next.contactName = "";
  }
  return next;
}

/** 小范围编辑里，客户/联系人/考察点候选延迟到下拉真正打开后再加载，保证弹窗首帧先响应。 */
function shouldDeferProjectEditOptionsLoad(fields: ProjectEditFieldKey[]): boolean {
  if (fields.length === allProjectEditFieldKeys.length) {
    return false;
  }
  return fields.some((field) => projectEditDeferredOptionFieldKeys.includes(field));
}

/** 单项目需求定制工作台页面主体。 */
export function CustomizationWorkbenchFeaturePage({
  projectId,
  opportunityId,
  legacyJiDiaoNo,
  hideLinkedItineraryTraces,
  onProjectChanged,
  onNewCustomizationCreated,
}: Props) {
  const currentUserQuery = useCurrentAuthUser();
  const canCreateProject = canUsePermission(currentUserQuery.data, "customization.project:create");
  const canAddTrace = canUsePermission(currentUserQuery.data, "customization.project:trace");
  const canUpdateProject = canUsePermission(currentUserQuery.data, "customization.project:update");
  const canGenerateDemand = canUsePermission(
    currentUserQuery.data,
    "customization.project:generate",
  );
  const canBindOpportunity = canUsePermission(
    currentUserQuery.data,
    "customization.project:bind_opportunity",
  );
  const canNotifyDispatcherPermission = canUsePermission(
    currentUserQuery.data,
    "customization.project:notify",
  );
  const canCreateItinerary = canUsePermission(
    currentUserQuery.data,
    ITINERARY_PERMISSIONS.create,
  );
  const canGenerateItinerary = canUsePermission(
    currentUserQuery.data,
    ITINERARY_PERMISSIONS.generate,
  );
  const canEditItinerary =
    canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.editorAccess) &&
    canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.update);
  const canCreateOpportunity = canUsePermission(
    currentUserQuery.data,
    "crm.opportunity:access",
  );
  const currentUserTraceRoleQuery = useQuery({
    queryKey: ["customization", "current-user-trace-role"],
    queryFn: fetchCustomizationCurrentUserTraceRole,
    staleTime: 5 * 60 * 1000,
  });
  const runtime = useCustomizationWorkbenchRuntime(projectId, {
    canEditQuickItinerary: canUsePermission(currentUserQuery.data, "customization.project:quick_itinerary"),
    quickItineraryViewerId: currentUserQuery.data?.username,
    opportunityId,
    legacyJiDiaoNo,
    hideLinkedItineraryTraces,
    onProjectChanged,
    onNewCustomizationCreated,
  });
  usePrefetchDispatcherSelectionCandidates(Boolean(projectId) && projectId !== "new");
  const [isDemandCollapsed, setIsDemandCollapsed] = useState(false);
  const [workbenchLayoutMode, setWorkbenchLayoutMode] =
    useState<CustomizationWorkbenchLayoutMode>("wide");
  const [itineraryDrawerVisible, setItineraryDrawerVisible] = useState(false);
  const [previewItineraryId, setPreviewItineraryId] = useState<string | null>(null);
  const [createNewConfirmVisible, setCreateNewConfirmVisible] = useState(false);
  const [historicalBaseConfirmVisible, setHistoricalBaseConfirmVisible] = useState(false);
  const [projectEditorVisible, setProjectEditorVisible] = useState(false);
  const [projectEditorSubmitting, setProjectEditorSubmitting] = useState(false);
  const [projectEditorContentReady, setProjectEditorContentReady] = useState(true);
  const [projectEditorFields, setProjectEditorFields] = useState<ProjectEditFieldKey[]>(allProjectEditFieldKeys);
  const [projectEditOptionsHydrationEnabled, setProjectEditOptionsHydrationEnabled] = useState(false);
  const [inlineProjectEditor, setInlineProjectEditor] = useState<InlineProjectEditorState | null>(null);
  const [inlineProjectEditDraft, setInlineProjectEditDraft] =
    useState<CustomizationProjectUpdatePayload | null>(null);
  const [projectForm, setProjectForm] = useState<CustomizationProjectUpdatePayload>(() =>
    buildProjectEditPayload(null),
  );
  const shadowFormRef = useRef<CustomizationProjectUpdatePayload>(buildProjectEditPayload(null));
  const projectEditorLiveDraftRef = useRef<CustomizationProjectUpdatePayload | null>(null);
  const [singleProjectEditDraft, setSingleProjectEditDraft] =
    useState<CustomizationProjectUpdatePayload | null>(null);
  const singleShadowFormRef = useRef<CustomizationProjectUpdatePayload | null>(null);
  const [projectEditOptions, setProjectEditOptions] = useState<CustomizationProjectEditOptions | null>(null);
  const [projectEditOptionsSchoolId, setProjectEditOptionsSchoolId] = useState<string | null>(null);
  const [projectEditOptionsLoading, setProjectEditOptionsLoading] = useState(false);
  const [projectPointNameOptions, setProjectPointNameOptions] = useState<string[]>([]);
  const [projectPointNamesLoading, setProjectPointNamesLoading] = useState(false);
  const [noticeDialog, setNoticeDialog] = useState<WorkbenchNotice | null>(null);
  const [versionSuccessDialog, setVersionSuccessDialog] = useState<WorkbenchNotice | null>(null);
  const [dispatcherConfirmVisible, setDispatcherConfirmVisible] = useState(false);
  const [dispatcherConfirmActionText, setDispatcherConfirmActionText] = useState("");
  const [dispatcherSelectionVisible, setDispatcherSelectionVisible] = useState(false);
  const [dispatcherSelectionInitial, setDispatcherSelectionInitial] = useState<{
    userId?: string;
    displayName?: string;
  }>({});
  const dispatcherSelectionResolverRef = useRef<
    ((candidate: CustomizationDispatcherSelectionCandidate | null) => void) | null
  >(null);
  const [itineraryAgentDemandIds, setItineraryAgentDemandIds] = useState<Set<string>>(
    () => new Set(),
  );
  const traceContentDraftRef = useRef("");
  const workbenchContainerRef = useRef<HTMLElement | null>(null);
  const itineraryEntryButtonRef = useRef<HTMLButtonElement | null>(null);
  const projectEditOptionsRequestRef = useRef(0);
  const collegeCandidateSearchRequestRef = useRef(0);
  const collegeCandidateSearchInFlightRef = useRef(false);
  const collegeCandidateSearchTimerRef = useRef<number | null>(null);
  const projectEditOptionsLoadingSchoolRef = useRef<string | null>(null);
  const projectEditOptionsCacheRef = useRef<Record<string, CustomizationProjectEditOptions>>({});
  const projectEditOptionsPendingRef = useRef<Record<string, Promise<CustomizationProjectEditOptions | null>>>({});
  const projectPointNamesByKeywordRef = useRef<Record<string, string[]>>({});
  const projectPointNamesPendingRef = useRef<Record<string, Promise<string[]>>>({});
  const projectPointNameSearchRequestRef = useRef(0);
  const projectEditorContentReadyTimerRef = useRef<number | null>(null);
  const pendingSingleProjectSaveRef = useRef<CustomizationProjectUpdatePayload | null>(null);
  const singleProjectSaveClosingRef = useRef(false);
  const noticeDialogKeyRef = useRef("");

  useLayoutEffect(() => {
    const container = workbenchContainerRef.current;
    if (!container) {
      return;
    }
    const updateLayoutMode = (width: number) => {
      setWorkbenchLayoutMode(resolveCustomizationWorkbenchLayoutMode(width));
    };
    const handleWindowResize = () => updateLayoutMode(container.clientWidth);
    handleWindowResize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", handleWindowResize);
      return () => window.removeEventListener("resize", handleWindowResize);
    }

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.clientWidth;
      updateLayoutMode(width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (workbenchLayoutMode !== "wide") {
      return;
    }
    setItineraryDrawerVisible(false);
    setIsDemandCollapsed(false);
  }, [workbenchLayoutMode]);

  const demandGroups = useMemo(
    () => buildDemandDisplayGroups(runtime.displayDemand, runtime.project),
    [runtime.displayDemand, runtime.project],
  );
  const displayDemandGroups = useMemo(
    () => buildRequirementDemandDisplayGroups(demandGroups, runtime.project?.customerType),
    [demandGroups, runtime.project?.customerType],
  );
  const requiredStatus = getDemandRequiredStatus(runtime.displayDemand);
  const requiredStatusEditFields = useMemo(
    () => resolveProjectEditFieldsByRequiredStatus(requiredStatus),
    [requiredStatus],
  );
  const canEditRequiredStatus = canUpdateProject && requiredStatusEditFields.length > 0;
  const generationBaseDemand =
    runtime.hasManualDraft && !runtime.historicalGenerationBase
      ? runtime.currentDemand ?? runtime.displayDemand
      : runtime.displayDemand ?? runtime.currentDemand;
  const generationBaseRequiredStatus = getDemandRequiredStatus(generationBaseDemand);
  const hasGeneratedDemand = Boolean(runtime.displayDemand);
  const hasCurrentDemand = Boolean(generationBaseDemand);
  const generationBaseVersionSeq = generationBaseDemand?.versionSeq ?? 0;
  const nextDemandVersionSeq =
    Math.max(
      generationBaseVersionSeq,
      ...runtime.demandVersions.map((version) => version.versionSeq ?? 0),
    ) + 1;
  const isPersistedGenerationBaseIncomplete =
    hasCurrentDemand && generationBaseRequiredStatus.status !== "complete";
  const canUpdatePersistedV1 =
    isPersistedGenerationBaseIncomplete && generationBaseVersionSeq === 1;
  // V2+ 仅在持久化基线和当前草稿都缺项时阻止继续生成；
  // 草稿补齐旧缺项或从完整版本另建缺项版本时，都应创建下一版而不覆盖历史。
  const hasAbnormalIncompleteLaterVersion =
    isPersistedGenerationBaseIncomplete &&
    requiredStatus.status !== "complete" &&
    generationBaseVersionSeq > 1;
  const hasGenerationChanges =
    runtime.selectedTraceIds.length > 0 ||
    Boolean(runtime.selectedReferenceDemandId) ||
    runtime.hasManualDraft;
  const generationAction = hasCurrentDemand
    ? canUpdatePersistedV1
      ? "update_current"
      : "create_version"
    : "create_new";
  const canUseGenerationAction =
    generationAction === "create_new" ? canCreateProject : canGenerateDemand;
  const canGenerate =
    canUseGenerationAction &&
    (!runtime.hasManualDraft || canUpdateProject) &&
    hasGenerationChanges &&
    !runtime.generating &&
    !hasAbnormalIncompleteLaterVersion;
  const canCreateNewCustomization =
    canCreateProject &&
    (!runtime.hasManualDraft || canUpdateProject) &&
    Boolean(runtime.displayDemand?.id) &&
    !runtime.generating;
  const isDisplayedDemandComplete = requiredStatus.status === "complete";
  const canStartItineraryGeneration =
    canGenerateItinerary &&
    !runtime.hasManualDraft &&
    !runtime.isDemandVersionSwitching &&
    isDisplayedDemandComplete &&
    Boolean(runtime.displayDemand?.id) &&
    !runtime.itineraryGenerating &&
    !runtime.generating;
  const canNotifyDispatcher =
    canNotifyDispatcherPermission &&
    !runtime.hasManualDraft &&
    Boolean(runtime.displayDemand?.id) &&
    !runtime.dispatcherNotifying &&
    !runtime.generating;
  const generationStatusText = formatGenerationBusyText(runtime.selectedTraceIds.length);
  const primaryActionLabel = getPrimaryGenerationActionLabel({
    hasCurrentDemand,
    currentVersionSeq: generationBaseVersionSeq,
    nextVersionSeq: nextDemandVersionSeq,
    canUpdatePersistedV1,
  });
  const confirmationStatusText = hasAbnormalIncompleteLaterVersion
    ? `当前 V${generationBaseVersionSeq} 存在异常缺项，不能覆盖历史版本。`
    : runtime.hasManualDraft
      ? `请先${primaryActionLabel}，再生成行程或通知计调。`
      : hasGenerationChanges
        ? `当前变更将通过“${primaryActionLabel}”形成持久化版本。`
        : "当前版本没有待提交变更。";
  const generationActionTitle = formatGenerationActionTitle(primaryActionLabel);
  const shouldConfirmHistoricalBaseGeneration =
    Boolean(runtime.historicalGenerationBase) &&
    generationAction === "create_version" &&
    !hasAbnormalIncompleteLaterVersion;
  const selectedDemandId = runtime.displayDemand?.id ?? runtime.currentDemand?.id;
  const isDemandVersionGenerating = (version: CustomizationDemandVersionOption) =>
    itineraryAgentDemandIds.has(version.id) && !version.hasItinerary;
  const selectedDemandVersion = runtime.demandVersions.find((version) => version.id === selectedDemandId);
  const canEnterManualItinerary =
    Boolean(selectedDemandId) &&
    canCreateItinerary &&
    (!selectedDemandVersion?.hasItinerary || canEditItinerary);
  const manualItineraryActionLabel = selectedDemandVersion?.hasItinerary
    ? "人工编辑行程"
    : "人工创建行程";
  const resolveDemandVersionRequiredStatusView = (
    version: CustomizationDemandVersionOption,
  ): DemandVersionRequiredStatusView | undefined =>
    runtime.displayDemand?.id === version.id ? requiredStatus : undefined;
  const selectedOrderVersionKey =
    selectedDemandVersion?.orderVersionKey ?? runtime.displayOrderVersionKey;
  const itineraryModifierName = selectedDemandVersion?.itineraryModifiedByName?.trim();
  const viewableItineraryId = selectedDemandVersion?.latestItineraryId?.trim() || "";
  const showViewItineraryEntry = Boolean(viewableItineraryId || itineraryModifierName);
  const hasItinerary = Boolean(selectedDemandVersion?.hasItinerary);
  const panelItineraryId = viewableItineraryId;
  const currentUserRealDisplayName = resolveNotificationActorName(currentUserQuery.data);
  const defaultTraceRole: CustomizationTraceRole =
    currentUserTraceRoleQuery.data?.defaultRole ?? "sales";
  const dispatcherNotifyLabel = formatDispatcherNotifyLabel(runtime.project?.dispatcherUserName);
  const itineraryInsight = getDemandVersionItineraryInsight({
    hasDemand: Boolean(runtime.displayDemand?.id),
    isGenerating: runtime.itineraryGenerating,
    isAgentGenerating: Boolean(
      selectedDemandVersion && isDemandVersionGenerating(selectedDemandVersion),
    ),
    version: selectedDemandVersion,
  });
  const dispatcherActionOptions = useMemo(
    () =>
      buildDispatcherActionOptions({
        versionNumber: runtime.displayDemand?.versionNumber ?? selectedDemandVersion?.versionNumber,
        actorName: currentUserRealDisplayName,
        canReviewItinerary: Boolean(selectedDemandVersion?.hasItinerary && currentUserRealDisplayName),
      }),
    [
      currentUserRealDisplayName,
      runtime.displayDemand?.versionNumber,
      selectedDemandVersion?.hasItinerary,
      selectedDemandVersion?.versionNumber,
    ],
  );
  const dispatcherConfirmTargetName = runtime.project?.dispatcherUserName?.trim() || "当前项目第一计调";
  const dispatcherConfirmVersionNumber =
    runtime.displayDemand?.versionNumber ?? selectedDemandVersion?.versionNumber ?? "当前版本";
  const opportunityName = runtime.project?.name || "定制项目工作台";
  const isSingleProjectEditor = projectEditorFields.length === 1;
  const isCompactProjectEditorModal =
    isSingleProjectEditor ||
    (projectEditorFields.length < allProjectEditFieldKeys.length &&
      projectEditorFields.every((field) => projectIdentityEditFieldKeys.includes(field)));
  const projectEditorSaving = projectEditorSubmitting || runtime.projectSaving;
  const isScopedProjectEditor = projectEditorFields.length < allProjectEditFieldKeys.length;
  const projectEditorDialogSaving = isScopedProjectEditor ? false : projectEditorSaving;
  const shouldRenderProjectEditorModalContent = projectEditorVisible;
  const hasProjectEditRemoteOptions = projectEditorFields.some((field) =>
    projectEditRemoteOptionFieldKeys.includes(field),
  );
  const shouldDeferProjectEditOptionsUntilSelectOpen = shouldDeferProjectEditOptionsLoad(projectEditorFields);
  const shouldAutoLoadProjectEditOptions =
    hasProjectEditRemoteOptions && !shouldDeferProjectEditOptionsUntilSelectOpen;
  const shouldResolveProjectEditOptionsForForm =
    hasProjectEditRemoteOptions &&
    (!shouldDeferProjectEditOptionsUntilSelectOpen || projectEditOptionsHydrationEnabled);
  const projectEditFormForOptions =
    isSingleProjectEditor && singleProjectEditDraft ? singleProjectEditDraft : projectForm;
  const projectEditOptionScopeForm = useMemo(
    (): ProjectEditOptionScopeForm => ({
      primarySchoolId: projectEditFormForOptions.primarySchoolId,
      customerId: projectEditFormForOptions.customerId,
      contactId: projectEditFormForOptions.contactId,
      dispatcherUserId: projectEditFormForOptions.dispatcherUserId,
      provinces: projectEditFormForOptions.provinces,
      pointNames: projectEditFormForOptions.pointNames,
      stayAreaNames: projectEditFormForOptions.stayAreaNames,
    }),
    [
      projectEditFormForOptions.contactId,
      projectEditFormForOptions.customerId,
      projectEditFormForOptions.dispatcherUserId,
      projectEditFormForOptions.pointNames,
      projectEditFormForOptions.primarySchoolId,
      projectEditFormForOptions.provinces,
      projectEditFormForOptions.stayAreaNames,
    ],
  );
  const projectEditOptionsForForm = useMemo(
    () => {
      if (!shouldResolveProjectEditOptionsForForm) {
        return null;
      }
      return mergeProjectEditOptionsForForm(
        projectEditOptions,
        projectEditOptionScopeForm,
        runtime.project,
        runtime.displayDemand,
        isSingleProjectEditor ? projectEditorFields : "all",
      );
    },
    [
      isSingleProjectEditor,
      projectEditOptions,
      projectEditOptionScopeForm,
      projectEditorFields,
      runtime.project,
      runtime.displayDemand,
      shouldResolveProjectEditOptionsForForm,
    ],
  );

  const prefetchProjectEditOptionsForSchool = (schoolId: string) => {
    if (projectEditOptionsCacheRef.current[schoolId]) {
      return Promise.resolve(projectEditOptionsCacheRef.current[schoolId]);
    }
    const pending = projectEditOptionsPendingRef.current[schoolId];
    if (pending) {
      return pending;
    }
    const request = runtime
      .loadProjectEditOptions({
        primarySchoolId: schoolId || undefined,
      })
      .then((nextOptions) => {
        if (nextOptions) {
          projectEditOptionsCacheRef.current[schoolId] = nextOptions;
        }
        return nextOptions;
      })
      .finally(() => {
        delete projectEditOptionsPendingRef.current[schoolId];
      });
    projectEditOptionsPendingRef.current[schoolId] = request;
    return request;
  };

  const requestProjectEditOptionsForSchool = (schoolId: string, options?: { force?: boolean }) => {
    // 高校/组织远程搜索进行中时，禁止用学校维度缓存回填，避免冲掉关键词结果。
    if (collegeCandidateSearchInFlightRef.current && !options?.force) {
      return;
    }
    const cached = projectEditOptionsCacheRef.current[schoolId];
    if (!options?.force && cached) {
      setProjectEditOptions(cached);
      setProjectEditOptionsSchoolId(schoolId);
      setProjectEditOptionsLoading(false);
      return;
    }
    if (
      !options?.force &&
      projectEditOptions &&
      projectEditOptionsSchoolId === schoolId
    ) {
      return;
    }
    if (!options?.force && projectEditOptionsLoadingSchoolRef.current === schoolId) {
      return;
    }
    const requestId = ++projectEditOptionsRequestRef.current;
    projectEditOptionsLoadingSchoolRef.current = schoolId;
    setProjectEditOptionsLoading(true);
    void prefetchProjectEditOptionsForSchool(schoolId)
      .then((nextOptions) => {
        if (
          (!options?.force && collegeCandidateSearchInFlightRef.current) ||
          requestId !== projectEditOptionsRequestRef.current ||
          !nextOptions
        ) {
          return;
        }
        setProjectEditOptions(nextOptions);
        setProjectEditOptionsSchoolId(schoolId);
        projectEditOptionsCacheRef.current[schoolId] = nextOptions;
      })
      .finally(() => {
        if (requestId === projectEditOptionsRequestRef.current) {
          projectEditOptionsLoadingSchoolRef.current = null;
          if (!collegeCandidateSearchInFlightRef.current) {
            setProjectEditOptionsLoading(false);
          }
        }
      });
  };

  const requestProjectEditOptionsForCurrentSchool = (options?: { force?: boolean }) => {
    setProjectEditOptionsHydrationEnabled(true);
    const activeForm =
      isSingleProjectEditor && singleShadowFormRef.current
        ? singleShadowFormRef.current
        : shadowFormRef.current;
    const schoolId = activeForm.primarySchoolId ?? "";
    const loadedForSchool = projectEditOptionsSchoolId === schoolId;
    const hasDependentCandidates =
      (projectEditOptions?.customerCandidates.length ?? 0) > 0 ||
      (projectEditOptions?.contactCandidates.length ?? 0) > 0;
    const force = Boolean(options?.force || (schoolId && loadedForSchool && !hasDependentCandidates));
    requestProjectEditOptionsForSchool(schoolId, force ? { force: true } : options);
  };

  const cancelCollegeCandidateSearch = () => {
    if (collegeCandidateSearchTimerRef.current !== null) {
      window.clearTimeout(collegeCandidateSearchTimerRef.current);
      collegeCandidateSearchTimerRef.current = null;
    }
    collegeCandidateSearchRequestRef.current += 1;
    collegeCandidateSearchInFlightRef.current = false;
  };

  const requestCollegeCandidatesSearch = (keyword = "") => {
    setProjectEditOptionsHydrationEnabled(true);
    const activeForm =
      isSingleProjectEditor && singleShadowFormRef.current
        ? singleShadowFormRef.current
        : shadowFormRef.current;
    const schoolId = activeForm.primarySchoolId ?? "";
    const normalizedKeyword = keyword.trim();
    // 丢弃页面打开初期缓存的「仅高校」列表，强制走网络；并作废学校维度在途请求。
    delete projectEditOptionsCacheRef.current[schoolId];
    delete projectEditOptionsPendingRef.current[schoolId];
    projectEditOptionsRequestRef.current += 1;
    projectEditOptionsLoadingSchoolRef.current = null;
    const requestId = ++collegeCandidateSearchRequestRef.current;
    collegeCandidateSearchInFlightRef.current = true;
    setProjectEditOptionsLoading(true);
    void runtime
      .loadProjectEditOptions({
        keyword: normalizedKeyword || undefined,
        primarySchoolId: schoolId || undefined,
      })
      .then((nextOptions) => {
        if (requestId !== collegeCandidateSearchRequestRef.current || !nextOptions) {
          return;
        }
        setProjectEditOptions((current) => {
          if (!normalizedKeyword) {
            // 无关键词时整包替换，避免把上一所学校的客户/联系人带进新学校。
            projectEditOptionsCacheRef.current[schoolId] = nextOptions;
            return nextOptions;
          }
          // 关键词主要用于高校/组织检索；保留已加载的客户/联系人等候选，避免被同关键词误过滤。
          return {
            ...(current ?? nextOptions),
            collegeCandidates: nextOptions.collegeCandidates,
          };
        });
        setProjectEditOptionsSchoolId(schoolId);
      })
      .finally(() => {
        if (requestId === collegeCandidateSearchRequestRef.current) {
          collegeCandidateSearchInFlightRef.current = false;
          setProjectEditOptionsLoading(false);
        }
      });
  };

  const scheduleCollegeCandidatesSearch = (keyword = "") => {
    if (collegeCandidateSearchTimerRef.current !== null) {
      window.clearTimeout(collegeCandidateSearchTimerRef.current);
    }
    collegeCandidateSearchTimerRef.current = window.setTimeout(() => {
      collegeCandidateSearchTimerRef.current = null;
      requestCollegeCandidatesSearch(keyword);
    }, 280);
  };

  const requestProjectPointNames = (keyword = "") => {
    const normalizedKeyword = keyword.trim();
    const stayAreaNames = splitEditableNameList(projectEditFormForOptions.stayAreaNames);
    const stayAreaCacheKey = stayAreaNames.join("\u0001");
    const cacheKey = `${normalizedKeyword || "__initial__"}::${stayAreaCacheKey || "__no_stay__"}`;
    const requestId = ++projectPointNameSearchRequestRef.current;
    const cached = projectPointNamesByKeywordRef.current[cacheKey];
    if (cached) {
      setProjectPointNameOptions(cached);
      setProjectPointNamesLoading(false);
      return;
    }
    setProjectPointNamesLoading(true);
    const pending =
      projectPointNamesPendingRef.current[cacheKey] ??
      runtime
        .loadProjectPointNames({
          keyword: normalizedKeyword || undefined,
          stayAreaNames: stayAreaNames.length ? stayAreaNames : undefined,
        })
        .then((nextOptions) => {
          projectPointNamesByKeywordRef.current[cacheKey] = nextOptions;
          return nextOptions;
        })
        .finally(() => {
          delete projectPointNamesPendingRef.current[cacheKey];
        });
    projectPointNamesPendingRef.current[cacheKey] = pending;
    void pending
      .then((nextOptions) => {
        if (requestId === projectPointNameSearchRequestRef.current) {
          setProjectPointNameOptions(nextOptions);
        }
      })
      .finally(() => {
        if (requestId === projectPointNameSearchRequestRef.current) {
          setProjectPointNamesLoading(false);
        }
      });
  };

  const clearProjectEditorContentReadyTimer = () => {
    if (projectEditorContentReadyTimerRef.current !== null) {
      window.clearTimeout(projectEditorContentReadyTimerRef.current);
      projectEditorContentReadyTimerRef.current = null;
    }
  };

  const scheduleProjectEditorContentReady = (immediate: boolean) => {
    clearProjectEditorContentReadyTimer();
    if (immediate) {
      setProjectEditorContentReady(true);
      return;
    }
    setProjectEditorContentReady(false);
  };

  useEffect(
    () => () => {
      clearProjectEditorContentReadyTimer();
    },
    [],
  );

  useEffect(() => {
    projectPointNameSearchRequestRef.current += 1;
    projectPointNamesByKeywordRef.current = {};
    projectPointNamesPendingRef.current = {};
    setProjectPointNameOptions([]);
    setProjectPointNamesLoading(false);
  }, [projectId]);

  useEffect(() => {
    if (!projectEditorVisible || isSingleProjectEditor || projectEditorContentReady) {
      return;
    }
    clearProjectEditorContentReadyTimer();
    projectEditorContentReadyTimerRef.current = window.setTimeout(() => {
      projectEditorContentReadyTimerRef.current = null;
      setProjectEditorContentReady(true);
    }, projectEditorFullFormMountDelayMs);
  }, [isSingleProjectEditor, projectEditorContentReady, projectEditorVisible]);

  useEffect(() => {
    // 弹窗打开期间保留本地草稿，避免切换学校后被项目快照把旧客户写回。
    if (projectEditorVisible) {
      return;
    }
    const nextProjectForm = buildProjectEditPayload(
      runtime.project,
      runtime.displayDemand,
      runtime.projectFieldOptions,
    );
    shadowFormRef.current = nextProjectForm;
    projectEditorLiveDraftRef.current = nextProjectForm;
    setProjectForm(nextProjectForm);
  }, [
    projectEditorVisible,
    runtime.displayDemand,
    runtime.project,
    runtime.projectFieldOptions,
  ]);

  useEffect(() => {
    if (!runtime.project?.id) {
      return;
    }
    void prefetchProjectEditOptionsForSchool(runtime.project.primarySchoolId ?? "");
  }, [runtime.project?.id, runtime.project?.primarySchoolId]);

  useEffect(() => {
    const optionValues = dispatcherActionOptions.map((option) => option.value);
    setDispatcherConfirmActionText((current) =>
      current && optionValues.includes(current) ? current : "",
    );
  }, [dispatcherActionOptions]);

  useEffect(() => {
    if (!projectEditorVisible || !shouldAutoLoadProjectEditOptions) {
      return;
    }
    requestProjectEditOptionsForCurrentSchool();
  }, [projectEditorVisible, projectEditFormForOptions.primarySchoolId, shouldAutoLoadProjectEditOptions]);

  useEffect(() => {
    if (!projectEditorVisible || !projectEditorFields.includes("pointNames")) {
      return;
    }
    requestProjectPointNames("");
  }, [projectEditorVisible, projectEditorFields, projectEditFormForOptions.stayAreaNames]);

  useEffect(() => {
    const notice = resolveWorkbenchNotice({
      mutationErrorMessage: runtime.mutationErrorMessage,
      errorMessage: runtime.errorMessage,
      referenceErrorMessage: runtime.referenceErrorMessage,
      routeLinkErrorMessage: runtime.routeLinkErrorMessage,
    });
    if (!notice) {
      noticeDialogKeyRef.current = "";
      return;
    }
    const noticeKey = `${notice.title}:${notice.message}`;
    if (noticeDialogKeyRef.current === noticeKey) {
      return;
    }
    noticeDialogKeyRef.current = noticeKey;
    setNoticeDialog(notice);
  }, [
    runtime.errorMessage,
    runtime.mutationErrorMessage,
    runtime.referenceErrorMessage,
    runtime.routeLinkErrorMessage,
  ]);

  const handleDemandToggle = () => {
    setIsDemandCollapsed((current) => !current);
  };

  const showVersionSuccessDialog = (
    actionLabel: string,
    result: CreateCustomizationDemandResult | null,
  ) => {
    if (!result) {
      return;
    }
    setVersionSuccessDialog(
      buildVersionSuccessNotice(
        actionLabel,
        result.demand.versionNumber,
        result.dispatcherNotification,
        Boolean(
          (result.project.dispatcherUserId ?? runtime.project?.dispatcherUserId)?.trim(),
        ),
      ),
    );
  };

  const handleGenerateDemandClick = async () => {
    if (shouldConfirmHistoricalBaseGeneration) {
      setHistoricalBaseConfirmVisible(true);
      return;
    }
    const result = await runtime.generateDemandVersion(generationAction);
    showVersionSuccessDialog(primaryActionLabel, result);
  };

  const handleCreateNewCustomizationClick = () => {
    setCreateNewConfirmVisible(true);
  };

  const handleConfirmCreateNewCustomization = async () => {
    const result = await runtime.createNewCustomization();
    if (result) {
      setCreateNewConfirmVisible(false);
    }
  };

  const handleConfirmHistoricalBaseGeneration = async () => {
    const baseDemandId = runtime.historicalGenerationBase?.id;
    setHistoricalBaseConfirmVisible(false);
    const result = await runtime.generateDemandVersion(generationAction, { baseDemandId });
    showVersionSuccessDialog(primaryActionLabel, result);
  };

  const handleStartItineraryGeneration = async () => {
    const displayDemandId = runtime.displayDemand?.id;
    const generated = await runtime.startItineraryGeneration();
    if (!generated || !displayDemandId) {
      return;
    }
    setItineraryAgentDemandIds((current) => {
      const next = new Set(current);
      next.add(displayDemandId);
      return next;
    });
    const opened = await openItineraryAgentExternally(displayDemandId);
    if (!opened) {
      setNoticeDialog({
        title: "行程计划智能体暂时无法打开",
        message: "单点登录地址获取失败，请稍后重试。",
      });
    }
  };

  const handleStartManualItineraryCreation = () => {
    const demandId = runtime.displayDemand?.id?.trim();
    if (!demandId || !runtime.startManualItineraryCreation()) {
      return;
    }
    const creationBlock = resolveManualItineraryCreationBlock({
      hasExistingItinerary: Boolean(selectedDemandVersion?.hasItinerary),
      persistedStatus: getDemandRequiredStatus(runtime.persistedDisplayDemand).status,
      displayedStatus: requiredStatus.status,
      displayedText: requiredStatus.text,
      saveActionLabel: primaryActionLabel,
    });
    if (creationBlock) {
      setNoticeDialog(creationBlock);
      return;
    }
    window.open(
      `/itineraries/demands/${encodeURIComponent(demandId)}/entry`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleItineraryDrawerClose = () => {
    setItineraryDrawerVisible(false);
    window.setTimeout(() => itineraryEntryButtonRef.current?.focus(), 0);
  };

  const showDispatcherNotificationResult = (result: CustomizationDispatcherNotificationResult | null) => {
    if (!result) {
      return;
    }
    if (result.sentCount > 0) {
      setNoticeDialog({
        title: "通知已发送",
        message: "已通知当前项目第一计调查看行程更新。",
      });
      return;
    }
    if (result.failedCount > 0) {
      const failureMessage = result.failureMessages[0]?.trim();
      setNoticeDialog({
        title: "通知发送失败",
        message: failureMessage
          ? `微信通知发送失败：${failureMessage}`
          : "微信通知发送失败，请稍后重试。",
      });
      return;
    }
    if (result.skippedCount > 0) {
      const failureMessage = result.failureMessages[0]?.trim();
      setNoticeDialog({
        title: "通知未发送",
        message: failureMessage || "当前项目第一计调未配置微信 OpenID，未发送通知。",
      });
      return;
    }
    setNoticeDialog({
      title: "通知未发送",
      message: "当前项目第一计调未配置微信 OpenID，未发送通知。",
    });
  };

  const handleNotifyDispatcherClick = () => {
    if (runtime.hasManualDraft) {
      setNoticeDialog({
        title: `请先${primaryActionLabel}`,
        message: `需求版本信息尚未保存，请先点击上方红色“${primaryActionLabel}”按钮，再通知计调。`,
      });
      return;
    }
    setDispatcherConfirmActionText("");
    setDispatcherConfirmVisible(true);
  };

  const handleConfirmDispatcherNotification = async () => {
    const selectedActionText = dispatcherConfirmActionText.trim();
    if (!selectedActionText) {
      return;
    }
    const result = await runtime.notifyDispatcher("pc_itinerary", selectedActionText);
    if (!result) {
      return;
    }
    setDispatcherConfirmVisible(false);
    setDispatcherConfirmActionText("");
    showDispatcherNotificationResult(result);
  };

  const resetProjectEditOptionsForSchool = (schoolId: string) => {
    projectEditOptionsRequestRef.current += 1;
    projectEditOptionsLoadingSchoolRef.current = null;
    const cachedOptions = projectEditOptionsCacheRef.current[schoolId];
    setProjectEditOptions(cachedOptions ?? null);
    setProjectEditOptionsSchoolId(cachedOptions ? schoolId : null);
    setProjectEditOptionsLoading(false);
  };

  const syncProjectEditOptionsForShadowForm = (form: CustomizationProjectUpdatePayload) => {
    const schoolId = form.primarySchoolId ?? "";
    if (projectEditOptionsSchoolId !== schoolId) {
      resetProjectEditOptionsForSchool(schoolId);
    }
  };

  const flushProjectEditorShadowForm = (target: ProjectEditorDraftTarget = "full") => {
    if (target === "single") {
      const nextDraft = singleShadowFormRef.current;
      if (nextDraft) {
        syncProjectEditOptionsForShadowForm(nextDraft);
      }
      setSingleProjectEditDraft(nextDraft);
      return;
    }
    syncProjectEditOptionsForShadowForm(shadowFormRef.current);
    setProjectForm(shadowFormRef.current);
  };

  const resetProjectEditorShadowState = () => {
    clearProjectEditorContentReadyTimer();
    projectEditorLiveDraftRef.current = null;
    singleShadowFormRef.current = null;
    singleProjectSaveClosingRef.current = false;
    setSingleProjectEditDraft(null);
    setProjectEditOptionsHydrationEnabled(false);
    const pendingSingleSave = pendingSingleProjectSaveRef.current;
    pendingSingleProjectSaveRef.current = null;
    void pendingSingleSave;
  };

  const writeProjectEditorShadowPatch = (target: ProjectEditorDraftTarget, patch: ProjectEditPatch) => {
    const current =
      target === "single"
        ? singleShadowFormRef.current ?? buildProjectEditPayload(null)
        : shadowFormRef.current;
    const next = applyProjectEditDraftPatch(current, patch);
    if (target === "single") {
      singleShadowFormRef.current = next;
    } else {
      shadowFormRef.current = next;
    }
  };

  const handleProjectFormChange = <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => {
    writeProjectEditorShadowPatch("full", { [key]: value } as ProjectEditPatch);
  };

  const reloadProjectEditOptionsAfterSchoolChange = (target: ProjectEditorDraftTarget) => {
    const activeForm =
      target === "single" && singleShadowFormRef.current
        ? singleShadowFormRef.current
        : shadowFormRef.current;
    const schoolId = activeForm.primarySchoolId ?? "";
    cancelCollegeCandidateSearch();
    delete projectEditOptionsCacheRef.current[schoolId];
    delete projectEditOptionsPendingRef.current[schoolId];
    setProjectEditOptionsHydrationEnabled(true);
    setProjectEditOptions((current) =>
      current
        ? {
            ...current,
            customerCandidates: [],
            contactCandidates: [],
          }
        : current,
    );
    // 未加载完成前不标记 schoolId，避免打开客户下拉被早退。
    setProjectEditOptionsSchoolId(null);
    projectEditOptionsLoadingSchoolRef.current = null;
    flushProjectEditorShadowForm(target);
    requestProjectEditOptionsForSchool(schoolId, { force: true });
  };

  const handleProjectIdentityChange = (patch: ProjectEditPatch) => {
    writeProjectEditorShadowPatch("full", patch);
    if (
      hasProjectEditPatchKey(patch, "primarySchoolId") &&
      (projectEditorFields.includes("customerId") || projectEditorFields.includes("contactId"))
    ) {
      reloadProjectEditOptionsAfterSchoolChange("full");
    }
  };

  const handleSingleProjectFormChange = <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => {
    writeProjectEditorShadowPatch("single", { [key]: value } as ProjectEditPatch);
  };

  const handleSingleProjectIdentityChange = (patch: ProjectEditPatch) => {
    writeProjectEditorShadowPatch("single", patch);
    if (
      hasProjectEditPatchKey(patch, "primarySchoolId") &&
      (projectEditorFields.includes("customerId") || projectEditorFields.includes("contactId"))
    ) {
      reloadProjectEditOptionsAfterSchoolChange("single");
    }
  };

  const handleProjectFormSave = async () => {
    if (projectEditorSaving) {
      return;
    }
    flushProjectEditorShadowForm("full");
    const nextPayload = normalizeProjectEditPayload(shadowFormRef.current);
    const committedPayload = projectEditorLiveDraftRef.current
      ? normalizeProjectEditPayload(projectEditorLiveDraftRef.current)
      : nextPayload;
    const scopedPayload = pickProjectEditPayloadForFields(committedPayload, projectEditorFields);
    const baselinePayload = pickProjectEditPayloadForFields(
      normalizeProjectEditPayload(
        buildProjectEditPayload(runtime.project, runtime.displayDemand, runtime.projectFieldOptions),
      ),
      projectEditorFields,
    );
    if (!hasProjectEditPayloadChanged(scopedPayload, baselinePayload)) {
      clearProjectEditorContentReadyTimer();
      setProjectEditorVisible(false);
      return;
    }
    if (isScopedProjectEditor) {
      clearProjectEditorContentReadyTimer();
      singleProjectSaveClosingRef.current = true;
      pendingSingleProjectSaveRef.current = null;
      void runtime.saveProject(scopedPayload, { silent: true, optimistic: true });
      setProjectEditorVisible(false);
      return;
    }
    setProjectEditorSubmitting(true);
    try {
      const result = await runtime.saveProject(scopedPayload);
      if (result) {
        clearProjectEditorContentReadyTimer();
        setProjectEditorVisible(false);
      }
    } finally {
      setProjectEditorSubmitting(false);
    }
  };

  const handleSingleProjectFormSave = async () => {
    const field = projectEditorFields[0];
    const draft = singleShadowFormRef.current ?? singleProjectEditDraft;
    if (!field || !draft) {
      return;
    }
    if (singleProjectSaveClosingRef.current) {
      return;
    }
    const nextPayload = normalizeSingleProjectEditPayload(draft, field);
    const baselinePayload = normalizeSingleProjectEditPayload(
      buildSingleProjectEditDraft(field, runtime.project, runtime.displayDemand, runtime.projectFieldOptions),
      field,
    );
    if (!hasProjectEditPayloadChanged(nextPayload, baselinePayload)) {
      clearProjectEditorContentReadyTimer();
      setProjectEditorVisible(false);
      return;
    }
    clearProjectEditorContentReadyTimer();
    singleProjectSaveClosingRef.current = true;
    pendingSingleProjectSaveRef.current = null;
    void runtime.saveProject(nextPayload, { silent: true, optimistic: true });
    setProjectEditorVisible(false);
  };

  const handleProjectEditorCancel = () => {
    projectEditOptionsRequestRef.current += 1;
    cancelCollegeCandidateSearch();
    projectEditOptionsLoadingSchoolRef.current = null;
    singleProjectSaveClosingRef.current = false;
    setProjectEditOptionsHydrationEnabled(false);
    setProjectEditOptionsLoading(false);
    clearProjectEditorContentReadyTimer();
    setProjectEditorVisible(false);
  };

  const closeInlineProjectEditor = () => {
    setInlineProjectEditor(null);
    setInlineProjectEditDraft(null);
  };

  const openInlineProjectEditorField = (
    field: ProjectEditFieldKey,
    displayField: CustomizationDemandDisplayField,
  ) => {
    const nextDraft = buildSingleProjectEditDraft(
      field,
      runtime.project,
      runtime.displayDemand,
      runtime.projectFieldOptions,
      displayField,
    );
    setInlineProjectEditor({ displayFieldKey: displayField.key, fieldKey: field });
    setInlineProjectEditDraft(nextDraft);
  };

  const handleInlineProjectDraftChange = <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => {
    setInlineProjectEditDraft((current) =>
      applyProjectEditDraftPatch(current ?? buildProjectEditPayload(null), {
        [key]: value,
      } as ProjectEditPatch),
    );
  };

  const handleInlineProjectFieldSave = () => {
    if (!inlineProjectEditor || !inlineProjectEditDraft) {
      return;
    }
    const field = inlineProjectEditor.fieldKey;
    const nextPayload = normalizeSingleProjectEditPayload(inlineProjectEditDraft, field);
    const baselinePayload = normalizeSingleProjectEditPayload(
      buildSingleProjectEditDraft(field, runtime.project, runtime.displayDemand, runtime.projectFieldOptions),
      field,
    );
    closeInlineProjectEditor();
    if (!hasProjectEditPayloadChanged(nextPayload, baselinePayload)) {
      return;
    }
    void runtime.saveProject(nextPayload, { silent: true, optimistic: true });
  };

  const handleProjectEditorOpen = async () => {
    closeInlineProjectEditor();
    shadowFormRef.current = buildProjectEditPayload(
      runtime.project,
      runtime.displayDemand,
      runtime.projectFieldOptions,
    );
    projectEditorLiveDraftRef.current = shadowFormRef.current;
    singleShadowFormRef.current = null;
    singleProjectSaveClosingRef.current = false;
    setSingleProjectEditDraft(null);
    setProjectEditorFields(allProjectEditFieldKeys);
    setProjectEditOptionsHydrationEnabled(true);
    scheduleProjectEditorContentReady(false);
    setProjectEditorVisible(true);
  };

  const openProjectEditorWithFields = (fieldKeys: ProjectEditFieldKey[]) => {
    closeInlineProjectEditor();
    shadowFormRef.current = buildProjectEditPayload(
      runtime.project,
      runtime.displayDemand,
      runtime.projectFieldOptions,
    );
    projectEditorLiveDraftRef.current = shadowFormRef.current;
    singleShadowFormRef.current = null;
    singleProjectSaveClosingRef.current = false;
    setProjectEditorFields(fieldKeys);
    setSingleProjectEditDraft(null);
    setProjectEditOptionsHydrationEnabled(!shouldDeferProjectEditOptionsLoad(fieldKeys));
    scheduleProjectEditorContentReady(false);
    setProjectEditorVisible(true);
  };

  const openSingleProjectEditorField = (
    field: ProjectEditFieldKey,
    displayField?: CustomizationDemandDisplayField,
  ) => {
    closeInlineProjectEditor();
    const nextDraft = buildSingleProjectEditDraft(
      field,
      runtime.project,
      runtime.displayDemand,
      runtime.projectFieldOptions,
      displayField,
    );
    setProjectEditorFields([field]);
    scheduleProjectEditorContentReady(true);
    singleProjectSaveClosingRef.current = false;
    singleShadowFormRef.current = nextDraft;
    setSingleProjectEditDraft(nextDraft);
    setProjectEditOptionsHydrationEnabled(!shouldDeferProjectEditOptionsLoad([field]));
    setProjectEditorVisible(true);
  };

  const openProjectEditorFields = (
    fieldKeys: ProjectEditFieldKey[] | null,
    displayField?: CustomizationDemandDisplayField,
  ) => {
    if (!fieldKeys || fieldKeys.length === 0) {
      return;
    }
    if (fieldKeys.length === 1) {
      openSingleProjectEditorField(fieldKeys[0], displayField);
      return;
    }
    openProjectEditorWithFields(fieldKeys);
  };

  const handleProjectFieldEditOpen = (field: CustomizationDemandDisplayField) => {
    if (runtime.historicalGenerationBase && field.key === "dispatcherUserName") {
      return;
    }
    const editFields = resolveProjectEditFieldsByDemandField(field.key);
    if (editFields?.length === 1 && editFields[0] === "dispatcherUserId") {
      openDispatcherSelection({
        selectedUserId: runtime.project?.dispatcherUserId,
        selectedDisplayName: runtime.project?.dispatcherUserName,
        onConfirm: (candidate) => {
          const nextPayload: CustomizationProjectUpdatePayload = candidate
            ? {
                dispatcherUserId: candidate.userId,
                dispatcherUserName: candidate.displayName,
              }
            : {
                dispatcherUserId: "",
                dispatcherUserName: "",
              };
          const baselinePayload: CustomizationProjectUpdatePayload = {
            dispatcherUserId: runtime.project?.dispatcherUserId,
            dispatcherUserName: runtime.project?.dispatcherUserName,
          };
          if (!hasProjectEditPayloadChanged(nextPayload, baselinePayload)) {
            return;
          }
          void runtime.saveProject(nextPayload, { silent: true, optimistic: true });
        },
      });
      return;
    }
    if (shouldUseInlineProjectEditor(editFields)) {
      openInlineProjectEditorField(editFields[0], field);
      return;
    }
    openProjectEditorFields(editFields, field);
  };

  const openDispatcherSelection = ({
    selectedUserId,
    selectedDisplayName,
    onConfirm,
  }: {
    selectedUserId?: string;
    selectedDisplayName?: string;
    onConfirm: (candidate: CustomizationDispatcherSelectionCandidate | null) => void;
  }) => {
    setDispatcherSelectionInitial({
      userId: selectedUserId,
      displayName: selectedDisplayName,
    });
    dispatcherSelectionResolverRef.current = onConfirm;
    setDispatcherSelectionVisible(true);
  };

  const handleDispatcherSelectionClose = () => {
    dispatcherSelectionResolverRef.current = null;
    setDispatcherSelectionInitial({});
    setDispatcherSelectionVisible(false);
  };

  const handleDispatcherSelectionConfirm = (
    candidate: CustomizationDispatcherSelectionCandidate | null,
  ) => {
    dispatcherSelectionResolverRef.current?.(candidate);
    dispatcherSelectionResolverRef.current = null;
    setDispatcherSelectionInitial({});
    setDispatcherSelectionVisible(false);
  };

  const handleRequiredStatusEditOpen = () => {
    if (!canEditRequiredStatus) {
      return;
    }
    openProjectEditorFields(requiredStatusEditFields);
  };

  if (runtime.loading) {
    return (
      <main
        ref={workbenchContainerRef}
        className={`customization-workbench-page is-${workbenchLayoutMode}-layout is-loading`}
        aria-busy="true"
      >
        <div className="customization-workbench-loading" aria-label="定制工作台加载中">
          <Spin tip="正在加载定制工作台…" size={32} />
        </div>
      </main>
    );
  }

  return (
    <main
      ref={workbenchContainerRef}
      className={`customization-workbench-page is-${workbenchLayoutMode}-layout`}
    >
      <div className="customization-top-stack">
        <section className="customization-hero">
          <div className="customization-hero-heading">
            <Title heading={3} className="customization-hero-title">
              {opportunityName}
            </Title>
            <div className="customization-hero-staff-tags" aria-label="当前版本人员">
              <WorkbenchStaffTag
                label="销售人员"
                value={runtime.project?.salesUserName?.trim() || "未配置"}
                tone="sales"
              />
              <WorkbenchStaffTag
                label="计调人员"
                value={runtime.project?.dispatcherUserName?.trim() || "未配置"}
                tone="dispatcher"
              />
            </div>
          </div>
          <div className="customization-hero-insights" aria-label="工作台关键状态">
            <WorkbenchInsight label="追溯" value={String(runtime.traces.length)} />
            <WorkbenchInsight
              label="版本"
              value={runtime.displayDemand?.versionNumber ?? "未生成"}
            />
            <WorkbenchInsight
              label="必填"
              value={formatRequiredStatusChip(requiredStatus)}
              tone={requiredStatus.status}
            />
            <WorkbenchInsight
              label="行程"
              value={itineraryInsight.value}
              tone={itineraryInsight.tone}
            />
          </div>
        </section>

        {runtime.generating ? (
          <section className="customization-generation-global" aria-label="版本处理状态">
            <LoaderCircle size={16} className="customization-generation-spinner" />
            <strong>版本处理中</strong>
            <span>{generationActionTitle}</span>
            <span>当前操作：{primaryActionLabel}</span>
          </section>
        ) : null}
      </div>

      <section
        className={`customization-workbench-grid${isDemandCollapsed ? " is-demand-collapsed" : ""}`}
        aria-label="定制工作台"
        data-layout={workbenchLayoutMode}
      >
        {isDemandCollapsed ? null : (
          <Card
            className={`customization-panel customization-trace-panel${runtime.generating ? " is-generating" : ""}`}
            aria-label="需求追溯面板"
          >
            <div className="customization-panel-title">
              <div>
                <Title heading={5} style={{ margin: 0 }}>
                  需求追溯
                </Title>
              </div>
              <div className="customization-panel-title-actions">
                <Tag
                  color={runtime.selectedTraceIds.length > 0 ? customizationStatusColors.complete.color : "arcoblue"}
                  style={
                    runtime.selectedTraceIds.length > 0
                      ? {
                          backgroundColor: customizationStatusColors.complete.bgColor,
                        }
                      : undefined
                  }
                >
                  已选 {runtime.selectedTraceIds.length}
                </Tag>
                <Tag color="arcoblue">{runtime.traces.length} 条</Tag>
              </div>
            </div>

            <div className="customization-trace-list">
              {runtime.traces.map((trace) => (
                <TraceItem
                  key={trace.id}
                  trace={trace}
                  referenceCandidates={runtime.traceReferenceCandidatesByTraceId[trace.id] ?? []}
                  routePreview={runtime.traceRouteLinkPreviewByTraceId[trace.id] ?? null}
                  selected={runtime.selectedTraceIds.includes(trace.id)}
                  onToggle={() => runtime.toggleTrace(trace.id)}
                />
              ))}
              {runtime.traces.length === 0 ? <Text type="secondary">暂无追溯记录</Text> : null}
            </div>

            <div className="customization-trace-footer">
              <TraceInputPanel
                canSubmit={canAddTrace}
                defaultRole={defaultTraceRole}
                traceContentRef={traceContentDraftRef}
                referenceCandidates={runtime.referenceCandidates}
                selectedReferenceDemandId={runtime.selectedReferenceDemandId}
                routeLinkPreview={runtime.routeLinkPreview}
                routeLinkPreviewing={runtime.routeLinkPreviewing}
                traceSubmitting={runtime.traceSubmitting}
                onPreviewTraceInput={runtime.previewTraceInput}
                onSubmitTrace={runtime.submitTrace}
                onToggleReferenceDemand={runtime.toggleReferenceDemand}
              />
            </div>
          </Card>
        )}

        {isDemandCollapsed ? (
          <div className="customization-demand-collapsed-rail">
            <Button
              className="customization-demand-rail-button"
              type="primary"
              aria-label="需求定制"
              onClick={handleDemandToggle}
            >
              <span className="collapsed-status-badge">
                <span className="collapsed-status-badge-text">需求定制</span>
                <span className="collapsed-status-badge-cue">&gt;&gt;</span>
              </span>
            </Button>
          </div>
        ) : (
          <Card
            className={`customization-panel customization-demand-panel${runtime.generating ? " is-generating" : ""}${
              runtime.isDemandVersionSwitching ? " is-version-switching" : ""
            }`}
            aria-label="定制需求面板"
          >
            <div className="customization-panel-title">
              <div>
                <Title heading={5} style={{ margin: 0 }}>
                  需求定制
                </Title>
              </div>
            </div>

              <div className="customization-demand-summary-bar" aria-label="定制需求摘要栏">
                <div className="customization-demand-summary-main">
                  <Text type="secondary" className="customization-demand-summary-meta">
                    {runtime.recentDemandVersions.length > 0
                      ? `最近 ${Math.min(runtime.recentDemandVersions.length, 3)} 版`
                      : "暂无版本"}
                    <span className="customization-demand-summary-jd" aria-label="当前JD号">
                      JD号 {selectedOrderVersionKey || "未生成"}
                    </span>
                    <span className="customization-demand-summary-updated">
                      更新 {getDemandUpdatedAt(runtime.displayDemand)}
                    </span>
                  </Text>
                </div>
                <div className="customization-demand-summary-actions">
                  <div className="customization-version-toolbar" aria-label="版本操作区">
                    {showViewItineraryEntry ? (
                      viewableItineraryId ? (
                        <button
                          type="button"
                          className="customization-demand-itinerary-modifier-tag"
                          aria-label="查看行程"
                          title={
                            itineraryModifierName
                              ? `${itineraryModifierName}修改了行程`
                              : "查看当前版本行程"
                          }
                          onClick={() => setPreviewItineraryId(viewableItineraryId)}
                        >
                          查看行程
                        </button>
                      ) : (
                        <span
                          className="customization-demand-itinerary-modifier-tag is-disabled"
                          aria-label="查看行程"
                          title={
                            itineraryModifierName
                              ? `${itineraryModifierName}修改了行程`
                              : "暂无可预览行程"
                          }
                        >
                          查看行程
                        </span>
                      )
                    ) : null}
                    {runtime.recentDemandVersions.length > 0 ? (
                      <div className="customization-version-list" aria-label="最近三版">
                        {runtime.recentDemandVersions.map((version) => (
                          <Button
                            key={version.id}
                            className={`customization-version-button ${getDemandVersionRequiredClass(
                              version,
                              resolveDemandVersionRequiredStatusView(version),
                            )} ${getDemandVersionItineraryClass(version, isDemandVersionGenerating(version))}`}
                            size="small"
                            type={version.id === selectedDemandId ? "primary" : "secondary"}
                            aria-label={`查看版本 ${version.versionNumber}`}
                            title={`${formatDemandVersionRequiredTitle(
                              version,
                              resolveDemandVersionRequiredStatusView(version),
                            )}；${formatDemandVersionItineraryTitle(version, isDemandVersionGenerating(version))}`}
                            onClick={() => runtime.selectDemandVersion(version.id)}
                          >
                            <span className="customization-version-button-version">{version.versionNumber}</span>
                            <DemandVersionItineraryPill
                              version={version}
                              compact
                              isGenerating={isDemandVersionGenerating(version)}
                            />
                          </Button>
                        ))}
                      </div>
                    ) : null}
                    <Select
                      aria-label="定制需求版本"
                      className={`customization-version-select ${
                        selectedDemandVersion
                          ? getDemandVersionItineraryClass(
                              selectedDemandVersion,
                              isDemandVersionGenerating(selectedDemandVersion),
                            )
                          : ""
                      }`}
                      labelInValue
                      value={
                        selectedDemandVersion
                          ? {
                              label: renderDemandVersionSelectValue(
                                selectedDemandVersion,
                                resolveDemandVersionRequiredStatusView(selectedDemandVersion),
                              ),
                              value: selectedDemandVersion.id,
                            }
                          : undefined
                      }
                      disabled={runtime.demandVersions.length === 0}
                      dropdownMenuClassName="customization-version-dropdown"
                      dropdownMenuStyle={{ minWidth: 220 }}
                      triggerProps={{
                        autoAlignPopupWidth: false,
                        autoFitPosition: true,
                        boundaryDistance: { right: 8, bottom: 8 },
                        position: "br",
                      }}
                      onChange={(demandId) => runtime.selectDemandVersion(resolveDemandVersionSelectValue(demandId))}
                    >
                      {runtime.demandVersions.map((version) => (
                        <Select.Option
                          key={version.id}
                          value={version.id}
                          className={`customization-version-option ${getDemandVersionItineraryClass(version, isDemandVersionGenerating(version))}`}
                        >
                          <DemandVersionOptionLabel
                            version={version}
                            isGenerating={isDemandVersionGenerating(version)}
                            requiredStatusView={resolveDemandVersionRequiredStatusView(version)}
                          />
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              <div
                aria-label="必填项状态"
                role={canEditRequiredStatus ? "button" : undefined}
                tabIndex={canEditRequiredStatus ? 0 : undefined}
                className={`customization-demand-required-status${
                  requiredStatus.status === "missing" ? " is-missing" : ""
                }${requiredStatus.status === "pending" ? " is-pending" : ""}${
                  requiredStatus.status === "review" ? " is-review" : ""
                }${
                  requiredStatus.status === "complete" ? " is-complete" : ""
                }${canEditRequiredStatus ? " is-editable" : ""}`}
                onClick={handleRequiredStatusEditOpen}
                onKeyDown={(event) => {
                  if (!canEditRequiredStatus || (event.key !== "Enter" && event.key !== " ")) {
                    return;
                  }
                  event.preventDefault();
                  handleRequiredStatusEditOpen();
                }}
              >
                <Alert
                  type={getRequiredStatusAlertType(requiredStatus.status)}
                  title={requiredStatus.title}
                  content={requiredStatus.text}
                  showIcon
                />
              </div>

              {runtime.generating ? (
                <div
                  className="customization-generation-overlay customization-generation-banner"
                  aria-label="AI 生成状态"
                >
                  <div className="customization-generation-card">
                    <LoaderCircle size={20} className="customization-generation-spinner" />
                    <div>
                      <strong>{generationActionTitle}</strong>
                      <span>{generationStatusText}</span>
                      <span aria-label="版本生成锁定提示">
                        生成期间请保持当前页面，不要重复点击创建或更新。
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              {hasGeneratedDemand ? (
                <div className="customization-demand-groups">
                  {displayDemandGroups.map((group) => (
                    <DemandDisplayGroup
                      key={group.key}
                      group={group}
                      disableDispatcherEdit={!canUpdateProject || Boolean(runtime.historicalGenerationBase)}
                      onEditField={canUpdateProject ? handleProjectFieldEditOpen : undefined}
                      inlineEditor={inlineProjectEditor}
                      inlineDraft={inlineProjectEditDraft}
                      onInlineDraftChange={handleInlineProjectDraftChange}
                      onInlineSave={handleInlineProjectFieldSave}
                      onInlineCancel={closeInlineProjectEditor}
                    />
                  ))}
                  <CustomizationQuickItineraryDetails editor={runtime.quickItinerary} />
                </div>
              ) : (
                <div className="customization-demand-empty" aria-label="未生成定制需求空状态">
                  <strong>暂无结构化需求</strong>
                  <span>勾选左侧追溯记录并生成第一版后，这里将展示版本字段与必填项校验结果。</span>
                </div>
              )}
          </Card>
        )}

        {isDemandCollapsed ? null : (
          <Card className="customization-confirmation-panel" aria-label="定制确认操作区">
            <div className="customization-confirmation-copy">
              <div className="customization-confirmation-heading">
                <Title heading={5} style={{ margin: 0 }}>
                  定制确认
                </Title>
              </div>
              <div className="customization-confirmation-tags" aria-label="定制确认来源">
                <Tag color="arcoblue">
                  当前版本 {runtime.displayDemand?.versionNumber ?? "未生成"}
                </Tag>
                <Tag>已选追溯 {runtime.selectedTraceIds.length}</Tag>
                {runtime.hasManualDraft ? (
                  <Tag color="orange">手工草稿 {runtime.manualDraftFieldCount} 项</Tag>
                ) : null}
              </div>
              <Text className="customization-confirmation-status">{confirmationStatusText}</Text>
            </div>
            <div className="customization-confirmation-action-area">
              <div className="customization-confirmation-actions" aria-label="定制确认动作">
                <Button
                  type="primary"
                  disabled={!canGenerate}
                  loading={runtime.generating}
                  icon={<FilePlus2 size={16} />}
                  onClick={() => {
                    void handleGenerateDemandClick();
                  }}
                >
                  {primaryActionLabel}
                </Button>
                <Button
                  type="secondary"
                  disabled={!canCreateNewCustomization}
                  loading={runtime.generating}
                  icon={<FilePlus2 size={16} />}
                  onClick={handleCreateNewCustomizationClick}
                >
                  复制，创建新定制
                </Button>
                {canGenerateItinerary ? (
                  <Button
                    type="secondary"
                    disabled={!canStartItineraryGeneration}
                    loading={runtime.itineraryGenerating}
                    icon={<Route size={16} />}
                    onClick={handleStartItineraryGeneration}
                  >
                    智能生成行程
                  </Button>
                ) : null}
                {canEnterManualItinerary ? (
                  <Button
                    type="secondary"
                    icon={<FilePlus2 size={16} />}
                    onClick={handleStartManualItineraryCreation}
                  >
                    {manualItineraryActionLabel}
                  </Button>
                ) : null}
                <Button
                  className="customization-dispatcher-notify-button"
                  type="secondary"
                  icon={<SendHorizontal size={16} />}
                  loading={runtime.dispatcherNotifying}
                  disabled={!canNotifyDispatcher}
                  onClick={handleNotifyDispatcherClick}
                >
                  {dispatcherNotifyLabel}
                </Button>
              </div>
              <Dropdown
                trigger="click"
                position="br"
                droplist={(
                  <Menu>
                    <Menu.Item
                      key="edit-demand"
                      disabled={!canUpdateProject}
                      onClick={() => {
                        void handleProjectEditorOpen();
                      }}
                    >
                      编辑需求定制
                    </Menu.Item>
                  </Menu>
                )}
              >
                <Button
                  className="customization-more-actions-button"
                  type="text"
                  icon={<MoreHorizontal size={18} />}
                  aria-label="更多操作"
                />
              </Dropdown>
            </div>
          </Card>
        )}

        {workbenchLayoutMode === "compact" && !isDemandCollapsed ? (
          <aside className="customization-itinerary-entry" aria-label="行程计划窄入口">
            <Button
              ref={itineraryEntryButtonRef}
              className="customization-itinerary-entry-button"
              type="secondary"
              icon={<Route size={18} />}
              aria-label="打开行程计划"
              onClick={() => setItineraryDrawerVisible(true)}
            >
              <span>行程计划</span>
              <small>{itineraryInsight.value}</small>
            </Button>
          </aside>
        ) : hasItinerary ? (
          <WorkbenchItineraryPreviewPanel
            itineraryId={panelItineraryId}
            isDemandCollapsed={isDemandCollapsed}
            isVersionSwitching={runtime.isDemandVersionSwitching}
            allowExpand
            onDemandToggle={handleDemandToggle}
            onOpenFullPreview={
              panelItineraryId ? () => setPreviewItineraryId(panelItineraryId) : undefined
            }
          />
        ) : (
          <WorkbenchItineraryEmptyPanel
            isDemandCollapsed={isDemandCollapsed}
            isVersionSwitching={runtime.isDemandVersionSwitching}
            allowExpand
            onDemandToggle={handleDemandToggle}
          />
        )}
      </section>
      <Drawer
        title="行程计划"
        width="min(760px, calc(100vw - 48px))"
        visible={workbenchLayoutMode === "compact" && itineraryDrawerVisible}
        footer={null}
        maskClosable
        escToExit
        unmountOnExit
        onCancel={handleItineraryDrawerClose}
      >
        <div className="customization-workbench-itinerary-drawer" aria-label="行程计划抽屉">
          {hasItinerary ? (
            <WorkbenchItineraryPreviewPanel
              itineraryId={panelItineraryId}
              isDemandCollapsed={false}
              isVersionSwitching={runtime.isDemandVersionSwitching}
              allowExpand={false}
              onDemandToggle={handleDemandToggle}
              onOpenFullPreview={
                panelItineraryId ? () => setPreviewItineraryId(panelItineraryId) : undefined
              }
            />
          ) : (
            <WorkbenchItineraryEmptyPanel
              isDemandCollapsed={false}
              isVersionSwitching={runtime.isDemandVersionSwitching}
              allowExpand={false}
              onDemandToggle={handleDemandToggle}
            />
          )}
        </div>
      </Drawer>
      <Modal
        visible={Boolean(runtime.opportunityBindingOpen && runtime.opportunityBinding)}
        title={
          !canBindOpportunity
            ? "商机归属待处理"
            : runtime.opportunityBinding?.status === "review_required"
              ? "复核商机归属"
              : "确认商机归属"
        }
        footer={null}
        maskClosable={false}
        escToExit={false}
        unmountOnExit
        className="customization-opportunity-binding-modal"
        style={{ width: 560 }}
        onCancel={runtime.closeOpportunityBinding}
      >
        {runtime.opportunityBinding ? (
          <CustomizationOpportunityBindingContent
            binding={runtime.opportunityBinding}
            candidatesLoading={runtime.opportunityBindingPreparing}
            canBindOpportunity={canBindOpportunity}
            canCreateOpportunity={canCreateOpportunity}
            onResolve={runtime.resolveOpportunityBinding}
            onClose={runtime.closeOpportunityBinding}
          />
        ) : null}
      </Modal>
      <Modal
        visible={createNewConfirmVisible}
        title="创建新定制"
        okText="确认创建"
        cancelText="取消"
        confirmLoading={runtime.generating}
        okButtonProps={{ disabled: runtime.generating }}
        onOk={() => {
          void handleConfirmCreateNewCustomization();
        }}
        onCancel={() => {
          if (!runtime.generating) {
            setCreateNewConfirmVisible(false);
          }
        }}
      >
        <div className="customization-create-new-confirm" aria-label="创建新定制确认">
          <Text>基线版本：{runtime.displayDemand?.versionNumber ?? "当前版本"}</Text>
          <Text>已选需求追溯：{runtime.selectedTraceIds.length} 条</Text>
          <Text>
            未提交手工修改：
            {runtime.hasManualDraft ? `${runtime.manualDraftFieldCount} 项` : "无"}
          </Text>
          <strong>新项目 V1 将带入以上当前可见内容</strong>
        </div>
      </Modal>
      <Modal
        visible={historicalBaseConfirmVisible && shouldConfirmHistoricalBaseGeneration}
        title={`以 ${runtime.historicalGenerationBase?.versionNumber ?? "历史版本"} 为基线生成新版本`}
        okText="生成新版本"
        cancelText="取消"
        onOk={handleConfirmHistoricalBaseGeneration}
        onCancel={() => setHistoricalBaseConfirmVisible(false)}
      >
        <Text>
          当前查看的是历史版本，确认后会生成新的下一版本，不会覆盖{" "}
          {runtime.historicalGenerationBase?.versionNumber ?? "历史版本"}。
        </Text>
      </Modal>
      <Modal
        visible={projectEditorVisible}
        title={formatProjectEditorTitle(projectEditorFields)}
        style={{
          width: isCompactProjectEditorModal
            ? "min(560px, calc(100vw - 32px))"
            : "min(1040px, calc(100vw - 48px))",
        }}
        className={formatProjectEditorModalClassName(isCompactProjectEditorModal, projectEditorFields)}
        getChildrenPopupContainer={() => document.body}
        okText="保存"
        cancelText="取消"
        confirmLoading={projectEditorDialogSaving}
        unmountOnExit
        afterClose={resetProjectEditorShadowState}
        onOk={() => {
          void (isSingleProjectEditor ? handleSingleProjectFormSave : handleProjectFormSave)();
        }}
        onCancel={handleProjectEditorCancel}
      >
        {shouldRenderProjectEditorModalContent ? (
          <div aria-label={projectEditorContentReady ? "需求定制编辑表单" : undefined}>
            {!projectEditorContentReady ? (
              <ProjectEditorPanelLoading />
            ) : isSingleProjectEditor && singleProjectEditDraft ? (
              <SingleProjectEditPanel
                value={singleProjectEditDraft}
                field={projectEditorFields[0]}
                fieldOptions={runtime.projectFieldOptions}
                demandFieldOptions={runtime.demandFieldOptions}
                editOptions={projectEditOptionsForForm}
                optionsLoading={projectEditOptionsLoading}
                projectPointNameOptions={projectPointNameOptions}
                projectPointNamesLoading={projectPointNamesLoading}
                onChange={handleSingleProjectFormChange}
                onIdentityChange={handleSingleProjectIdentityChange}
                onOptionsOpen={requestProjectEditOptionsForCurrentSchool}
                onCollegeSearch={scheduleCollegeCandidatesSearch}
                onPointNamesSearch={requestProjectPointNames}
                onOpenDispatcherSelection={openDispatcherSelection}
              />
            ) : (
              <ProjectEditPanel
                value={shadowFormRef.current}
                fieldOptions={runtime.projectFieldOptions}
                demandFieldOptions={runtime.demandFieldOptions}
                editOptions={projectEditOptionsForForm}
                visibleFields={projectEditorFields}
                optionsLoading={projectEditOptionsLoading}
                projectPointNameOptions={projectPointNameOptions}
                projectPointNamesLoading={projectPointNamesLoading}
                saving={projectEditorSaving}
                hideActions
                onChange={handleProjectFormChange}
                onIdentityChange={handleProjectIdentityChange}
                onDraftChange={(nextValue) => {
                  projectEditorLiveDraftRef.current = nextValue;
                }}
                onOptionsOpen={requestProjectEditOptionsForCurrentSchool}
                onCollegeSearch={scheduleCollegeCandidatesSearch}
                onPointNamesSearch={requestProjectPointNames}
                onSettled={() => flushProjectEditorShadowForm("full")}
                onSave={handleProjectFormSave}
                actionLabel="保存"
                onOpenDispatcherSelection={openDispatcherSelection}
              />
            )}
          </div>
        ) : null}
      </Modal>
      <Modal
        visible={dispatcherConfirmVisible}
        title="通知计调"
        okText="确认通知"
        cancelText="取消"
        confirmLoading={runtime.dispatcherNotifying}
        okButtonProps={{
          disabled: !dispatcherConfirmActionText.trim() || runtime.dispatcherNotifying,
        }}
        onOk={() => {
          void handleConfirmDispatcherNotification();
        }}
        onCancel={() => {
          if (runtime.dispatcherNotifying) {
            return;
          }
          setDispatcherConfirmVisible(false);
          setDispatcherConfirmActionText("");
        }}
      >
        <div className="customization-dispatcher-confirm" aria-label="通知计调确认表单">
          <div className="customization-dispatcher-confirm-meta">
            <Text>通知对象：{dispatcherConfirmTargetName}</Text>
            <Text>当前版本：{dispatcherConfirmVersionNumber}</Text>
          </div>
          <Radio.Group
            value={dispatcherConfirmActionText}
            onChange={(value) => setDispatcherConfirmActionText(formatProjectEditSelectValue(value))}
          >
            <Space direction="vertical" size={8}>
              {dispatcherActionOptions.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </Modal>
      <DispatcherSelectionModal
        visible={dispatcherSelectionVisible}
        selectedUserId={dispatcherSelectionInitial.userId ?? runtime.project?.dispatcherUserId}
        selectedDisplayName={
          dispatcherSelectionInitial.displayName ?? runtime.project?.dispatcherUserName
        }
        onClose={handleDispatcherSelectionClose}
        onConfirm={handleDispatcherSelectionConfirm}
      />
      <Modal
        visible={Boolean(versionSuccessDialog)}
        title={versionSuccessDialog?.title ?? "版本操作成功"}
        okText="知道了"
        cancelButtonProps={{ style: { display: "none" } }}
        maskClosable={false}
        escToExit={false}
        closable={false}
        onOk={() => setVersionSuccessDialog(null)}
        onCancel={() => setVersionSuccessDialog(null)}
      >
        <Text>{versionSuccessDialog?.message}</Text>
      </Modal>
      <Modal
        visible={Boolean(noticeDialog)}
        title={noticeDialog?.title ?? "提示"}
        okText="知道了"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => {
          noticeDialogKeyRef.current = "";
          setNoticeDialog(null);
        }}
        onCancel={() => {
          noticeDialogKeyRef.current = "";
          setNoticeDialog(null);
        }}
      >
        <Text>{noticeDialog?.message}</Text>
      </Modal>
      <CustomizationItineraryPreviewHost
        itineraryId={previewItineraryId}
        onClose={() => setPreviewItineraryId(null)}
      />
    </main>
  );
}

type WorkbenchItineraryEmptyPanelProps = {
  isDemandCollapsed: boolean;
  isVersionSwitching: boolean;
  allowExpand: boolean;
  onDemandToggle: () => void;
};

/** 无行程时保留右侧行程计划空态，提示可走智能生成或人工创建。 */
function WorkbenchItineraryEmptyPanel({
  isDemandCollapsed,
  isVersionSwitching,
  allowExpand,
  onDemandToggle,
}: WorkbenchItineraryEmptyPanelProps) {
  return (
    <Card
      className={`customization-panel customization-itinerary-panel${isDemandCollapsed ? " is-primary" : ""}${
        isVersionSwitching ? " is-version-switching" : ""
      }`}
      aria-busy={isVersionSwitching}
      aria-label="行程计划面板"
    >
      <div className="customization-panel-title">
        <div>
          <Title heading={5} style={{ margin: 0 }}>
            行程计划
          </Title>
          <Text type="secondary">可通过智能体生成，也可创建空白行程后完全手动录入。</Text>
        </div>
        {allowExpand ? (
          <Space wrap className="customization-itinerary-actions">
            <Button
              className="customization-itinerary-expand-button"
              size="small"
              type={isDemandCollapsed ? "primary" : "secondary"}
              icon={isDemandCollapsed ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              aria-pressed={isDemandCollapsed}
              onClick={onDemandToggle}
            >
              {isDemandCollapsed ? "恢复需求区域" : "扩大行程区域"}
            </Button>
          </Space>
        ) : null}
      </div>
      <div className="customization-itinerary-empty">
        <strong>尚未创建行程</strong>
        <span>可选择“智能生成行程”使用智能体，或点击“人工创建行程”进入原生编辑器手动录入。</span>
      </div>
      {isVersionSwitching ? (
        <div className="customization-itinerary-switch-overlay" role="status" aria-live="polite">
          <LoaderCircle size={22} className="customization-itinerary-switch-icon" />
          <strong>正在切换行程</strong>
          <span>正在载入所选版本的行程状态</span>
          <i aria-hidden="true" />
        </div>
      ) : null}
    </Card>
  );
}

type WorkbenchItineraryPreviewPanelProps = {
  itineraryId: string;
  isDemandCollapsed: boolean;
  isVersionSwitching: boolean;
  allowExpand: boolean;
  onDemandToggle: () => void;
  onOpenFullPreview?: () => void;
};

/** 有行程时在常驻栏与窄屏抽屉中展示统一只读预览。 */
function WorkbenchItineraryPreviewPanel({
  itineraryId,
  isDemandCollapsed,
  isVersionSwitching,
  allowExpand,
  onDemandToggle,
  onOpenFullPreview,
}: WorkbenchItineraryPreviewPanelProps) {
  const previewQuery = useQuery({
    queryKey: ["itinerary", "workbench-preview", itineraryId] as const,
    queryFn: () => itineraryPreviewRuntime.getEditor(itineraryId),
    enabled: Boolean(itineraryId),
    retry: false,
  });
  const previewDegraded = !previewQuery.isError && previewQuery.data?.previewDegraded === true;
  const previewErrorMessage = previewQuery.isError
    ? itineraryPreviewLoadErrorText(previewQuery.error)
    : undefined;
  const previewCanRetry = previewQuery.isError && canRetryItineraryPreviewLoad(previewQuery.error);
  const publicItinerary = previewQuery.data?.payload
    ? derivePublicItinerary(previewQuery.data.payload)
    : undefined;

  return (
    <Card
      className={`customization-panel customization-itinerary-panel${isDemandCollapsed ? " is-primary" : ""}${
        isVersionSwitching ? " is-version-switching" : ""
      }`}
      aria-busy={isVersionSwitching || (Boolean(itineraryId) && previewQuery.isLoading)}
      aria-label="行程预览面板"
    >
      <div className="customization-panel-title">
        <div>
          <Title heading={5} style={{ margin: 0 }}>
            行程预览
          </Title>
          <Text type="secondary">当前版本已有行程，右侧仅展示只读预览；编辑请使用「人工编辑行程」。</Text>
        </div>
        <Space wrap className="customization-itinerary-actions">
          {onOpenFullPreview ? (
            <Button size="small" type="secondary" onClick={onOpenFullPreview}>
              完整预览
            </Button>
          ) : null}
          {allowExpand ? (
            <Button
              className="customization-itinerary-expand-button"
              size="small"
              type={isDemandCollapsed ? "primary" : "secondary"}
              icon={isDemandCollapsed ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              aria-pressed={isDemandCollapsed}
              onClick={onDemandToggle}
            >
              {isDemandCollapsed ? "恢复需求区域" : "扩大行程区域"}
            </Button>
          ) : null}
        </Space>
      </div>
      {!itineraryId ? (
        <div className="customization-itinerary-empty">
          <strong>暂无可预览行程</strong>
          <span>当前版本已标记有行程，但尚未关联可预览的行程 ID。</span>
        </div>
      ) : previewQuery.isLoading ? (
        <div className="customization-itinerary-empty" role="status" aria-live="polite">
          <Spin size={24} />
          <strong>正在加载行程预览</strong>
        </div>
      ) : previewErrorMessage ? (
        <div className="customization-itinerary-empty" role="alert">
          <strong>行程预览加载失败</strong>
          <span>{previewErrorMessage}</span>
          {previewCanRetry ? (
            <Button
              size="small"
              type="secondary"
              icon={<RefreshCw size={14} />}
              onClick={() => void previewQuery.refetch()}
            >
              重试
            </Button>
          ) : null}
        </div>
      ) : publicItinerary ? (
        <div className="customization-itinerary-preview-body" aria-label="行程预览内容">
          {previewDegraded ? (
            <Alert
              type="warning"
              showIcon
              content={`该历史行程有 ${previewQuery.data?.previewSkippedSegmentCount ?? 0} 个交通段因缺少可验证的节点引用未展示。`}
            />
          ) : null}
          <ItineraryClientDetail
            itinerary={publicItinerary}
            idPrefix="customization-workbench-preview"
            showDayNavigation
          />
        </div>
      ) : (
        <div className="customization-itinerary-empty">
          <strong>暂无可预览行程</strong>
          <span>未能生成可展示的行程内容。</span>
        </div>
      )}
      {isVersionSwitching ? (
        <div className="customization-itinerary-switch-overlay" role="status" aria-live="polite">
          <LoaderCircle size={22} className="customization-itinerary-switch-icon" />
          <strong>正在切换行程</strong>
          <span>正在载入所选版本的行程预览</span>
          <i aria-hidden="true" />
        </div>
      ) : null}
    </Card>
  );
}

/** PC 工作台智能生成成功后外跳到行程计划智能体。 */
async function openItineraryAgentExternally(demandId: string): Promise<boolean> {
  const normalizedDemandId = demandId.trim();
  if (!normalizedDemandId) {
    return false;
  }
  const targetUrl = `/chat_v2/route_plan/entry?demand_id=${encodeURIComponent(normalizedDemandId)}`;
  try {
    const result = await buildLegacyJumpUrl({ targetUrl });
    const editorUrl = buildLegacyItineraryEditorUrl(result.jumpUrl);
    if (!editorUrl) {
      return false;
    }
    window.open(editorUrl, "_self");
    return true;
  } catch {
    return false;
  }
}

/** 使用配置基址把后端单点路径收敛为受信智能体完整地址。 */
function buildLegacyItineraryEditorUrl(jumpUrl: string): string {
  const normalizedJumpUrl = jumpUrl.trim();
  const baseUrl = getLegacyItineraryBaseUrl();
  if (!normalizedJumpUrl || !baseUrl) {
    return "";
  }
  try {
    const parsedBaseUrl = new URL(baseUrl);
    const parsedJumpUrl = new URL(normalizedJumpUrl, parsedBaseUrl);
    return `${parsedBaseUrl.origin}${parsedJumpUrl.pathname}${parsedJumpUrl.search}${parsedJumpUrl.hash}`;
  } catch {
    return "";
  }
}

/** 新增追溯输入面板，隔离高频输入状态，避免每次敲字重绘整个工作台。 */
const TraceInputPanel = memo(function TraceInputPanel({
  canSubmit,
  defaultRole,
  traceContentRef,
  referenceCandidates,
  selectedReferenceDemandId,
  routeLinkPreview,
  routeLinkPreviewing,
  traceSubmitting,
  onPreviewTraceInput,
  onSubmitTrace,
  onToggleReferenceDemand,
}: {
  canSubmit: boolean;
  defaultRole: string;
  traceContentRef: MutableRefObject<string>;
  referenceCandidates: CustomizationReferenceDemandCandidate[];
  selectedReferenceDemandId?: string | null;
  routeLinkPreview: CustomizationRouteLinkRecognitionResult | null;
  routeLinkPreviewing?: boolean;
  traceSubmitting?: boolean;
  onPreviewTraceInput: (content: string) => Promise<void>;
  onSubmitTrace: (payload: CustomizationTraceCreate) => Promise<void>;
  onToggleReferenceDemand: (demandId: string) => void;
}) {
  const [traceForm, setTraceForm] = useState<Omit<CustomizationTraceCreate, "content">>({
    role: defaultRole,
    sourceType: "backend",
    isImportant: false,
  });
  const [traceContentResetKey, setTraceContentResetKey] = useState(0);
  const [traceSubmittingLocally, setTraceSubmittingLocally] = useState(false);
  const traceSubmittingRef = useRef(false);
  const tracePreviewDebounceTimerRef = useRef<number | null>(null);
  const isTraceSubmitting = traceSubmittingLocally || Boolean(traceSubmitting);
  const hasActiveTracePreview =
    referenceCandidates.length > 0 ||
    Boolean(selectedReferenceDemandId) ||
    Boolean(routeLinkPreview) ||
    Boolean(routeLinkPreviewing);

  useEffect(
    () => () => {
      if (tracePreviewDebounceTimerRef.current !== null) {
        window.clearTimeout(tracePreviewDebounceTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    setTraceForm((current) => (
      current.role === defaultRole ? current : { ...current, role: defaultRole }
    ));
  }, [defaultRole]);

  const clearTracePreviewDebounce = () => {
    if (tracePreviewDebounceTimerRef.current !== null) {
      window.clearTimeout(tracePreviewDebounceTimerRef.current);
      tracePreviewDebounceTimerRef.current = null;
    }
  };

  const handleTraceSubmit = async () => {
    if (traceSubmittingRef.current || traceSubmitting) {
      return;
    }
    const content = traceContentRef.current;
    if (!content.trim()) {
      return;
    }
    traceSubmittingRef.current = true;
    setTraceSubmittingLocally(true);
    clearTracePreviewDebounce();
    try {
      await onSubmitTrace({ ...traceForm, content });
      traceContentRef.current = "";
      setTraceContentResetKey((current) => current + 1);
      setTraceForm((current) => ({ ...current, isImportant: false }));
      void onPreviewTraceInput("");
    } finally {
      traceSubmittingRef.current = false;
      setTraceSubmittingLocally(false);
    }
  };

  const handleTraceContentChange = (content: string) => {
    traceContentRef.current = content;
    clearTracePreviewDebounce();
    const normalizedContent = content.trim();
    if (!normalizedContent) {
      if (hasActiveTracePreview) {
        void onPreviewTraceInput("");
      }
      return;
    }
    if (!shouldPreviewTraceInput(normalizedContent)) {
      if (hasActiveTracePreview) {
        void onPreviewTraceInput("");
      }
      return;
    }
    tracePreviewDebounceTimerRef.current = window.setTimeout(() => {
      tracePreviewDebounceTimerRef.current = null;
      void onPreviewTraceInput(content);
    }, tracePreviewDebounceMs);
  };

  return (
    <section className="customization-shared-input-panel" aria-label="需求定制共用输入面板">
      <div className="customization-input-head">
        <div className="customization-input-copy">
          <Text className="customization-input-title">新增追溯</Text>
        </div>
        <Tag
          style={{
            background: "linear-gradient(135deg, #7c3aed, #2563eb)",
            color: "#fff",
            border: "none",
            fontWeight: 600,
          }}
        >
          AI 抽取
        </Tag>
      </div>
      <div className="customization-input-compose">
        <TextArea
          key={traceContentResetKey}
          aria-label="需求追溯内容"
          rows={4}
          defaultValue={traceContentRef.current}
          placeholder="输入需求、JD号或行程链接"
          onChange={handleTraceContentChange}
        />
      </div>
      <div className="customization-input-action-row">
        <Button
          className="customization-send-button"
          type="primary"
          loading={isTraceSubmitting}
          disabled={!canSubmit || isTraceSubmitting}
          icon={<SendHorizontal size={16} />}
          onClick={handleTraceSubmit}
        >
          发送记录
        </Button>
      </div>
      <ReferenceDemandCandidates
        candidates={referenceCandidates}
        selectedDemandId={selectedReferenceDemandId}
        onToggle={onToggleReferenceDemand}
      />
      <RouteLinkPreviewCard preview={routeLinkPreview} loading={routeLinkPreviewing} />
    </section>
  );
});

/** 判断追溯输入是否需要进入预览识别，普通文本只保留本地输入状态。 */
function shouldPreviewTraceInput(content: string): boolean {
  return tracePreviewReferencePattern.test(content) || tracePreviewRouteLinkPattern.test(content);
}

/** 按工作台 content-box 宽度决定三栏、双栏或纵向布局。 */
function resolveCustomizationWorkbenchLayoutMode(
  width: number,
): CustomizationWorkbenchLayoutMode {
  if (width >= CUSTOMIZATION_WORKBENCH_WIDE_MIN) {
    return "wide";
  }
  if (width >= CUSTOMIZATION_WORKBENCH_COMPACT_MIN) {
    return "compact";
  }
  return "stacked";
}

/** 生成主按钮文案，当前版本缺项时只更新版本，不提示创建下一版。 */
function getPrimaryGenerationActionLabel({
  hasCurrentDemand,
  currentVersionSeq,
  nextVersionSeq,
  canUpdatePersistedV1,
}: {
  hasCurrentDemand: boolean;
  currentVersionSeq: number;
  nextVersionSeq: number;
  canUpdatePersistedV1: boolean;
}): string {
  if (!hasCurrentDemand) {
    return "创建版本 V1";
  }
  if (canUpdatePersistedV1) {
    return `更新版本 V${currentVersionSeq || 1}`;
  }
  return `创建版本 V${nextVersionSeq}`;
}

/** 生成中状态标题。 */
function formatGenerationActionTitle(actionLabel: string): string {
  if (actionLabel.startsWith("创建版本 V")) {
    return `正在${actionLabel}`;
  }
  if (actionLabel.startsWith("更新版本 V")) {
    return `正在${actionLabel}`;
  }
  return "正在生成需求版本";
}

/** 版本提交成功后使用强确认弹窗提示，避免短暂提示被用户错过。 */
function buildVersionSuccessNotice(
  actionLabel: string,
  versionNumber?: string,
  notification?: CustomizationDispatcherNotificationResult | null,
  hasDispatcher = false,
): WorkbenchNotice {
  const normalizedVersion = versionNumber?.trim() || "当前版本";
  const notifySuffix = buildAutoNotifySuccessSuffix(notification, hasDispatcher);
  if (actionLabel.startsWith("更新版本 V")) {
    return {
      title: "当前版本更新成功",
      message: `已更新 ${normalizedVersion}${notifySuffix}`,
    };
  }
  return {
    title: "新版本创建成功",
    message: `已创建 ${normalizedVersion}${notifySuffix}`,
  };
}

/** 根据自动通知计调结果拼装版本成功提示后缀。 */
function buildAutoNotifySuccessSuffix(
  notification?: CustomizationDispatcherNotificationResult | null,
  hasDispatcher = false,
): string {
  // 未配置第一计调时不会真正下发，成功确认不要写“正在通知计调”。
  if (!hasDispatcher) {
    return "";
  }
  if (notification && notification.sentCount > 0) {
    return "，已自动通知计调。仍可再次手动通知或生成行程。";
  }
  if (notification && notification.failedCount > 0) {
    const reason = notification.failureMessages[0]?.trim();
    return reason
      ? `，自动通知计调失败：${reason}。请确认后继续通知计调或生成行程。`
      : "，自动通知计调失败。请确认后继续通知计调或生成行程。";
  }
  if (notification && notification.skippedCount > 0) {
    const reason = notification.failureMessages[0]?.trim();
    return reason
      ? `，自动通知计调未发送：${reason}。请确认后继续通知计调或生成行程。`
      : "，自动通知计调未发送。请确认后继续通知计调或生成行程。";
  }
  // HTTP 入口异步推送时响应不带通知结果，提示已开始后台通知。
  return "，正在自动通知计调。仍可再次手动通知或生成行程。";
}

/** 必填项状态对应的 Arco Alert 类型。 */
function getRequiredStatusAlertType(
  status: ReturnType<typeof getDemandRequiredStatus>["status"],
): "success" | "error" | "info" {
  if (status === "complete") {
    return "success";
  }
  if (status === "missing" || status === "review") {
    return "error";
  }
  return "info";
}

/** 将工作台运行时提示统一收敛到弹窗，不挤占主内容区域。 */
function resolveWorkbenchNotice(messages: {
  mutationErrorMessage?: string;
  errorMessage?: string;
  referenceErrorMessage?: string;
  routeLinkErrorMessage?: string;
}): WorkbenchNotice | null {
  const mutationErrorMessage = messages.mutationErrorMessage?.trim();
  if (mutationErrorMessage) {
    return {
      title: "操作失败",
      message: mutationErrorMessage,
    };
  }
  const errorMessage = messages.errorMessage?.trim();
  if (errorMessage) {
    return {
      title: "加载失败",
      message: errorMessage,
    };
  }
  const referenceErrorMessage = messages.referenceErrorMessage?.trim();
  if (referenceErrorMessage) {
    return {
      title: "参考预览失败",
      message: referenceErrorMessage,
    };
  }
  const routeLinkErrorMessage = messages.routeLinkErrorMessage?.trim();
  if (routeLinkErrorMessage) {
    return {
      title: "行程预览失败",
      message: routeLinkErrorMessage,
    };
  }
  return null;
}

/** 顶部当前版本人员标签。 */
function WorkbenchStaffTag({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "sales" | "dispatcher";
}) {
  return (
    <div className={`customization-staff-tag is-${tone}`}>
      <span className="customization-staff-tag-label">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** 顶部关键状态指标。 */
function WorkbenchInsight({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "pending" | "review" | "missing" | "complete" | "neutral";
}) {
  const colors = customizationStatusColors[tone] ?? fallbackStatusColor;
  return (
    <div
      className={`customization-insight-chip is-${tone}`}
      style={{
        borderColor: `${colors.color}20`,
        background: colors.bgColor,
      }}
    >
      <span style={{ color: colors.color }}>{label}</span>
      <strong style={{ color: colors.color }}>{value}</strong>
    </div>
  );
}

/** 生成必填状态短标签。 */
function formatRequiredStatusChip(status: ReturnType<typeof getDemandRequiredStatus>): string {
  if (status.status === "complete") {
    return "完整";
  }
  if (status.status === "review") {
    return `待确认 ${status.reviewReasons.length}`;
  }
  if (status.status === "missing") {
    return `缺 ${status.missingFields.length}`;
  }
  return "待生成";
}

/** 生成中状态说明。 */
function formatGenerationBusyText(traceCount: number): string {
  if (traceCount > 0) {
    return `已锁定 ${traceCount} 条追溯，完成后会刷新当前版本。`;
  }
  return "AI 正在生成定制需求，完成后会刷新当前版本。";
}

/** 参考定制候选卡片。 */
const ReferenceDemandCandidates = memo(function ReferenceDemandCandidates({
  candidates,
  selectedDemandId,
  onToggle,
}: {
  candidates: CustomizationReferenceDemandCandidate[];
  selectedDemandId?: string | null;
  onToggle: (demandId: string) => void;
}) {
  if (candidates.length === 0) {
    return null;
  }

  return (
    <div className="customization-reference-demand-list" aria-label="参考定制候选">
      {candidates.map((candidate) => {
        const selected = selectedDemandId === candidate.demandId;
        return (
          <DemandReferencePreviewCard
            key={candidate.demandId}
            candidate={candidate}
            selected={selected}
            onToggle={onToggle}
          />
        );
      })}
    </div>
  );
});

/** 需求定制参考预览卡片，内容来自结构化需求版本。 */
function DemandReferencePreviewCard({
  candidate,
  selected = false,
  onToggle,
}: {
  candidate: CustomizationReferenceDemandCandidate;
  selected?: boolean;
  onToggle?: (demandId: string) => void;
}) {
  const selectable = Boolean(onToggle);
  return (
    <article
      className={`customization-preview-card customization-demand-preview-card${
        selectable ? " customization-demand-preview-card--selectable" : ""
      }${selected ? " is-selected" : ""}`}
      aria-label="需求定制预览"
    >
      {selectable ? (
        <Checkbox
          checked={selected}
          aria-label={`选择参考定制 ${candidate.demandId}`}
          onChange={() => onToggle?.(candidate.demandId)}
        />
      ) : null}
      <div className="customization-demand-preview-body">
        <div className="customization-preview-header customization-demand-preview-header">
          <div>
            <Text type="secondary" className="customization-preview-eyebrow">
              需求定制预览
            </Text>
            <strong>{candidate.title}</strong>
          </div>
          <Space wrap size="mini" className="customization-preview-tags">
            <Tag color="green">参考JD</Tag>
            <Tag color={selected ? "green" : "arcoblue"}>{candidate.referenceKey}</Tag>
          </Space>
        </div>
        <ul className="customization-preview-highlights customization-demand-preview-highlights">
          {candidate.highlights.map((highlight) => (
            <li key={highlight}>{highlight}</li>
          ))}
        </ul>
      </div>
    </article>
  );
}

/** 行程链接预览卡片。 */
const RouteLinkPreviewCard = memo(function RouteLinkPreviewCard({
  preview,
  loading,
}: {
  preview: CustomizationRouteLinkRecognitionResult | null;
  loading?: boolean;
}) {
  if (!preview?.route && !loading) {
    return null;
  }

  const route = preview?.route;
  const dateRange = route ? formatRoutePreviewDateRange(route.startDate, route.endDate) : "";
  const highlights = route ? buildRoutePreviewHighlights(route, dateRange) : [];

  return (
    <article className="customization-preview-card customization-route-preview-card" aria-label="行程预览">
      <div className="customization-preview-header customization-route-preview-header">
        <div>
          <Text type="secondary" className="customization-preview-eyebrow customization-route-preview-eyebrow">
            行程预览
          </Text>
          <strong>{route?.title ?? "行程预览加载中"}</strong>
        </div>
        <Tag color="green">行程</Tag>
      </div>
      {route ? (
        <ul className="customization-preview-highlights customization-route-preview-highlights">
          {highlights.map((highlight) => (
            <li key={highlight}>{highlight}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
});

/** 格式化通知计调按钮文案。 */
function formatDispatcherNotifyLabel(dispatcherName?: string): string {
  const name = dispatcherName?.trim();
  return name ? `通知计调${name}` : "通知计调";
}

/** 解析通知模板里可直接展示的登录人姓名，未加载时不返回占位值。 */
function resolveNotificationActorName(user: AuthUser | null | undefined): string {
  return (
    user?.displayName?.trim() ||
    user?.realname?.trim() ||
    user?.nickname?.trim() ||
    user?.username?.trim() ||
    ""
  );
}

/** 构造通知模板下拉选项。 */
function buildDispatcherActionOptions({
  versionNumber,
  actorName,
  canReviewItinerary,
}: {
  versionNumber?: string;
  actorName?: string;
  canReviewItinerary: boolean;
}): Array<{ label: string; value: string }> {
  const normalizedVersion = versionNumber?.trim() || "当前版本";
  const normalizedActorName = actorName?.trim();
  if (!normalizedActorName) {
    return [];
  }
  const options = [
    `${normalizedActorName}已修改版本 ${normalizedVersion}`,
    `${normalizedActorName}已创建版本 ${normalizedVersion}，请你做行程`,
  ];
  if (canReviewItinerary) {
    options.push(`${normalizedActorName}修改了行程，请你审核`);
  }
  return options.map((value) => ({ label: value, value }));
}

/** 格式化行程预览日期范围。 */
function formatRoutePreviewDateRange(startDate?: string, endDate?: string): string {
  if (startDate && endDate) {
    return `${startDate} ~ ${endDate}`;
  }
  return startDate ?? endDate ?? "";
}

/** 生成行程预览的主要业务摘要，避免暴露技术字段。 */
function buildRoutePreviewHighlights(
  route: NonNullable<CustomizationRouteLinkRecognitionResult["route"]>,
  dateRange: string,
): string[] {
  const dispatcherName = route.dispatcherUserName?.trim() || route.dispatcherUserId?.trim();
  const pointNames = (route.pointNames ?? []).map((item) => item.trim()).filter(Boolean);
  const stayAreaNames = (route.stayAreaNames ?? []).map((item) => item.trim()).filter(Boolean);

  return [
    dateRange ? `日期：${dateRange}` : "",
    dispatcherName ? `第一计调：${dispatcherName}` : "",
    pointNames.length > 0 ? `考察点：${pointNames.join("、")}` : "",
    stayAreaNames.length > 0 ? `住宿点：${stayAreaNames.join("、")}` : "",
    route.summary?.trim() ?? "",
  ].filter(Boolean);
}

const serviceDescriptionFieldOrder = new Map(
  [
    "roundTripTransportNote",
    "localTransportNote",
    "accommodationNote",
    "guideCount",
    "guideNote",
    "includeTickets",
    "ticketNote",
    "includeMeals",
    "mealStandards",
    "mealNote",
    "companyVisitCount",
    "companyVisitNote",
    "intangibleHeritageCount",
    "intangibleHeritageNote",
  ].map((key, index) => [key, index]),
);
const hiddenWorkbenchDemandFieldKeys = new Set(["includeTickets", "includeMeals", "mealStandards"]);
const requiredDemandCompanionFieldKeys = new Set(["guideNote"]);
const studentTeacherCountDisplayFieldKey = "studentTeacherCount";
const studentTeacherCountDisplayFieldLabel = "学生人数 / 老师人数";

/** 将需求展示字段重排为必填项和其他两类展示板块。 */
function buildRequirementDemandDisplayGroups(
  groups: CustomizationDemandDisplayGroup[],
  customerType?: string | null,
): CustomizationDemandDisplayGroup[] {
  const requiredFields: CustomizationDemandDisplayField[] = [];
  const otherFields: CustomizationDemandDisplayField[] = [];

  for (const group of groups) {
    const fields = mergeStudentTeacherCountFields(buildDemandDescriptionFields(group));
    for (const field of fields) {
      const inRequiredSection = isRequiredDemandSectionField(field.key, customerType);
      const required = isRequiredDemandDisplayField(field.key, customerType);
      const nextField = { ...field, required };
      if (inRequiredSection) {
        requiredFields.push(nextField);
      } else {
        otherFields.push(nextField);
      }
    }
  }

  return [
    {
      key: "required",
      title: "必填项",
      fields: requiredFields,
    },
    {
      key: "other",
      title: "其他",
      fields: otherFields,
    },
  ].filter((group) => group.fields.length > 0);
}

/** 将学生人数和老师人数合并为同一张展示卡片。 */
function mergeStudentTeacherCountFields(
  fields: CustomizationDemandDisplayField[],
): CustomizationDemandDisplayField[] {
  const studentField = fields.find((field) => isStudentCountFieldKey(field.key));
  const teacherField = fields.find((field) => isTeacherCountFieldKey(field.key));
  if (!studentField || !teacherField) {
    return fields;
  }

  return fields.flatMap((field) => {
    if (isStudentCountFieldKey(field.key)) {
      return [buildStudentTeacherCountField(studentField, teacherField)];
    }
    if (isTeacherCountFieldKey(field.key)) {
      return [];
    }
    return [field];
  });
}

/** 构建学生和老师人数的合并展示字段。 */
function buildStudentTeacherCountField(
  studentField: CustomizationDemandDisplayField,
  teacherField: CustomizationDemandDisplayField,
): CustomizationDemandDisplayField {
  const studentValue = formatStudentTeacherCountPart(studentField);
  const teacherValue = formatStudentTeacherCountPart(teacherField);
  const hit = [...(studentField.hit ?? []), ...(teacherField.hit ?? [])];

  return {
    ...studentField,
    key: studentTeacherCountDisplayFieldKey,
    label: studentTeacherCountDisplayFieldLabel,
    kind: "number",
    value: `${studentValue} + ${teacherValue}`,
    displayText: `${studentValue} + ${teacherValue}`,
    changed: Boolean(studentField.changed || teacherField.changed),
    hit: hit.length > 0 ? hit : undefined,
  };
}

/** 格式化合并人数卡片内的单个人数字段。 */
function formatStudentTeacherCountPart(field: CustomizationDemandDisplayField): string {
  const value = field.displayText?.trim() || field.value?.trim();
  return value && value !== "--" ? value : "--";
}

/** 判断是否为学生人数字段。 */
function isStudentCountFieldKey(fieldKey: string): boolean {
  return fieldKey === "studentCount" || fieldKey === "student_count";
}

/** 判断是否为老师人数字段。 */
function isTeacherCountFieldKey(fieldKey: string): boolean {
  return fieldKey === "teacherCount" || fieldKey === "teacher_count";
}

/** 判断工作台展示字段是否需要必填标记。 */
function isRequiredDemandDisplayField(
  fieldKey: string,
  customerType?: string | null,
): boolean {
  if (fieldKey === studentTeacherCountDisplayFieldKey) {
    return true;
  }
  return isRequiredDemandField(fieldKey, { customerType });
}

/** 判断字段是否进入必填项分区。 */
function isRequiredDemandSectionField(
  fieldKey: string,
  customerType?: string | null,
): boolean {
  return (
    isRequiredDemandDisplayField(fieldKey, customerType) ||
    isDestinationDemandField(fieldKey) ||
    requiredDemandCompanionFieldKeys.has(fieldKey)
  );
}

/** 生成描述表格字段，服务标准使用固定顺序让长字段独占整行。 */
function buildDemandDescriptionFields(
  group: CustomizationDemandDisplayGroup,
): CustomizationDemandDisplayField[] {
  if (group.key !== "service") {
    return group.fields;
  }

  return [...group.fields]
    .filter((field) => !hiddenWorkbenchDemandFieldKeys.has(field.key))
    .sort((left, right) => {
      const leftIndex = serviceDescriptionFieldOrder.get(left.key) ?? Number.MAX_SAFE_INTEGER;
      const rightIndex = serviceDescriptionFieldOrder.get(right.key) ?? Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    })
    .map((field) => ({
      ...field,
      span: resolveServiceDescriptionFieldSpan(field),
    }));
}

/** 服务标准字段跨列：企业/非遗同行结束，餐饮、门票和餐饮要求单独一行。 */
function resolveServiceDescriptionFieldSpan(field: CustomizationDemandDisplayField): number | undefined {
  if (field.key === "companyVisitNote" || field.key === "intangibleHeritageNote") {
    return 2;
  }
  if (field.key === "guideNote") {
    return 2;
  }
  if (field.key === "mealStandards") {
    return 3;
  }
  if (field.key === "ticketNote" || field.key === "mealNote") {
    return 3;
  }
  return field.span;
}

/** 渲染版本下拉选中值，拆成短标签避免长文案挤在一起。 */
function renderDemandVersionSelectValue(
  version: CustomizationDemandVersionOption,
  requiredStatusView?: DemandVersionRequiredStatusView,
) {
  return (
    <span className="customization-version-select-value">
      <span className="customization-version-select-version">{version.versionNumber}</span>
      {version.isCurrent ? <span className="customization-version-current-pill">当前</span> : null}
      <span className={`customization-version-required-pill ${getDemandVersionRequiredClass(version, requiredStatusView)}`}>
        {getDemandVersionRequiredStatus(version, requiredStatusView)}
      </span>
    </span>
  );
}

/** 版本下拉选项内容，使用分段标签提升可扫读性。 */
function DemandVersionOptionLabel({
  version,
  requiredStatusView,
  isGenerating = false,
}: {
  version: CustomizationDemandVersionOption;
  requiredStatusView?: DemandVersionRequiredStatusView;
  isGenerating?: boolean;
}) {
  return (
    <span className="customization-version-option-content">
      <span className="customization-version-option-version">{version.versionNumber}</span>
      <span className="customization-version-option-status">
        {version.isCurrent ? <span className="customization-version-current-pill">当前</span> : null}
        <span className={`customization-version-required-pill ${getDemandVersionRequiredClass(version, requiredStatusView)}`}>
          {getDemandVersionRequiredStatus(version, requiredStatusView)}
        </span>
        <DemandVersionItineraryPill version={version} isGenerating={isGenerating} />
      </span>
    </span>
  );
}

/** 行程状态小标签。 */
function DemandVersionItineraryPill({
  version,
  compact = false,
  isGenerating = false,
}: {
  version: CustomizationDemandVersionOption;
  compact?: boolean;
  isGenerating?: boolean;
}) {
  return (
    <span
      className={`customization-version-itinerary-pill ${getDemandVersionItineraryClass(version, isGenerating)}${
        compact ? " is-compact" : ""
      }`}
    >
      {isGenerating
        ? compact ? "生" : "生成中"
        : version.hasItinerary ? compact ? "行" : "行程" : compact ? "无" : "无行程"}
    </span>
  );
}

/** 获取版本按钮必填项状态样式。 */
function getDemandVersionRequiredClass(
  version: CustomizationDemandVersionOption,
  requiredStatusView?: DemandVersionRequiredStatusView,
): string {
  const versionRequiredStatus = getDemandVersionRequiredStatusView(version, requiredStatusView);
  if (versionRequiredStatus.status === "review") {
    return "is-review";
  }
  return versionRequiredStatus.status === "missing" ? "is-missing" : "is-complete";
}

/** 获取版本行程状态样式。 */
function getDemandVersionItineraryClass(
  version: CustomizationDemandVersionOption,
  isGenerating = false,
): string {
  if (isGenerating) return "is-generating";
  return version.hasItinerary ? "has-itinerary" : "no-itinerary";
}

/** 格式化版本按钮悬停提示。 */
function formatDemandVersionRequiredTitle(
  version: CustomizationDemandVersionOption,
  requiredStatusView?: DemandVersionRequiredStatusView,
): string {
  const { missingFields, reviewReasons } = getDemandVersionRequiredStatusView(version, requiredStatusView);
  if (reviewReasons.length > 0) {
    return missingFields.length > 0
      ? `必填项待确认：${reviewReasons.join("、")}；待补字段：${missingFields.join("、")}`
      : `必填项待确认：${reviewReasons.join("、")}`;
  }
  return missingFields.length > 0 ? `缺少必填项：${missingFields.join("、")}` : "必填项完整";
}

/** 格式化版本行程状态提示。 */
function formatDemandVersionItineraryTitle(
  version: CustomizationDemandVersionOption,
  isGenerating = false,
): string {
  if (isGenerating) return "正在通过行程计划智能体生成初版行程";
  if (version.hasItinerary) {
    const count = version.itineraryCount && version.itineraryCount > 0 ? version.itineraryCount : 1;
    return `已有行程 ${count} 条`;
  }
  return "无行程";
}

/** 获取顶部当前版本行程状态。 */
function getDemandVersionItineraryInsight({
  hasDemand,
  isGenerating,
  isAgentGenerating = false,
  version,
}: {
  hasDemand: boolean;
  isGenerating: boolean;
  isAgentGenerating?: boolean;
  version?: CustomizationDemandVersionOption | null;
}): { value: string; tone: "pending" | "complete" | "neutral" } {
  if (!hasDemand) {
    return { value: "待生成", tone: "pending" };
  }
  if (isGenerating) {
    return { value: "生成中", tone: "pending" };
  }
  if (isAgentGenerating) {
    return { value: "生成中", tone: "pending" };
  }
  if (version?.hasItinerary) {
    return { value: "已有行程", tone: "complete" };
  }
  return { value: "无行程", tone: "neutral" };
}

/** 获取版本必填项状态短文案。 */
function getDemandVersionRequiredStatus(
  version: CustomizationDemandVersionOption,
  requiredStatusView?: DemandVersionRequiredStatusView,
): string {
  const versionRequiredStatus = getDemandVersionRequiredStatusView(version, requiredStatusView);
  if (versionRequiredStatus.status === "review") {
    return "待确认";
  }
  if (versionRequiredStatus.status === "missing") {
    return "缺必填";
  }
  return versionRequiredStatus.status === "pending" ? "待生成" : "完整";
}

/** 获取版本必填项视图状态，当前草稿可覆盖服务端版本列表状态。 */
function getDemandVersionRequiredStatusView(
  version: CustomizationDemandVersionOption,
  requiredStatusView?: DemandVersionRequiredStatusView,
): DemandVersionRequiredStatusView {
  if (requiredStatusView) {
    return requiredStatusView;
  }
  const missingFields = normalizeDemandMissingFields(version.needsHumanReviewReasons);
  const reviewReasons = normalizeDemandReviewReasons(version.needsHumanReviewReasons);
  return {
    missingFields,
    reviewReasons,
    status: reviewReasons.length > 0 ? "review" : missingFields.length > 0 ? "missing" : "complete",
  };
}

/** 需求定制分组字段块。 */
const DemandDisplayGroup = memo(function DemandDisplayGroup({
  group,
  disableDispatcherEdit = false,
  onEditField,
  inlineEditor,
  inlineDraft,
  onInlineDraftChange,
  onInlineSave,
  onInlineCancel,
}: {
  group: CustomizationDemandDisplayGroup;
  disableDispatcherEdit?: boolean;
  onEditField?: (field: CustomizationDemandDisplayField) => void;
  inlineEditor?: InlineProjectEditorState | null;
  inlineDraft?: CustomizationProjectUpdatePayload | null;
  onInlineDraftChange?: <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => void;
  onInlineSave?: () => void;
  onInlineCancel?: () => void;
}) {
  const fields = buildDemandDescriptionFields(group);

  return (
    <section
      className={`customization-demand-group${
        group.key === "service" ? " customization-demand-group--service" : ""
      }`}
    >
      <div className="customization-demand-group-heading">
        <Text className="customization-demand-group-title">{group.title}</Text>
        <span>{fields.length} 项</span>
      </div>
      <div className="customization-demand-field-grid">
        {fields.map((field) => (
          <DemandFieldCard
            key={`${group.key}-${field.key}`}
            field={field}
            editableOverride={disableDispatcherEdit && field.key === "dispatcherUserName" ? false : undefined}
            onEdit={onEditField}
            inlineEditor={inlineEditor?.displayFieldKey === field.key ? inlineEditor : null}
            inlineDraft={inlineDraft}
            onInlineDraftChange={onInlineDraftChange}
            onInlineSave={onInlineSave}
            onInlineCancel={onInlineCancel}
          />
        ))}
      </div>
    </section>
  );
}, areDemandDisplayGroupPropsEqual);

/** 仅在需求分组引用变化时重渲染展示分组，避免输入态牵动整组卡片。 */
function areDemandDisplayGroupPropsEqual(
  previous: {
    group: CustomizationDemandDisplayGroup;
    disableDispatcherEdit?: boolean;
    onEditField?: (field: CustomizationDemandDisplayField) => void;
    inlineEditor?: InlineProjectEditorState | null;
    inlineDraft?: CustomizationProjectUpdatePayload | null;
  },
  next: {
    group: CustomizationDemandDisplayGroup;
    disableDispatcherEdit?: boolean;
    onEditField?: (field: CustomizationDemandDisplayField) => void;
    inlineEditor?: InlineProjectEditorState | null;
    inlineDraft?: CustomizationProjectUpdatePayload | null;
  },
): boolean {
  return (
    previous.group === next.group &&
    previous.disableDispatcherEdit === next.disableDispatcherEdit &&
    previous.inlineEditor === next.inlineEditor &&
    previous.inlineDraft === next.inlineDraft
  );
}

/** 单个需求字段块。 */
function DemandFieldCard({
  field,
  editableOverride,
  onEdit,
  inlineEditor,
  inlineDraft,
  onInlineDraftChange,
  onInlineSave,
  onInlineCancel,
}: {
  field: CustomizationDemandDisplayField;
  editableOverride?: boolean;
  onEdit?: (field: CustomizationDemandDisplayField) => void;
  inlineEditor?: InlineProjectEditorState | null;
  inlineDraft?: CustomizationProjectUpdatePayload | null;
  onInlineDraftChange?: <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => void;
  onInlineSave?: () => void;
  onInlineCancel?: () => void;
}) {
  const isInlineEditing = Boolean(inlineEditor && inlineDraft);
  const editable = editableOverride ?? Boolean(onEdit && resolveProjectEditFieldsByDemandField(field.key));
  const fieldClassName = [
    "customization-demand-field-card",
    field.span && field.span >= 3 ? "is-wide" : "",
    field.span === 2 ? "is-half" : "",
    field.changed ? "is-changed" : "",
    isDemandFieldValueMissing(field) ? "is-missing" : "",
    editable && !isInlineEditing ? "is-editable" : "",
    isInlineEditing ? "is-inline-editing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <div className="customization-demand-field-label">
        <DemandFieldLabel field={field} />
      </div>
      {inlineEditor && inlineDraft && onInlineDraftChange && onInlineSave && onInlineCancel ? (
        <InlineDemandFieldEditor
          field={inlineEditor.fieldKey}
          label={field.label}
          draft={inlineDraft}
          onChange={onInlineDraftChange}
          onSave={onInlineSave}
          onCancel={onInlineCancel}
        />
      ) : (
        <DemandFieldValue field={field} />
      )}
    </>
  );

  return (
    <article
      className={fieldClassName}
      role={editable && !isInlineEditing ? "button" : undefined}
      tabIndex={editable && !isInlineEditing ? 0 : undefined}
      aria-label={editable && !isInlineEditing ? `编辑${field.label}` : undefined}
      style={editable && !isInlineEditing ? demandFieldCardCopyableStyle : undefined}
      onClick={(event) => {
        if (!editable || isInlineEditing || hasActiveTextSelection(event.currentTarget)) {
          return;
        }
        onEdit?.(field);
      }}
      onKeyDown={(event) => {
        if (!editable || isInlineEditing || (event.key !== "Enter" && event.key !== " ")) {
          return;
        }
        event.preventDefault();
        onEdit?.(field);
      }}
    >
      {content}
    </article>
  );
}

/** 需求字段卡片内联编辑器。 */
function InlineDemandFieldEditor({
  field,
  label,
  draft,
  onChange,
  onSave,
  onCancel,
}: {
  field: ProjectEditFieldKey;
  label: string;
  draft: CustomizationProjectUpdatePayload;
  onChange: <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const value = draft[field];
  const stringValue = value === null || value === undefined ? "" : String(value);
  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };
  const handleTextAreaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Escape") {
      return;
    }
    event.preventDefault();
    onCancel();
  };

  const editor = projectInlineTextAreaEditFieldKeys.includes(field) ? (
    <TextArea
      aria-label={label}
      value={stringValue}
      autoFocus
      autoSize={{ minRows: 2, maxRows: 5 }}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={handleTextAreaKeyDown}
      onBlur={onSave}
      onChange={(nextValue) => onChange(field, nextValue as never)}
    />
  ) : projectInlineNumberEditFieldKeys.includes(field) ? (
    <Input
      aria-label={label}
      value={stringValue}
      autoFocus
      inputMode="numeric"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={handleInputKeyDown}
      onBlur={onSave}
      onChange={(nextValue) => onChange(field, parseOptionalNumber(nextValue) as never)}
    />
  ) : (
    <Input
      aria-label={label}
      value={stringValue}
      autoFocus
      onClick={(event) => event.stopPropagation()}
      onKeyDown={handleInputKeyDown}
      onBlur={onSave}
      onChange={(nextValue) => onChange(field, nextValue as never)}
    />
  );

  return (
    <div
      className="customization-demand-inline-editor"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {editor}
    </div>
  );
}

/** 判断当前字段卡片内是否已有文本选择，避免复制时触发编辑。 */
function hasActiveTextSelection(container: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) {
    return false;
  }
  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;
  return Boolean(
    (anchorNode && container.contains(anchorNode)) || (focusNode && container.contains(focusNode)),
  );
}

/** 判断字段块是否缺失，用于轻量标记空必填项。 */
function isDemandFieldValueMissing(field: CustomizationDemandDisplayField): boolean {
  const value = field.displayText?.trim() || field.value?.trim() || "";
  const required =
    typeof field.required === "boolean"
      ? field.required
      : isRequiredDemandDisplayField(field.key);
  return required && (!value || value === "--" || value.includes("--"));
}

/** 判断字段是否可以直接在卡片内编辑。 */
function shouldUseInlineProjectEditor(
  fields: ProjectEditFieldKey[] | null,
): fields is [ProjectEditFieldKey] {
  return Boolean(fields && fields.length === 1 && projectInlineEditFieldKeys.has(fields[0]));
}

/** 根据必填状态里展示的字段文案反推编辑字段。 */
function resolveProjectEditFieldsByRequiredStatus(status: DemandRequiredStatus): ProjectEditFieldKey[] {
  return uniqueProjectEditFields(
    status.missingFields.flatMap((label) => resolveProjectEditFieldsByRequiredLabel(label)),
  );
}

/** 根据单个必填文案反推编辑字段。 */
function resolveProjectEditFieldsByRequiredLabel(label: string): ProjectEditFieldKey[] {
  if (label === "目的地信息") {
    return ["provinces", "pointNames", "stayAreaNames"];
  }
  if (label === "学生人数 / 老师人数") {
    return ["studentCount", "teacherCount"];
  }
  if (label === "出发高校" || label === "出发高校/组织") {
    return ["primarySchoolId"];
  }
  const matchedEntry = Object.entries(projectEditFieldLabels).find(([, fieldLabel]) => fieldLabel === label);
  return matchedEntry ? [matchedEntry[0] as ProjectEditFieldKey] : [];
}

/** 保持字段顺序去重。 */
function uniqueProjectEditFields(fields: ProjectEditFieldKey[]): ProjectEditFieldKey[] {
  const result: ProjectEditFieldKey[] = [];
  for (const field of fields) {
    if (!result.includes(field)) {
      result.push(field);
    }
  }
  return result;
}

/** 获取版本选择框返回的需求版本 ID。 */
function resolveDemandVersionSelectValue(value: unknown): string {
  if (value && typeof value === "object" && "value" in value) {
    return String((value as { value: unknown }).value);
  }
  return String(value);
}

/** 根据需求字段找到对应项目编辑字段；高校/客户/联系人联动编辑。 */
function resolveProjectEditFieldsByDemandField(fieldKey: string): ProjectEditFieldKey[] | null {
  const identityFieldMap: Record<string, ProjectEditFieldKey[]> = {
    primarySchoolNameSnapshot: ["primarySchoolId"],
    primary_school_name_snapshot: ["primarySchoolId"],
    customerName: ["primarySchoolId", "customerId"],
    customer_name: ["primarySchoolId", "customerId"],
    contactName: ["primarySchoolId", "contactId"],
    contact_name: ["primarySchoolId", "contactId"],
  };
  const identityFields = identityFieldMap[fieldKey];
  if (identityFields) {
    return identityFields;
  }
  if (fieldKey === studentTeacherCountDisplayFieldKey) {
    return ["studentCount", "teacherCount"];
  }
  const directMap: Record<string, ProjectEditFieldKey> = {
    provinces: "provinces",
    provinceNames: "provinces",
    province_names: "provinces",
    majorNames: "majorNames",
    major_names: "majorNames",
    pointNames: "pointNames",
    point_names: "pointNames",
    stayAreaNames: "stayAreaNames",
    stay_area_names: "stayAreaNames",
    departureStartDate: "departureStartDate",
    departure_start_date: "departureStartDate",
    durationDays: "durationDays",
    duration_days: "durationDays",
    studentCount: "studentCount",
    student_count: "studentCount",
    teacherCount: "teacherCount",
    teacher_count: "teacherCount",
    budgetPerPerson: "budgetPerPerson",
    budget_per_person: "budgetPerPerson",
    customerType: "customerType",
    customer_type: "customerType",
    tripType: "tripType",
    trip_type: "tripType",
    signType: "signType",
    sign_type: "signType",
    dispatcherUserName: "dispatcherUserId",
    dispatcher_user_name: "dispatcherUserId",
    operatingCompany: "operatingCompany",
    operating_company: "operatingCompany",
    teachingRequirements: "teachingRequirements",
    teaching_requirements: "teachingRequirements",
    specialRequirements: "specialRequirements",
    special_requirements: "specialRequirements",
    schoolPickupRequirement: "schoolPickupRequirement",
    school_pickup_requirement: "schoolPickupRequirement",
    invoiceRequirement: "invoiceRequirement",
    invoice_requirement: "invoiceRequirement",
    remark: "remark",
    teacherMealRequirement: "teacherMealRequirement",
    teacher_meal_requirement: "teacherMealRequirement",
    teacherTicketRequirement: "teacherTicketRequirement",
    teacher_ticket_requirement: "teacherTicketRequirement",
    teacherTransportRequirement: "teacherTransportRequirement",
    teacher_transport_requirement: "teacherTransportRequirement",
    teacherAccommodationRequirement: "teacherAccommodationRequirement",
    teacher_accommodation_requirement: "teacherAccommodationRequirement",
    roundTripTransportNote: "roundTripTransportNote",
    round_trip_transport_note: "roundTripTransportNote",
    localTransportNote: "localTransportNote",
    local_transport_note: "localTransportNote",
    accommodationNote: "accommodationNote",
    accommodation_note: "accommodationNote",
    guideCount: "guideCount",
    guide_count: "guideCount",
    guideNote: "guideNote",
    guide_note: "guideNote",
    includeTickets: "includeTickets",
    include_tickets: "includeTickets",
    ticketNote: "ticketNote",
    ticket_note: "ticketNote",
    includeMeals: "includeMeals",
    include_meals: "includeMeals",
    mealStandards: "mealStandards",
    meal_standards: "mealStandards",
    mealNote: "mealNote",
    meal_note: "mealNote",
    companyVisitCount: "companyVisitCount",
    company_visit_count: "companyVisitCount",
    companyVisitNote: "companyVisitNote",
    company_visit_note: "companyVisitNote",
    intangibleHeritageCount: "intangibleHeritageCount",
    intangible_heritage_count: "intangibleHeritageCount",
    intangibleHeritageNote: "intangibleHeritageNote",
    intangible_heritage_note: "intangibleHeritageNote",
  };
  const field = directMap[fieldKey];
  return field ? [field] : null;
}

/** 格式化项目编辑弹窗标题。 */
function formatProjectEditorTitle(fields: ProjectEditFieldKey[]): string {
  if (fields.length === allProjectEditFieldKeys.length) {
    return "编辑需求定制";
  }
  if (projectIdentityEditFieldKeys.every((field) => fields.includes(field))) {
    return "编辑出发高校/组织、客户和联系人";
  }
  if (areProjectEditFieldsEqual(fields, ["primarySchoolId", "customerId"])) {
    return "编辑出发高校/组织和客户";
  }
  if (areProjectEditFieldsEqual(fields, ["primarySchoolId", "contactId"])) {
    return "编辑出发高校/组织和联系人";
  }
  if (fields.length > 3) {
    return `补齐 ${fields.length} 项必填信息`;
  }
  return `编辑${fields.map((field) => projectEditFieldLabels[field]).join("、")}`;
}

/** 格式化项目编辑弹窗样式类，供不同字段规模适配布局。 */
function formatProjectEditorModalClassName(
  isCompactProjectEditorModal: boolean,
  fields: ProjectEditFieldKey[],
): string {
  const classes = [
    isCompactProjectEditorModal
      ? "customization-project-editor-single-field-modal"
      : "customization-project-editor-multi-field-modal",
  ];
  if (
    isCompactProjectEditorModal &&
    fields.some((field) => projectIdentityEditFieldKeys.includes(field))
  ) {
    classes.push("customization-project-editor-compact-identity-modal");
  }
  return classes.join(" ");
}

/** 判断弹窗字段集合是否完全一致，忽略顺序。 */
function areProjectEditFieldsEqual(
  fields: ProjectEditFieldKey[],
  expected: ProjectEditFieldKey[],
): boolean {
  return fields.length === expected.length && expected.every((field) => fields.includes(field));
}

/** 全量编辑表单延迟挂载前的轻量占位，避免重控件阻塞弹窗打开首帧。 */
function ProjectEditorPanelLoading() {
  return (
    <div className="customization-project-editor-loading" aria-label="项目编辑表单加载中">
      <Spin loading />
    </div>
  );
}

/** 单字段项目编辑区，只挂载当前字段需要的控件和候选派生。 */
function SingleProjectEditPanel({
  value,
  field,
  fieldOptions,
  demandFieldOptions,
  editOptions,
  optionsLoading,
  projectPointNameOptions,
  projectPointNamesLoading,
  onChange,
  onIdentityChange,
  onOptionsOpen,
  onCollegeSearch,
  onPointNamesSearch,
  onSettled,
  onOpenDispatcherSelection,
}: {
  value: CustomizationProjectUpdatePayload;
  field: ProjectEditFieldKey;
  fieldOptions?: CustomizationProjectFieldOptions;
  demandFieldOptions?: CustomizationDemandFieldOptions;
  editOptions?: CustomizationProjectEditOptions | null;
  optionsLoading?: boolean;
  projectPointNameOptions: string[];
  projectPointNamesLoading?: boolean;
  onChange: <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => void;
  onIdentityChange: (patch: CustomizationProjectUpdatePayload) => void;
  onOptionsOpen?: () => void;
  onCollegeSearch?: (keyword?: string) => void;
  onPointNamesSearch: (keyword?: string) => void;
  onSettled?: () => void;
  onOpenDispatcherSelection: (options: {
    selectedUserId?: string;
    selectedDisplayName?: string;
    onConfirm: (candidate: CustomizationDispatcherSelectionCandidate | null) => void;
  }) => void;
}) {
  const label = projectEditFieldLabels[field];
  const [localValue, setLocalValue] = useState<CustomizationProjectUpdatePayload>(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleLocalChange = <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    nextValue: CustomizationProjectUpdatePayload[K],
  ) => {
    setLocalValue((current) => applyProjectEditDraftPatch(current, { [key]: nextValue } as ProjectEditPatch));
    onChange(key, nextValue);
  };

  const handleLocalIdentityChange = (patch: CustomizationProjectUpdatePayload) => {
    setLocalValue((current) => applyProjectEditDraftPatch(current, patch));
    onIdentityChange(patch);
  };

  const collegeCandidates = editOptions?.collegeCandidates ?? emptyCollegeCandidates;
  const customerCandidates = editOptions?.customerCandidates ?? emptyCustomerCandidates;
  const contactCandidates = editOptions?.contactCandidates ?? emptyContactCandidates;
  const majorCandidates = editOptions?.majorCandidates ?? emptyMajorCandidates;
  const collegeSelectOptions = useMemo(
    () => buildCollegeSelectOptions(collegeCandidates, localValue.primarySchoolId, localValue.primarySchoolName),
    [collegeCandidates, localValue.primarySchoolId, localValue.primarySchoolName],
  );
  const customerSelectOptions = useMemo(
    () => buildCustomerSelectOptions(customerCandidates, localValue.customerId, localValue.customerName),
    [customerCandidates, localValue.customerId, localValue.customerName],
  );
  const contactSelectOptions = useMemo(
    () => buildContactSelectOptions(contactCandidates, localValue.contactId, localValue.contactName),
    [contactCandidates, localValue.contactId, localValue.contactName],
  );
  const majorSelectOptions = useMemo(
    () => buildMajorSelectOptions(majorCandidates, localValue.majorNames),
    [majorCandidates, localValue.majorNames],
  );
  const majorSlashSelectOptions = useMemo(
    () => toSlashLabeledSelectOptions(majorSelectOptions),
    [majorSelectOptions],
  );
  const majorSlashLabelByValue = useMemo(
    () => new Map(majorSelectOptions.map((option) => [option.value, option.label])),
    [majorSelectOptions],
  );
  const majorSlashFilterOption = useMemo(
    () => createSlashLabelFilterOption(majorSelectOptions),
    [majorSelectOptions],
  );

  const renderField = () => {
    if (field === "primarySchoolId") {
      return (
        <ProjectEditFieldFrame label={label} meta="单选">
          <Select
            aria-label="出发高校/组织"
            value={localValue.primarySchoolId ?? ""}
            options={collegeSelectOptions}
            loading={optionsLoading}
            allowClear
            showSearch
            filterOption={() => true}
            placeholder="出发高校/组织"
            virtualListProps={projectEditSelectVirtualListProps}
            triggerProps={projectEditSelectTriggerProps}
            onVisibleChange={(visible) => {
              if (visible) {
                onCollegeSearch?.("");
              } else {
                onSettled?.();
              }
            }}
            onSearch={(inputValue) => onCollegeSearch?.(inputValue)}
            onChange={(nextValue) => {
              const selectedValue = formatProjectEditSelectValue(nextValue);
              const candidate = editOptions?.collegeCandidates.find((item) => item.id === selectedValue);
              handleLocalIdentityChange({
                primarySchoolId: selectedValue || undefined,
                primarySchoolName: candidate?.name ?? undefined,
                primarySchoolOrgType: candidate?.orgType ?? undefined,
                customerId: "",
                customerName: "",
                contactId: "",
                contactName: "",
              });
            }}
          />
        </ProjectEditFieldFrame>
      );
    }

    if (field === "customerId") {
      return (
        <ProjectEditFieldFrame
          label={label}
          wide
          meta={
            isSocialCustomization(localValue.customerType)
              ? "社会类定制可不填"
              : undefined
          }
        >
          <Select
            aria-label="客户"
            value={localValue.customerId ?? ""}
            options={customerSelectOptions}
            loading={optionsLoading}
            allowClear
            showSearch
            filterOption={projectEditObjectSelectFilterOption}
            placeholder="客户"
            virtualListProps={projectEditSelectVirtualListProps}
            triggerProps={projectEditSelectTriggerProps}
            onVisibleChange={(visible) => {
              if (visible) {
                onOptionsOpen?.();
              } else {
                onSettled?.();
              }
            }}
            onChange={(nextValue) => {
              const selectedValue = formatProjectEditSelectValue(nextValue);
              const candidate = editOptions?.customerCandidates.find((item) => item.id === selectedValue);
              handleLocalIdentityChange({
                customerId: selectedValue || "",
                customerName: candidate?.name ?? "",
              });
            }}
          />
        </ProjectEditFieldFrame>
      );
    }

    if (field === "contactId") {
      return (
        <ProjectEditFieldFrame label={label} meta="单选">
          <Select
            aria-label="客户联系人"
            value={localValue.contactId ?? ""}
            options={contactSelectOptions}
            loading={optionsLoading}
            allowClear
            showSearch
            filterOption={projectEditObjectSelectFilterOption}
            placeholder="客户联系人"
            virtualListProps={projectEditSelectVirtualListProps}
            triggerProps={projectEditSelectTriggerProps}
            onVisibleChange={(visible) => {
              if (visible) {
                onOptionsOpen?.();
              } else {
                onSettled?.();
              }
            }}
            onChange={(nextValue) => {
              const selectedValue = formatProjectEditSelectValue(nextValue);
              const candidate = editOptions?.contactCandidates.find((item) => item.id === selectedValue);
              handleLocalIdentityChange({
                contactId: selectedValue || "",
                contactName: candidate?.name ?? "",
              });
            }}
          />
        </ProjectEditFieldFrame>
      );
    }

    if (field === "provinces") {
      return (
        <ProjectEditNameMultiSelectField
          label={label}
          value={localValue.provinces}
          options={editOptions?.provinceNames ?? []}
          loading={optionsLoading}
          preserveCurrentValues={false}
          virtualListProps={projectEditSingleFieldSelectVirtualListProps}
          triggerProps={projectEditSingleFieldSelectTriggerProps}
          dropdownMenuClassName="customization-project-editor-single-field-select-dropdown"
          onOptionsOpen={onOptionsOpen}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("provinces", nextValue)}
        />
      );
    }

    if (field === "majorNames") {
      return (
        <ProjectEditFieldFrame label={label} meta="多选" wide className="is-major-field is-multiselect-field">
            <Select
              aria-label="所学专业"
              mode="multiple"
              allowCreate
              allowClear
              value={localValue.majorNames ?? []}
              options={majorSlashSelectOptions}
              loading={optionsLoading}
              showSearch={projectEditMultipleSelectSearchProps}
              filterOption={majorSlashFilterOption}
              maxTagCount={projectEditMultipleSelectMaxTagCount}
              placeholder="所学专业"
              virtualListProps={projectEditSingleFieldSelectVirtualListProps}
              triggerProps={projectEditSingleFieldSelectTriggerProps}
              dropdownMenuClassName="customization-project-editor-single-field-select-dropdown"
              renderTag={(tagProps) => renderProjectEditSlashSelectTag(tagProps, majorSlashLabelByValue)}
              onVisibleChange={(visible) => {
                if (!visible) {
                  onSettled?.();
                }
              }}
              onChange={(nextValue) =>
                handleLocalChange("majorNames", formatProjectEditSelectValues(nextValue))
              }
            />
        </ProjectEditFieldFrame>
      );
    }

    if (field === "pointNames") {
      return (
        <ProjectEditNameMultiSelectField
          label={label}
          value={localValue.pointNames}
          options={projectPointNameOptions}
          loading={projectPointNamesLoading}
          allowCreate={false}
          filterOption={false}
          virtualListProps={projectEditSingleFieldSelectVirtualListProps}
          triggerProps={projectEditSingleFieldSelectTriggerProps}
          dropdownMenuClassName="customization-project-editor-single-field-select-dropdown"
          onSearch={onPointNamesSearch}
          onOptionsOpen={() => onPointNamesSearch("")}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("pointNames", nextValue)}
        />
      );
    }

    if (field === "stayAreaNames") {
      return (
        <ProjectEditNameMultiSelectField
          label={label}
          value={localValue.stayAreaNames}
          options={editOptions?.stayAreaNames ?? []}
          loading={optionsLoading}
          allowCreate={false}
          virtualListProps={projectEditSingleFieldSelectVirtualListProps}
          triggerProps={projectEditSingleFieldSelectTriggerProps}
          dropdownMenuClassName="customization-project-editor-single-field-select-dropdown"
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("stayAreaNames", nextValue)}
        />
      );
    }

    if (field === "departureStartDate") {
      return (
        <ProjectEditDateField
          label={label}
          value={localValue.departureStartDate}
          placeholder="YYYY-MM-DD"
          meta="日期"
          onChange={(nextValue) => handleLocalChange("departureStartDate", nextValue)}
        />
      );
    }

    if (field === "durationDays") {
      return (
        <ProjectEditNumberField
          label={label}
          value={localValue.durationDays}
          onChange={(nextValue) => handleLocalChange("durationDays", nextValue)}
        />
      );
    }

    if (field === "studentCount") {
      return (
        <ProjectEditNumberField
          label={label}
          value={localValue.studentCount}
          onChange={(nextValue) => handleLocalChange("studentCount", nextValue)}
        />
      );
    }

    if (field === "teacherCount") {
      return (
        <ProjectEditNumberField
          label={label}
          value={localValue.teacherCount}
          onChange={(nextValue) => handleLocalChange("teacherCount", nextValue)}
        />
      );
    }

    if (field === "guideCount") {
      return (
        <ProjectEditNumberField
          label={label}
          value={localValue.guideCount}
          onChange={(nextValue) => handleLocalChange("guideCount", nextValue)}
        />
      );
    }

    if (field === "companyVisitCount") {
      return (
        <ProjectEditNumberField
          label={label}
          value={localValue.companyVisitCount}
          onChange={(nextValue) => handleLocalChange("companyVisitCount", nextValue)}
        />
      );
    }

    if (field === "intangibleHeritageCount") {
      return (
        <ProjectEditNumberField
          label={label}
          value={localValue.intangibleHeritageCount}
          onChange={(nextValue) => handleLocalChange("intangibleHeritageCount", nextValue)}
        />
      );
    }

    if (field === "budgetPerPerson") {
      return (
        <ProjectEditTextInput
          label={label}
          value={localValue.budgetPerPerson}
          onChange={(nextValue) => handleLocalChange("budgetPerPerson", nextValue)}
        />
      );
    }

    if (field === "customerType") {
      return (
        <ProjectEditSelectField
          label={label}
          value={localValue.customerType}
          options={fieldOptions?.customerTypeOptions ?? []}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("customerType", nextValue)}
        />
      );
    }

    if (field === "tripType") {
      return (
        <ProjectEditSelectField
          label={label}
          value={localValue.tripType}
          options={fieldOptions?.tripTypeOptions ?? []}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("tripType", nextValue)}
        />
      );
    }

    if (field === "signType") {
      return (
        <ProjectEditSelectField
          label={label}
          value={localValue.signType}
          options={fieldOptions?.signTypeOptions ?? []}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("signType", nextValue)}
        />
      );
    }

    if (field === "operatingCompany") {
      return (
        <ProjectEditSelectField
          label={label}
          value={localValue.operatingCompany}
          options={fieldOptions?.operatingCompanyOptions ?? []}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("operatingCompany", nextValue)}
        />
      );
    }

    if (field === "dispatcherUserId") {
      return (
        <ProjectEditFieldFrame label={label} meta="单选">
          <DispatcherSelectionField
            value={localValue.dispatcherUserId}
            displayValue={localValue.dispatcherUserName}
            onOpen={() =>
              onOpenDispatcherSelection({
                selectedUserId: localValue.dispatcherUserId,
                selectedDisplayName: localValue.dispatcherUserName,
                onConfirm: (candidate) => {
                  handleLocalChange("dispatcherUserId", candidate?.userId ?? "");
                  handleLocalChange("dispatcherUserName", candidate?.displayName ?? "");
                },
              })
            }
          />
        </ProjectEditFieldFrame>
      );
    }

    if (field === "schoolPickupRequirement") {
      return (
        <ProjectEditSelectField
          label={label}
          value={localValue.schoolPickupRequirement}
          options={buildSchoolPickupRequirementOptions(localValue.schoolPickupRequirement, demandFieldOptions)}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("schoolPickupRequirement", nextValue)}
        />
      );
    }

    if (field === "includeTickets") {
      return (
        <ProjectEditBooleanSelect
          label={label}
          value={localValue.includeTickets}
          trueLabel="包含"
          falseLabel="不包含"
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("includeTickets", nextValue)}
        />
      );
    }

    if (field === "includeMeals") {
      return (
        <ProjectEditBooleanSelect
          label={label}
          value={localValue.includeMeals}
          trueLabel="含餐"
          falseLabel="不含餐"
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("includeMeals", nextValue)}
        />
      );
    }

    if (field === "mealStandards") {
      return (
        <ProjectEditArrayField
          label={label}
          value={localValue.mealStandards}
          onChange={(nextValue) => handleLocalChange("mealStandards", nextValue)}
        />
      );
    }

    if (
      field === "teachingRequirements" ||
      field === "specialRequirements" ||
      field === "invoiceRequirement" ||
      field === "remark" ||
      field === "teacherMealRequirement" ||
      field === "teacherTicketRequirement" ||
      field === "teacherTransportRequirement" ||
      field === "teacherAccommodationRequirement" ||
      field === "roundTripTransportNote" ||
      field === "localTransportNote" ||
      field === "accommodationNote" ||
      field === "guideNote" ||
      field === "ticketNote" ||
      field === "mealNote" ||
      field === "companyVisitNote" ||
      field === "intangibleHeritageNote"
    ) {
      return (
        <ProjectEditTextArea
          label={label}
          value={localValue[field] as string | undefined}
          onChange={(nextValue) => handleLocalChange(field, nextValue as never)}
        />
      );
    }

    return (
      <ProjectEditTextInput
        label={label}
        value={localValue[field] as string | undefined}
        onChange={(nextValue) => handleLocalChange(field, nextValue as never)}
      />
    );
  };

  return (
    <div className="customization-project-editor customization-project-single-editor" aria-label="项目单字段编辑">
      <div className="customization-project-editor-grid is-single-field-grid">{renderField()}</div>
    </div>
  );
}

/** 项目身份与专业编辑区。 */
function ProjectEditPanel({
  value,
  fieldOptions,
  demandFieldOptions,
  editOptions,
  visibleFields = allProjectEditFieldKeys,
  optionsLoading,
  projectPointNameOptions,
  projectPointNamesLoading,
  saving,
  hideActions,
  actionLabel,
  onChange,
  onIdentityChange,
  onDraftChange,
  onOptionsOpen,
  onCollegeSearch,
  onPointNamesSearch,
  onSettled,
  onSave,
  onOpenDispatcherSelection,
}: {
  value: CustomizationProjectUpdatePayload;
  fieldOptions?: CustomizationProjectFieldOptions;
  demandFieldOptions?: CustomizationDemandFieldOptions;
  editOptions?: CustomizationProjectEditOptions | null;
  visibleFields?: ProjectEditFieldKey[];
  optionsLoading?: boolean;
  projectPointNameOptions: string[];
  projectPointNamesLoading?: boolean;
  saving?: boolean;
  hideActions?: boolean;
  actionLabel?: string;
  onChange: <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => void;
  onIdentityChange: (patch: CustomizationProjectUpdatePayload) => void;
  onDraftChange?: (value: CustomizationProjectUpdatePayload) => void;
  onOptionsOpen?: () => void;
  onCollegeSearch?: (keyword?: string) => void;
  onPointNamesSearch: (keyword?: string) => void;
  onSettled?: () => void;
  onSave: () => void;
  onOpenDispatcherSelection: (options: {
    selectedUserId?: string;
    selectedDisplayName?: string;
    onConfirm: (candidate: CustomizationDispatcherSelectionCandidate | null) => void;
  }) => void;
}) {
  const [localValue, setLocalValue] = useState<CustomizationProjectUpdatePayload>(value);

  useEffect(() => {
    setLocalValue(value);
    onDraftChange?.(value);
  }, [onDraftChange, value]);

  const handleLocalChange = <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    nextValue: CustomizationProjectUpdatePayload[K],
  ) => {
    setLocalValue((current) => {
      const nextDraft = applyProjectEditDraftPatch(current, { [key]: nextValue } as ProjectEditPatch);
      onDraftChange?.(nextDraft);
      return nextDraft;
    });
    onChange(key, nextValue);
  };

  const handleLocalIdentityChange = (patch: CustomizationProjectUpdatePayload) => {
    setLocalValue((current) => {
      const nextDraft = applyProjectEditDraftPatch(current, patch);
      onDraftChange?.(nextDraft);
      return nextDraft;
    });
    onIdentityChange(patch);
  };

  const hasEditOptions = Boolean(editOptions);
  const canShow = (field: ProjectEditFieldKey) =>
    visibleFields.includes(field) && !hiddenWorkbenchDemandFieldKeys.has(field);
  const isIdentityOnlyEditor =
    visibleFields.length === projectIdentityEditFieldKeys.length &&
    projectIdentityEditFieldKeys.every((field) => visibleFields.includes(field));
  const isCompactIdentityEditor =
    visibleFields.length > 0 &&
    visibleFields.length < projectIdentityEditFieldKeys.length &&
    visibleFields.every((field) => projectIdentityEditFieldKeys.includes(field));
  const shouldShowCandidateSummary = hasEditOptions && isIdentityOnlyEditor;
  const collegeCandidates = editOptions?.collegeCandidates ?? emptyCollegeCandidates;
  const customerCandidates = editOptions?.customerCandidates ?? emptyCustomerCandidates;
  const contactCandidates = editOptions?.contactCandidates ?? emptyContactCandidates;
  const majorCandidates = editOptions?.majorCandidates ?? emptyMajorCandidates;
  const collegeSelectOptions = useMemo(
    () => buildCollegeSelectOptions(collegeCandidates, localValue.primarySchoolId, localValue.primarySchoolName),
    [collegeCandidates, localValue.primarySchoolId, localValue.primarySchoolName],
  );
  const customerSelectOptions = useMemo(
    () => buildCustomerSelectOptions(customerCandidates, localValue.customerId, localValue.customerName),
    [customerCandidates, localValue.customerId, localValue.customerName],
  );
  const contactSelectOptions = useMemo(
    () => buildContactSelectOptions(contactCandidates, localValue.contactId, localValue.contactName),
    [contactCandidates, localValue.contactId, localValue.contactName],
  );
  const identitySelectResetKey = localValue.primarySchoolId ?? "no-school";
  const majorSelectOptions = useMemo(
    () => buildMajorSelectOptions(majorCandidates, localValue.majorNames),
    [majorCandidates, localValue.majorNames],
  );
  const majorSlashSelectOptions = useMemo(
    () => toSlashLabeledSelectOptions(majorSelectOptions),
    [majorSelectOptions],
  );
  const majorSlashLabelByValue = useMemo(
    () => new Map(majorSelectOptions.map((option) => [option.value, option.label])),
    [majorSelectOptions],
  );
  const majorSlashFilterOption = useMemo(
    () => createSlashLabelFilterOption(majorSelectOptions),
    [majorSelectOptions],
  );
  const schoolPickupRequirementOptions = useMemo(
    () => buildSchoolPickupRequirementOptions(localValue.schoolPickupRequirement, demandFieldOptions),
    [demandFieldOptions, localValue.schoolPickupRequirement],
  );
  const renderSectionTitle = (label: string, fields: ProjectEditFieldKey[]) => {
    const visibleCount = fields.filter(canShow).length;
    if (visibleCount === 0) {
      return null;
    }
    return (
      <div className="customization-project-editor-section-title" aria-label={`编辑分组：${label}`}>
        <strong>{label}</strong>
        <span>{visibleCount} 项</span>
      </div>
    );
  };

  return (
    <div className="customization-project-editor" aria-label="项目基础信息编辑">
      <div
        className={`customization-project-editor-grid${
          isIdentityOnlyEditor
            ? " is-identity-editor"
            : isCompactIdentityEditor
              ? " is-single-field-grid"
              : ""
        }`}
      >
        {renderSectionTitle("项目与客户", ["primarySchoolId", "customerId", "contactId"])}
        {canShow("primarySchoolId") ? (
          <ProjectEditFieldFrame label={projectEditFieldLabels.primarySchoolId} meta="单选">
            <Select
              aria-label="出发高校/组织"
              value={localValue.primarySchoolId ?? ""}
              options={collegeSelectOptions}
              loading={optionsLoading}
              allowClear
              showSearch
              filterOption={() => true}
              placeholder="出发高校/组织"
              virtualListProps={projectEditSelectVirtualListProps}
              triggerProps={projectEditSelectTriggerProps}
              onVisibleChange={(visible) => {
                if (visible) {
                  onCollegeSearch?.("");
                } else {
                  onSettled?.();
                }
              }}
              onSearch={(inputValue) => onCollegeSearch?.(inputValue)}
              onChange={(nextValue) => {
                const selectedValue = formatProjectEditSelectValue(nextValue);
                const candidate = editOptions?.collegeCandidates.find((item) => item.id === selectedValue);
                handleLocalIdentityChange({
                  primarySchoolId: selectedValue || undefined,
                  primarySchoolName: candidate?.name ?? undefined,
                  primarySchoolOrgType: candidate?.orgType ?? undefined,
                  customerId: "",
                  customerName: "",
                  contactId: "",
                  contactName: "",
                });
              }}
            />
          </ProjectEditFieldFrame>
        ) : null}
        {canShow("customerId") ? (
          <ProjectEditFieldFrame
            label={projectEditFieldLabels.customerId}
            wide
            meta={
              isSocialCustomization(localValue.customerType)
                ? "社会类定制可不填"
                : undefined
            }
          >
            <Select
              key={`customer-${identitySelectResetKey}`}
              aria-label="客户"
              value={localValue.customerId || undefined}
              options={customerSelectOptions}
              loading={optionsLoading}
              allowClear
              showSearch
              filterOption={projectEditObjectSelectFilterOption}
              placeholder="客户"
              virtualListProps={projectEditSelectVirtualListProps}
              triggerProps={projectEditSelectTriggerProps}
              onVisibleChange={(visible) => {
                if (visible) {
                  onOptionsOpen?.();
                } else {
                  onSettled?.();
                }
              }}
              onChange={(nextValue) => {
                const selectedValue = formatProjectEditSelectValue(nextValue);
                const candidate = editOptions?.customerCandidates.find((item) => item.id === selectedValue);
                handleLocalIdentityChange({
                  customerId: selectedValue || "",
                  customerName: candidate?.name ?? "",
                });
              }}
            />
          </ProjectEditFieldFrame>
        ) : null}
        {canShow("contactId") ? (
          <ProjectEditFieldFrame label={projectEditFieldLabels.contactId} meta="单选">
            <Select
              key={`contact-${identitySelectResetKey}`}
              aria-label="客户联系人"
              value={localValue.contactId || undefined}
              options={contactSelectOptions}
              loading={optionsLoading}
              allowClear
              showSearch
              filterOption={projectEditObjectSelectFilterOption}
              placeholder="客户联系人"
              virtualListProps={projectEditSelectVirtualListProps}
              triggerProps={projectEditSelectTriggerProps}
              onVisibleChange={(visible) => {
                if (visible) {
                  onOptionsOpen?.();
                } else {
                  onSettled?.();
                }
              }}
              onChange={(nextValue) => {
                const selectedValue = formatProjectEditSelectValue(nextValue);
                const candidate = editOptions?.contactCandidates.find((item) => item.id === selectedValue);
                handleLocalIdentityChange({
                  contactId: selectedValue || "",
                  contactName: candidate?.name ?? "",
                });
              }}
            />
          </ProjectEditFieldFrame>
        ) : null}
        {renderSectionTitle("目的地与专业", ["provinces", "majorNames", "pointNames", "stayAreaNames"])}
        {canShow("provinces") ? (
        <ProjectEditNameMultiSelectField
          label={projectEditFieldLabels.provinces}
          value={localValue.provinces}
          options={editOptions?.provinceNames ?? []}
          loading={optionsLoading}
          preserveCurrentValues={false}
          onOptionsOpen={onOptionsOpen}
          onSettled={onSettled}
          onChange={(nextValue) => handleLocalChange("provinces", nextValue)}
        />
      ) : null}
        {canShow("majorNames") ? (
          <ProjectEditFieldFrame
            label={projectEditFieldLabels.majorNames}
            meta="多选"
            wide
            className="is-major-field is-multiselect-field"
          >
            <Select
              aria-label="所学专业"
              mode="multiple"
              allowCreate
              allowClear
              value={localValue.majorNames ?? []}
              options={majorSlashSelectOptions}
              loading={optionsLoading}
              showSearch={projectEditMultipleSelectSearchProps}
              filterOption={majorSlashFilterOption}
              maxTagCount={projectEditMultipleSelectMaxTagCount}
              placeholder="所学专业"
              virtualListProps={projectEditSelectVirtualListProps}
              triggerProps={projectEditSelectTriggerProps}
              renderTag={(tagProps) => renderProjectEditSlashSelectTag(tagProps, majorSlashLabelByValue)}
              onVisibleChange={(visible) => {
                if (!visible) {
                  onSettled?.();
                }
              }}
              onChange={(nextValue) =>
                handleLocalChange("majorNames", formatProjectEditSelectValues(nextValue))
              }
            />
          </ProjectEditFieldFrame>
        ) : null}
        {canShow("pointNames") ? (
          <ProjectEditNameMultiSelectField
            label={projectEditFieldLabels.pointNames}
            value={localValue.pointNames}
            options={projectPointNameOptions}
            loading={projectPointNamesLoading}
            allowCreate={false}
            filterOption={false}
            onSearch={onPointNamesSearch}
            onOptionsOpen={() => onPointNamesSearch("")}
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("pointNames", nextValue)}
          />
        ) : null}
        {canShow("stayAreaNames") ? (
          <ProjectEditNameMultiSelectField
            label={projectEditFieldLabels.stayAreaNames}
            value={localValue.stayAreaNames}
            options={editOptions?.stayAreaNames ?? []}
            loading={optionsLoading}
            allowCreate={false}
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("stayAreaNames", nextValue)}
          />
        ) : null}
        {renderSectionTitle("出行基础", [
          "departureStartDate",
          "durationDays",
          "studentCount",
          "teacherCount",
          "budgetPerPerson",
        ])}
        {canShow("departureStartDate") ? (
          <ProjectEditDateField
            label={projectEditFieldLabels.departureStartDate}
            value={localValue.departureStartDate}
            placeholder="YYYY-MM-DD"
            meta="日期"
            onChange={(nextValue) => handleLocalChange("departureStartDate", nextValue)}
          />
        ) : null}
        {canShow("durationDays") ? (
          <ProjectEditNumberField
            label={projectEditFieldLabels.durationDays}
            value={localValue.durationDays}
            onChange={(nextValue) => handleLocalChange("durationDays", nextValue)}
          />
        ) : null}
        {canShow("studentCount") ? (
          <ProjectEditNumberField
            label={projectEditFieldLabels.studentCount}
            value={localValue.studentCount}
            onChange={(nextValue) => handleLocalChange("studentCount", nextValue)}
          />
        ) : null}
        {canShow("teacherCount") ? (
          <ProjectEditNumberField
            label={projectEditFieldLabels.teacherCount}
            value={localValue.teacherCount}
            onChange={(nextValue) => handleLocalChange("teacherCount", nextValue)}
          />
        ) : null}
        {canShow("budgetPerPerson") ? (
          <ProjectEditTextInput
            label={projectEditFieldLabels.budgetPerPerson}
            value={localValue.budgetPerPerson}
            onChange={(nextValue) => handleLocalChange("budgetPerPerson", nextValue)}
          />
        ) : null}
        {renderSectionTitle("业务与计调", [
          "customerType",
          "tripType",
          "signType",
          "schoolPickupRequirement",
          "operatingCompany",
          "dispatcherUserId",
        ])}
        {canShow("customerType") ? (
          <ProjectEditSelectField
            label={projectEditFieldLabels.customerType}
            value={localValue.customerType}
            options={fieldOptions?.customerTypeOptions ?? []}
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("customerType", nextValue)}
          />
        ) : null}
        {canShow("tripType") ? (
          <ProjectEditSelectField
            label={projectEditFieldLabels.tripType}
            value={localValue.tripType}
            options={fieldOptions?.tripTypeOptions ?? []}
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("tripType", nextValue)}
          />
        ) : null}
        {canShow("signType") ? (
          <ProjectEditSelectField
            label={projectEditFieldLabels.signType}
            value={localValue.signType}
            options={fieldOptions?.signTypeOptions ?? []}
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("signType", nextValue)}
          />
        ) : null}
        {canShow("schoolPickupRequirement") ? (
          <ProjectEditSelectField
            label={projectEditFieldLabels.schoolPickupRequirement}
            value={localValue.schoolPickupRequirement}
            options={schoolPickupRequirementOptions}
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("schoolPickupRequirement", nextValue)}
          />
        ) : null}
        {canShow("operatingCompany") ? (
          <ProjectEditSelectField
            label={projectEditFieldLabels.operatingCompany}
            value={localValue.operatingCompany}
            options={fieldOptions?.operatingCompanyOptions ?? []}
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("operatingCompany", nextValue)}
          />
        ) : null}
        {canShow("dispatcherUserId") ? (
          <ProjectEditFieldFrame label={projectEditFieldLabels.dispatcherUserId} meta="单选">
            <DispatcherSelectionField
              value={localValue.dispatcherUserId}
              displayValue={localValue.dispatcherUserName}
              onOpen={() =>
                onOpenDispatcherSelection({
                  selectedUserId: localValue.dispatcherUserId,
                  selectedDisplayName: localValue.dispatcherUserName,
                  onConfirm: (candidate) => {
                    handleLocalIdentityChange({
                      dispatcherUserId: candidate?.userId ?? "",
                      dispatcherUserName: candidate?.displayName ?? "",
                    });
                  },
                })
              }
            />
          </ProjectEditFieldFrame>
        ) : null}
        {renderSectionTitle("需求说明", [
          "teachingRequirements",
          "specialRequirements",
          "invoiceRequirement",
          "remark",
        ])}
        {canShow("teachingRequirements") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.teachingRequirements}
            value={localValue.teachingRequirements}
            onChange={(nextValue) => handleLocalChange("teachingRequirements", nextValue)}
          />
        ) : null}
        {canShow("specialRequirements") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.specialRequirements}
            value={localValue.specialRequirements}
            onChange={(nextValue) => handleLocalChange("specialRequirements", nextValue)}
          />
        ) : null}
        {canShow("invoiceRequirement") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.invoiceRequirement}
            value={localValue.invoiceRequirement}
            onChange={(nextValue) => handleLocalChange("invoiceRequirement", nextValue)}
          />
        ) : null}
        {canShow("remark") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.remark}
            value={localValue.remark}
            onChange={(nextValue) => handleLocalChange("remark", nextValue)}
          />
        ) : null}
        {renderSectionTitle("老师需求", [
          "teacherMealRequirement",
          "teacherTicketRequirement",
          "teacherTransportRequirement",
          "teacherAccommodationRequirement",
        ])}
        {canShow("teacherMealRequirement") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.teacherMealRequirement}
            value={localValue.teacherMealRequirement}
            onChange={(nextValue) => handleLocalChange("teacherMealRequirement", nextValue)}
          />
        ) : null}
        {canShow("teacherTicketRequirement") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.teacherTicketRequirement}
            value={localValue.teacherTicketRequirement}
            onChange={(nextValue) => handleLocalChange("teacherTicketRequirement", nextValue)}
          />
        ) : null}
        {canShow("teacherTransportRequirement") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.teacherTransportRequirement}
            value={localValue.teacherTransportRequirement}
            onChange={(nextValue) => handleLocalChange("teacherTransportRequirement", nextValue)}
          />
        ) : null}
        {canShow("teacherAccommodationRequirement") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.teacherAccommodationRequirement}
            value={localValue.teacherAccommodationRequirement}
            onChange={(nextValue) => handleLocalChange("teacherAccommodationRequirement", nextValue)}
          />
        ) : null}
        {renderSectionTitle("餐住行与资源", [
          "roundTripTransportNote",
          "localTransportNote",
          "accommodationNote",
          "guideCount",
          "guideNote",
          "includeTickets",
          "ticketNote",
          "includeMeals",
          "mealStandards",
          "mealNote",
        ])}
        {canShow("roundTripTransportNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.roundTripTransportNote}
            value={localValue.roundTripTransportNote}
            onChange={(nextValue) => handleLocalChange("roundTripTransportNote", nextValue)}
          />
        ) : null}
        {canShow("localTransportNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.localTransportNote}
            value={localValue.localTransportNote}
            onChange={(nextValue) => handleLocalChange("localTransportNote", nextValue)}
          />
        ) : null}
        {canShow("accommodationNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.accommodationNote}
            value={localValue.accommodationNote}
            onChange={(nextValue) => handleLocalChange("accommodationNote", nextValue)}
          />
        ) : null}
        {canShow("guideCount") ? (
          <ProjectEditNumberField
            label={projectEditFieldLabels.guideCount}
            value={localValue.guideCount}
            onChange={(nextValue) => handleLocalChange("guideCount", nextValue)}
          />
        ) : null}
        {canShow("guideNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.guideNote}
            value={localValue.guideNote}
            onChange={(nextValue) => handleLocalChange("guideNote", nextValue)}
          />
        ) : null}
        {canShow("includeTickets") ? (
          <ProjectEditBooleanSelect
            label={projectEditFieldLabels.includeTickets}
            value={localValue.includeTickets}
            trueLabel="包含"
            falseLabel="不包含"
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("includeTickets", nextValue)}
          />
        ) : null}
        {canShow("ticketNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.ticketNote}
            value={localValue.ticketNote}
            onChange={(nextValue) => handleLocalChange("ticketNote", nextValue)}
          />
        ) : null}
        {canShow("includeMeals") ? (
          <ProjectEditBooleanSelect
            label={projectEditFieldLabels.includeMeals}
            value={localValue.includeMeals}
            trueLabel="含餐"
            falseLabel="不含餐"
            onSettled={onSettled}
            onChange={(nextValue) => handleLocalChange("includeMeals", nextValue)}
          />
        ) : null}
        {canShow("mealStandards") ? (
          <ProjectEditArrayField
            label={projectEditFieldLabels.mealStandards}
            value={localValue.mealStandards}
            onChange={(nextValue) => handleLocalChange("mealStandards", nextValue)}
          />
        ) : null}
        {canShow("mealNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.mealNote}
            value={localValue.mealNote}
            onChange={(nextValue) => handleLocalChange("mealNote", nextValue)}
          />
        ) : null}
        {renderSectionTitle("企业与非遗", [
          "companyVisitCount",
          "companyVisitNote",
          "intangibleHeritageCount",
          "intangibleHeritageNote",
        ])}
        {canShow("companyVisitCount") ? (
          <ProjectEditNumberField
            label={projectEditFieldLabels.companyVisitCount}
            value={localValue.companyVisitCount}
            onChange={(nextValue) => handleLocalChange("companyVisitCount", nextValue)}
          />
        ) : null}
        {canShow("companyVisitNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.companyVisitNote}
            value={localValue.companyVisitNote}
            onChange={(nextValue) => handleLocalChange("companyVisitNote", nextValue)}
          />
        ) : null}
        {canShow("intangibleHeritageCount") ? (
          <ProjectEditNumberField
            label={projectEditFieldLabels.intangibleHeritageCount}
            value={localValue.intangibleHeritageCount}
            onChange={(nextValue) => handleLocalChange("intangibleHeritageCount", nextValue)}
          />
        ) : null}
        {canShow("intangibleHeritageNote") ? (
          <ProjectEditTextArea
            label={projectEditFieldLabels.intangibleHeritageNote}
            value={localValue.intangibleHeritageNote}
            onChange={(nextValue) => handleLocalChange("intangibleHeritageNote", nextValue)}
          />
        ) : null}
      </div>
      {hideActions ? null : (
        <div className="customization-project-editor-actions">
          <Button type="primary" loading={saving} disabled={saving} onClick={onSave}>
            {actionLabel ?? "保存基础信息"}
          </Button>
        </div>
      )}
      {shouldShowCandidateSummary ? (
        <ProjectEditCandidateSummary editOptions={editOptions} />
      ) : null}
    </div>
  );
}

/** 普通文本字段编辑器。 */
const ProjectEditTextInput = memo(function ProjectEditTextInput({
  label,
  value,
  placeholder,
  meta = "文本",
  onChange,
}: {
  label: string;
  value?: string;
  placeholder?: string;
  meta?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="customization-project-editor-field is-text-field">
      <ProjectEditFieldLabel meta={meta}>{label}</ProjectEditFieldLabel>
      <Input
        aria-label={label}
        value={value ?? ""}
        placeholder={placeholder ?? label}
        onChange={(nextValue) => onChange(nextValue)}
      />
    </label>
  );
}, areProjectEditTextInputPropsEqual);

/** 日期字段编辑器，使用 Arco DatePicker 精确到日。 */
const ProjectEditDateField = memo(function ProjectEditDateField({
  label,
  value,
  placeholder = "YYYY-MM-DD",
  meta = "日期",
  onChange,
}: {
  label: string;
  value?: string;
  placeholder?: string;
  meta?: string;
  onChange: (value: string) => void;
}) {
  return (
    <ProjectEditFieldFrame label={label} meta={meta} className="is-date-field">
      <DatePicker
        allowClear
        className="customization-project-editor-date-picker"
        format="YYYY-MM-DD"
        inputProps={{
          "aria-label": label,
          onBlur: (event) => {
            const nextValue = event.currentTarget.value.trim();
            if (isValidProjectEditDateValue(nextValue)) {
              onChange(nextValue);
            }
          },
        }}
        placeholder={placeholder}
        value={value || undefined}
        onChange={(nextValue) => onChange(nextValue ? String(nextValue) : "")}
      />
    </ProjectEditFieldFrame>
  );
}, areProjectEditDateFieldPropsEqual);

/** 校验键盘录入日期，避免格式正确但日期不存在的文本进入草稿。 */
function isValidProjectEditDateValue(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** 多行文本字段编辑器。 */
const ProjectEditTextArea = memo(function ProjectEditTextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="customization-project-editor-field is-wide-field is-textarea-field">
      <ProjectEditFieldLabel meta="长文本">{label}</ProjectEditFieldLabel>
      <TextArea
        aria-label={label}
        value={value ?? ""}
        autoSize={{ minRows: 2, maxRows: 5 }}
        onChange={(nextValue) => onChange(nextValue)}
      />
    </label>
  );
}, areProjectEditTextAreaPropsEqual);

/** 数字字段编辑器。 */
const ProjectEditNumberField = memo(function ProjectEditNumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: number;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <label className="customization-project-editor-field is-number-field">
      <ProjectEditFieldLabel meta="数字">{label}</ProjectEditFieldLabel>
      <Input
        aria-label={label}
        type="number"
        min={0}
        value={value === undefined ? "" : String(value)}
        onChange={(nextValue) => onChange(parseOptionalNumber(nextValue))}
      />
    </label>
  );
}, areProjectEditNumberFieldPropsEqual);

/** 数组字段编辑器，使用顿号、逗号、空格或换行切分。 */
const ProjectEditArrayField = memo(function ProjectEditArrayField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <label className="customization-project-editor-field is-wide-field is-textarea-field is-array-field">
      <ProjectEditFieldLabel meta="文本区域">{label}</ProjectEditFieldLabel>
      <TextArea
        aria-label={label}
        value={formatEditableNameList(value)}
        autoSize={{ minRows: 2, maxRows: 4 }}
        onChange={(nextValue) => onChange(splitEditableNameList(nextValue))}
      />
    </label>
  );
}, areProjectEditArrayFieldPropsEqual);

/** 编辑弹窗字段外壳；复杂控件不使用原生 label 包裹，避免代理点击干扰下拉交互。 */
function ProjectEditFieldFrame({
  label,
  meta,
  wide,
  className,
  children,
}: {
  label: string;
  meta?: string;
  wide?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const fieldClassName = [
    "customization-project-editor-field",
    wide ? "is-wide-field" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={fieldClassName}>
      <ProjectEditFieldLabel meta={meta}>{label}</ProjectEditFieldLabel>
      {children}
    </div>
  );
}

/** 编辑弹窗字段标签。 */
function ProjectEditFieldLabel({ children, meta }: { children: ReactNode; meta?: string }) {
  return (
    <Text type="secondary" className="customization-project-editor-label">
      <span>{children}</span>
      {meta ? <span className="customization-project-editor-field-type">{meta}</span> : null}
    </Text>
  );
}

/** 数据库候选名称多选下拉。 */
const ProjectEditNameMultiSelectField = memo(function ProjectEditNameMultiSelectField({
  label,
  value,
  options,
  loading,
  allowCreate,
  preserveCurrentValues = true,
  filterOption,
  virtualListProps,
  triggerProps,
  dropdownMenuClassName,
  onSearch,
  onOptionsOpen,
  onSettled,
  onChange,
}: {
  label: string;
  value?: string[];
  options: string[];
  loading?: boolean;
  allowCreate?: boolean;
  preserveCurrentValues?: boolean;
  filterOption?: false | typeof projectEditObjectSelectFilterOption;
  virtualListProps?: ProjectEditSelectVirtualListProps;
  triggerProps?: ProjectEditSelectTriggerProps;
  dropdownMenuClassName?: string;
  onSearch?: (keyword: string) => void;
  onOptionsOpen?: () => void;
  onSettled?: () => void;
  onChange: (value: string[]) => void;
}) {
  const [searchKeyword, setSearchKeyword] = useState("");
  const normalizedValue = useMemo(() => splitEditableNameList(value), [value]);
  const baseSelectOptions = useMemo(
    () => options.map((option) => ({
      value: option,
      label: option,
    })),
    [options],
  );
  const optionValueSet = useMemo(() => new Set(options), [options]);
  const selectOptions = useMemo(() => {
    const withSelectedOptions = (() => {
      if (!preserveCurrentValues) {
        return baseSelectOptions;
      }
      const missingSelectedOptions = normalizedValue.filter((item) => !optionValueSet.has(item));
      if (missingSelectedOptions.length === 0) {
        return baseSelectOptions;
      }
      return [
        ...baseSelectOptions,
        ...missingSelectedOptions.map((option) => ({
          value: option,
          label: option,
        })),
      ];
    })();
    return filterProjectEditNameOptionsForSearch(
      withSelectedOptions,
      normalizedValue,
      searchKeyword,
    );
  }, [
    baseSelectOptions,
    normalizedValue,
    optionValueSet,
    preserveCurrentValues,
    searchKeyword,
  ]);
  const slashSelectOptions = useMemo(
    () => toSlashLabeledSelectOptions(selectOptions),
    [selectOptions],
  );
  const slashLabelByValue = useMemo(
    () => new Map(selectOptions.map((option) => [option.value, option.label])),
    [selectOptions],
  );
  const slashFilterOption = useMemo(
    () => createSlashLabelFilterOption(selectOptions),
    [selectOptions],
  );
  const resolvedFilterOption =
    filterOption === false ? false : (filterOption ?? slashFilterOption);
  return (
    <ProjectEditFieldFrame label={label} meta="多选" wide className="is-multiselect-field">
      <Select
        aria-label={label}
        mode="multiple"
        allowCreate={allowCreate ?? preserveCurrentValues}
        allowClear
        value={normalizedValue}
        options={slashSelectOptions}
        loading={loading}
        showSearch={projectEditMultipleSelectSearchProps}
        filterOption={resolvedFilterOption}
        maxTagCount={projectEditMultipleSelectMaxTagCount}
        placeholder={label}
        virtualListProps={virtualListProps ?? projectEditSelectVirtualListProps}
        triggerProps={triggerProps ?? projectEditSelectTriggerProps}
        dropdownMenuClassName={dropdownMenuClassName}
        renderTag={(tagProps) => renderProjectEditSlashSelectTag(tagProps, slashLabelByValue)}
        onSearch={(inputValue) => {
          setSearchKeyword(inputValue);
          onSearch?.(inputValue);
        }}
        onVisibleChange={(visible) => {
          if (visible) {
            onOptionsOpen?.();
          } else {
            setSearchKeyword("");
            onSettled?.();
          }
        }}
        onChange={(nextValue) => onChange(formatProjectEditSelectValues(nextValue))}
      />
    </ProjectEditFieldFrame>
  );
}, areProjectEditNameMultiSelectFieldPropsEqual);

/** 枚举文本字段编辑器。 */
const ProjectEditSelectField = memo(function ProjectEditSelectField({
  label,
  value,
  options,
  onSettled,
  onChange,
}: {
  label: string;
  value?: string;
  options: string[];
  onSettled?: () => void;
  onChange: (value: string | undefined) => void;
}) {
  const selectOptions = useMemo(
    () => options.map((option) => ({ value: option, label: option })),
    [options],
  );
  return (
    <ProjectEditFieldFrame label={label} meta="单选">
      <Select
        aria-label={label}
        value={value}
        options={selectOptions}
        allowClear
        showSearch
        placeholder={label}
        virtualListProps={projectEditSelectVirtualListProps}
        triggerProps={projectEditSelectTriggerProps}
        onVisibleChange={(visible) => {
          if (!visible) {
            onSettled?.();
          }
        }}
        onChange={(nextValue) => onChange(formatProjectEditSelectValue(nextValue) || undefined)}
      />
    </ProjectEditFieldFrame>
  );
}, areProjectEditSelectFieldPropsEqual);

/** 布尔字段编辑器。 */
const ProjectEditBooleanSelect = memo(function ProjectEditBooleanSelect({
  label,
  value,
  trueLabel,
  falseLabel,
  onSettled,
  onChange,
}: {
  label: string;
  value?: boolean;
  trueLabel: string;
  falseLabel: string;
  onSettled?: () => void;
  onChange: (value: boolean | undefined) => void;
}) {
  const selectOptions = useMemo(
    () => [
      { value: "true", label: trueLabel },
      { value: "false", label: falseLabel },
    ],
    [falseLabel, trueLabel],
  );
  return (
    <ProjectEditFieldFrame label={label} meta="二选一">
      <Select
        aria-label={label}
        value={value === undefined ? undefined : String(value)}
        options={selectOptions}
        allowClear
        placeholder={label}
        virtualListProps={projectEditSelectVirtualListProps}
        triggerProps={projectEditSelectTriggerProps}
        onVisibleChange={(visible) => {
          if (!visible) {
            onSettled?.();
          }
        }}
        onChange={(nextValue) => {
          const nextText = formatProjectEditSelectValue(nextValue);
          if (nextText === "true") {
            onChange(true);
            return;
          }
          if (nextText === "false") {
            onChange(false);
            return;
          }
          onChange(undefined);
        }}
      />
    </ProjectEditFieldFrame>
  );
}, areProjectEditBooleanSelectPropsEqual);

/** 比较普通文本字段渲染所需属性。 */
function areProjectEditTextInputPropsEqual(
  previous: { label: string; value?: string; placeholder?: string; meta?: string },
  next: { label: string; value?: string; placeholder?: string; meta?: string },
): boolean {
  return (
    previous.label === next.label &&
    previous.value === next.value &&
    previous.placeholder === next.placeholder &&
    previous.meta === next.meta
  );
}

/** 比较日期字段渲染所需属性。 */
function areProjectEditDateFieldPropsEqual(
  previous: { label: string; value?: string; placeholder?: string; meta?: string },
  next: { label: string; value?: string; placeholder?: string; meta?: string },
): boolean {
  return areProjectEditTextInputPropsEqual(previous, next);
}

/** 比较多行文本字段渲染所需属性。 */
function areProjectEditTextAreaPropsEqual(
  previous: { label: string; value?: string },
  next: { label: string; value?: string },
): boolean {
  return previous.label === next.label && previous.value === next.value;
}

/** 比较数字字段渲染所需属性。 */
function areProjectEditNumberFieldPropsEqual(
  previous: { label: string; value?: number },
  next: { label: string; value?: number },
): boolean {
  return previous.label === next.label && previous.value === next.value;
}

/** 比较数组字段渲染所需属性。 */
function areProjectEditArrayFieldPropsEqual(
  previous: { label: string; value?: string[] },
  next: { label: string; value?: string[] },
): boolean {
  return previous.label === next.label && areStringArraysEqual(previous.value ?? [], next.value ?? []);
}

/** 比较名称多选字段渲染所需属性。 */
function areProjectEditNameMultiSelectFieldPropsEqual(
  previous: {
    label: string;
    value?: string[];
    options: string[];
    loading?: boolean;
    allowCreate?: boolean;
    filterOption?: false | typeof projectEditObjectSelectFilterOption;
    virtualListProps?: ProjectEditSelectVirtualListProps;
    triggerProps?: ProjectEditSelectTriggerProps;
    dropdownMenuClassName?: string;
    onSearch?: (keyword: string) => void;
    onOptionsOpen?: () => void;
  },
  next: {
    label: string;
    value?: string[];
    options: string[];
    loading?: boolean;
    allowCreate?: boolean;
    filterOption?: false | typeof projectEditObjectSelectFilterOption;
    virtualListProps?: ProjectEditSelectVirtualListProps;
    triggerProps?: ProjectEditSelectTriggerProps;
    dropdownMenuClassName?: string;
    onSearch?: (keyword: string) => void;
    onOptionsOpen?: () => void;
  },
): boolean {
  return (
    previous.label === next.label &&
    previous.options === next.options &&
    previous.loading === next.loading &&
    previous.allowCreate === next.allowCreate &&
    previous.filterOption === next.filterOption &&
    previous.virtualListProps === next.virtualListProps &&
    previous.triggerProps === next.triggerProps &&
    previous.dropdownMenuClassName === next.dropdownMenuClassName &&
    previous.onSearch === next.onSearch &&
    previous.onOptionsOpen === next.onOptionsOpen &&
    areStringArraysEqual(previous.value ?? [], next.value ?? [])
  );
}

/** 比较枚举下拉字段渲染所需属性。 */
function areProjectEditSelectFieldPropsEqual(
  previous: { label: string; value?: string; options: string[] },
  next: { label: string; value?: string; options: string[] },
): boolean {
  return previous.label === next.label && previous.value === next.value && previous.options === next.options;
}

/** 比较布尔下拉字段渲染所需属性。 */
function areProjectEditBooleanSelectPropsEqual(
  previous: { label: string; value?: boolean; trueLabel: string; falseLabel: string },
  next: { label: string; value?: boolean; trueLabel: string; falseLabel: string },
): boolean {
  return (
    previous.label === next.label &&
    previous.value === next.value &&
    previous.trueLabel === next.trueLabel &&
    previous.falseLabel === next.falseLabel
  );
}

/** 展示客户候选的辅助辨识信息。 */
function ProjectEditCandidateSummary({
  editOptions,
}: {
  editOptions?: CustomizationProjectEditOptions | null;
}) {
  const items = [
    ...(editOptions?.customerCandidates ?? []).map((item) => item.anchorOrgUnitName),
  ].filter((item): item is string => Boolean(item));

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="customization-project-editor-candidates">
      {Array.from(new Set(items)).map((item) => (
        <span key={item}>{item}</span>
      ))}
    </div>
  );
}

type NativeSelectOption = {
  value: string;
  label: string;
};

type SlashLabeledSelectOption = {
  value: string;
  label: ReactNode;
};

/** 拆分「名称 / 分类」展示文案，无分隔符时整段视为名称。 */
function splitSlashLabel(raw: string): { name: string; category?: string } {
  const separatorIndex = raw.indexOf(" / ");
  if (separatorIndex <= 0) {
    return { name: raw.trim() };
  }
  const name = raw.slice(0, separatorIndex).trim();
  const category = raw.slice(separatorIndex + 3).trim();
  if (!name) {
    return { name: raw.trim() };
  }
  return category ? { name, category } : { name };
}

/** 渲染带分色层级的「名称 / 分类」文案。 */
function renderSlashLabeledText(raw: string, variant: "option" | "tag" = "option"): ReactNode {
  const text = raw.trim();
  if (!text) {
    return null;
  }
  const { name, category } = splitSlashLabel(text);
  if (!category) {
    return (
      <span className={`customization-project-editor-slash-label is-${variant} is-plain`} title={text}>
        {name}
      </span>
    );
  }
  return (
    <span className={`customization-project-editor-slash-label is-${variant}`} title={text}>
      <strong className="customization-project-editor-slash-name">{name}</strong>
      <span className="customization-project-editor-slash-sep" aria-hidden>
        /
      </span>
      <span className="customization-project-editor-slash-category">{category}</span>
    </span>
  );
}

/** 将字符串候选转为可分色展示的 Select options，搜索仍按原始文案匹配。 */
function toSlashLabeledSelectOptions(options: NativeSelectOption[]): SlashLabeledSelectOption[] {
  return options.map((option) => ({
    value: option.value,
    label: renderSlashLabeledText(option.label),
  }));
}

/** 按候选原始 label 文本过滤，兼容 ReactNode 展示 label。 */
function createSlashLabelFilterOption(options: NativeSelectOption[]) {
  const searchTextByValue = new Map(
    options.map((option) => [option.value, option.label.trim().toLowerCase()]),
  );
  return (
    inputValue: string,
    option: { props?: { value?: unknown } },
  ) => {
    const keyword = inputValue.trim().toLowerCase();
    if (!keyword) {
      return true;
    }
    const value = String(option.props?.value ?? "");
    const searchText = searchTextByValue.get(value) ?? value.toLowerCase();
    return searchText.includes(keyword) || value.toLowerCase().includes(keyword);
  };
}

/** 多选已选 tag：名称与分类分色，避免整段灰字。 */
function renderProjectEditSlashSelectTag(
  tagProps: {
    label?: ReactNode;
    value?: unknown;
    closable?: boolean;
    onClose?: (event: unknown) => void;
  },
  labelTextByValue?: Map<string, string>,
) {
  const value = String(tagProps.value ?? "");
  const rawLabel =
    labelTextByValue?.get(value) ??
    (typeof tagProps.label === "string" ? tagProps.label : value);
  return (
    <Tag
      className="customization-project-editor-select-tag"
      closable={tagProps.closable}
      onClose={tagProps.onClose}
      title={rawLabel}
    >
      {renderSlashLabeledText(rawLabel, "tag")}
    </Tag>
  );
}

/** 统一读取项目编辑下拉的单值结果。 */
function formatProjectEditSelectValue(value: unknown): string {
  return Array.isArray(value) ? formatProjectEditSelectValue(value[0]) : String(value ?? "").trim();
}

/** 统一读取项目编辑下拉的多值结果。 */
function formatProjectEditSelectValues(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((item) => formatProjectEditSelectValue(item))
    .filter((item, index, all) => item && all.indexOf(item) === index);
}

/** 比较字符串数组内容是否一致。 */
function areStringArraysEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

/** 从项目快照和当前需求结构组装项目编辑表单初始值，项目快照优先。 */
function buildProjectEditPayload(
  project: CustomizationProjectSnapshot | null,
  demand?: CustomizationDemandDraft | null,
  fieldOptions?: CustomizationProjectFieldOptions,
): CustomizationProjectUpdatePayload {
  const demandProject = demand?.project;
  const demandFields = buildProjectEditDemandFallbacks(demand, project, fieldOptions);
  return {
    primarySchoolId: project?.primarySchoolId ?? demandProject?.primarySchoolId,
    primarySchoolName: project?.primarySchoolNameSnapshot ?? demandProject?.primarySchoolNameSnapshot,
    primarySchoolOrgType: project?.primarySchoolOrgType ?? demandProject?.primarySchoolOrgType,
    customerId: project?.customerId ?? demandProject?.customerId,
    customerName: project?.customerName ?? demandProject?.customerName,
    contactId: project?.contactId ?? demandProject?.contactId,
    contactName: project?.contactName ?? demandProject?.contactName,
    customerType: project?.customerType ?? demandProject?.customerType ?? demandFields.customerType,
    tripType: project?.tripType ?? demandProject?.tripType ?? demandFields.tripType,
    signType: project?.signType ?? demandProject?.signType ?? demandFields.signType,
    dealProgress: project?.dealProgress ?? demandProject?.dealProgress ?? demandFields.dealProgress,
    dispatcherUserId: project?.dispatcherUserId ?? demandProject?.dispatcherUserId ?? demandFields.dispatcherUserId,
    dispatcherUserName:
      project?.dispatcherUserName ?? demandProject?.dispatcherUserName ?? demandFields.dispatcherUserName,
    operatingCompany:
      project?.operatingCompany ?? demandProject?.operatingCompany ?? demandFields.operatingCompany,
    provinces: buildDemandRelationEditNames(demand, project, "provinces", demand?.relations?.provinces),
    majorNames: buildDemandRelationEditNames(demand, project, "majorNames", demand?.relations?.majorNames),
    pointNames: buildDemandRelationEditNames(demand, project, "pointNames", demand?.relations?.pointNames),
    stayAreaNames: buildDemandRelationEditNames(demand, project, "stayAreaNames", demand?.relations?.stayAreaNames),
    destinationSummary: demand?.summary?.destinationSummary ?? demand?.destinationSummary,
    departureStartDate: demand?.summary?.departureStartDate ?? demand?.departureStartDate,
    durationDays: demand?.summary?.durationDays ?? demand?.durationDays,
    studentCount: demand?.summary?.studentCount ?? demand?.studentCount,
    teacherCount: demand?.summary?.teacherCount ?? demand?.teacherCount,
    totalCount: demand?.summary?.totalCount ?? demand?.totalCount,
    budgetPerPerson: demand?.summary?.budgetPerPerson ?? demand?.budgetPerPerson,
    teachingRequirements: demand?.customerRequirements?.teachingRequirements ?? demand?.teachingRequirements,
    specialRequirements: demand?.customerRequirements?.specialRequirements ?? demand?.specialRequirements,
    schoolPickupRequirement:
      demand?.customerRequirements?.schoolPickupRequirement ?? demand?.schoolPickupRequirement,
    invoiceRequirement: demand?.customerRequirements?.invoiceRequirement ?? demand?.invoiceRequirement,
    remark: demand?.customerRequirements?.remark ?? demand?.remark,
    teacherMealRequirement:
      demand?.teacherRequirements?.teacherMealRequirement ?? demand?.teacherMealRequirement,
    teacherTicketRequirement:
      demand?.teacherRequirements?.teacherTicketRequirement ?? demand?.teacherTicketRequirement,
    teacherTransportRequirement:
      demand?.teacherRequirements?.teacherTransportRequirement ?? demand?.teacherTransportRequirement,
    teacherAccommodationRequirement:
      demand?.teacherRequirements?.teacherAccommodationRequirement ?? demand?.teacherAccommodationRequirement,
    roundTripTransportNote: demand?.serviceStandard?.roundTripTransportNote,
    localTransportNote: demand?.serviceStandard?.localTransportNote,
    accommodationNote: demand?.serviceStandard?.accommodationNote,
    guideCount: demand?.serviceStandard?.guideCount,
    guideNote: demand?.serviceStandard?.guideNote,
    includeTickets: demand?.serviceStandard?.includeTickets,
    ticketNote: demand?.serviceStandard?.ticketNote,
    includeMeals: demand?.serviceStandard?.includeMeals,
    mealStandards: splitEditableNameList(demand?.serviceStandard?.mealStandards),
    mealNote: demand?.serviceStandard?.mealNote,
    companyVisitCount: demand?.serviceStandard?.companyVisitCount,
    companyVisitNote: demand?.serviceStandard?.companyVisitNote,
    intangibleHeritageCount: demand?.serviceStandard?.intangibleHeritageCount,
    intangibleHeritageNote: demand?.serviceStandard?.intangibleHeritageNote,
  };
}

/** 为单字段编辑构造轻量草稿，避免点击字段时重置整表表单。 */
function buildSingleProjectEditDraft(
  field: ProjectEditFieldKey,
  project: CustomizationProjectSnapshot | null,
  demand?: CustomizationDemandDraft | null,
  fieldOptions?: CustomizationProjectFieldOptions,
  displayField?: CustomizationDemandDisplayField,
): CustomizationProjectUpdatePayload {
  const demandProject = demand?.project;
  let demandFields: ReturnType<typeof buildProjectEditDemandFallbacks> | null = null;
  const getDemandFields = () => {
    demandFields ??= buildProjectEditDemandFallbacks(demand, project, fieldOptions);
    return demandFields;
  };
  const draft: CustomizationProjectUpdatePayload = {
    primarySchoolId: project?.primarySchoolId ?? demandProject?.primarySchoolId,
    primarySchoolName: project?.primarySchoolNameSnapshot ?? demandProject?.primarySchoolNameSnapshot,
    primarySchoolOrgType: project?.primarySchoolOrgType ?? demandProject?.primarySchoolOrgType,
    customerId: project?.customerId ?? demandProject?.customerId,
    customerName: project?.customerName ?? demandProject?.customerName,
    contactId: project?.contactId ?? demandProject?.contactId,
    contactName: project?.contactName ?? demandProject?.contactName,
  };

  if (field === "primarySchoolId") {
    return draft;
  }
  if (field === "customerId") {
    return draft;
  }
  if (field === "contactId") {
    return draft;
  }
  if (field === "provinces") {
    draft.provinces = buildSingleRelationEditNames(displayField, demand, project, "provinces", demand?.relations?.provinces);
    return draft;
  }
  if (field === "majorNames") {
    draft.majorNames = buildSingleRelationEditNames(displayField, demand, project, "majorNames", demand?.relations?.majorNames);
    return draft;
  }
  if (field === "pointNames") {
    draft.pointNames = buildSingleRelationEditNames(displayField, demand, project, "pointNames", demand?.relations?.pointNames);
    draft.stayAreaNames = buildSingleRelationEditNames(
      undefined,
      demand,
      project,
      "stayAreaNames",
      demand?.relations?.stayAreaNames,
    );
    return draft;
  }
  if (field === "stayAreaNames") {
    draft.stayAreaNames = buildSingleRelationEditNames(displayField, demand, project, "stayAreaNames", demand?.relations?.stayAreaNames);
    return draft;
  }
  if (field === "destinationSummary") {
    draft.destinationSummary = demand?.summary?.destinationSummary ?? demand?.destinationSummary;
    return draft;
  }
  if (field === "departureStartDate") {
    draft.departureStartDate = demand?.summary?.departureStartDate ?? demand?.departureStartDate;
    return draft;
  }
  if (field === "durationDays") {
    draft.durationDays = demand?.summary?.durationDays ?? demand?.durationDays;
    return draft;
  }
  if (field === "studentCount" || field === "teacherCount" || field === "guideCount") {
    draft.studentCount = demand?.summary?.studentCount ?? demand?.studentCount;
    draft.teacherCount = demand?.summary?.teacherCount ?? demand?.teacherCount;
    draft.totalCount = demand?.summary?.totalCount ?? demand?.totalCount;
    draft.guideCount = demand?.serviceStandard?.guideCount;
    return draft;
  }
  if (field === "budgetPerPerson") {
    draft.budgetPerPerson = demand?.summary?.budgetPerPerson ?? demand?.budgetPerPerson;
    return draft;
  }
  if (field === "customerType") {
    draft.customerType = project?.customerType ?? demandProject?.customerType ?? getDemandFields().customerType;
    return draft;
  }
  if (field === "tripType") {
    draft.tripType = project?.tripType ?? demandProject?.tripType ?? getDemandFields().tripType;
    return draft;
  }
  if (field === "signType") {
    draft.signType = project?.signType ?? demandProject?.signType ?? getDemandFields().signType;
    return draft;
  }
  if (field === "dispatcherUserId") {
    draft.dispatcherUserId = project?.dispatcherUserId ?? demandProject?.dispatcherUserId ?? getDemandFields().dispatcherUserId;
    draft.dispatcherUserName =
      project?.dispatcherUserName ?? demandProject?.dispatcherUserName ?? getDemandFields().dispatcherUserName;
    return draft;
  }
  if (field === "operatingCompany") {
    draft.operatingCompany =
      project?.operatingCompany ?? demandProject?.operatingCompany ?? getDemandFields().operatingCompany;
    return draft;
  }
  if (field === "teachingRequirements") {
    draft.teachingRequirements = demand?.customerRequirements?.teachingRequirements ?? demand?.teachingRequirements;
    return draft;
  }
  if (field === "specialRequirements") {
    draft.specialRequirements = demand?.customerRequirements?.specialRequirements ?? demand?.specialRequirements;
    return draft;
  }
  if (field === "schoolPickupRequirement") {
    draft.schoolPickupRequirement =
      demand?.customerRequirements?.schoolPickupRequirement ?? demand?.schoolPickupRequirement;
    return draft;
  }
  if (field === "invoiceRequirement") {
    draft.invoiceRequirement = demand?.customerRequirements?.invoiceRequirement ?? demand?.invoiceRequirement;
    return draft;
  }
  if (field === "remark") {
    draft.remark = demand?.customerRequirements?.remark ?? demand?.remark;
    return draft;
  }
  if (field === "teacherMealRequirement") {
    draft.teacherMealRequirement =
      demand?.teacherRequirements?.teacherMealRequirement ?? demand?.teacherMealRequirement;
    return draft;
  }
  if (field === "teacherTicketRequirement") {
    draft.teacherTicketRequirement =
      demand?.teacherRequirements?.teacherTicketRequirement ?? demand?.teacherTicketRequirement;
    return draft;
  }
  if (field === "teacherTransportRequirement") {
    draft.teacherTransportRequirement =
      demand?.teacherRequirements?.teacherTransportRequirement ?? demand?.teacherTransportRequirement;
    return draft;
  }
  if (field === "teacherAccommodationRequirement") {
    draft.teacherAccommodationRequirement =
      demand?.teacherRequirements?.teacherAccommodationRequirement ?? demand?.teacherAccommodationRequirement;
    return draft;
  }
  if (field === "roundTripTransportNote") {
    draft.roundTripTransportNote = demand?.serviceStandard?.roundTripTransportNote;
    return draft;
  }
  if (field === "localTransportNote") {
    draft.localTransportNote = demand?.serviceStandard?.localTransportNote;
    return draft;
  }
  if (field === "accommodationNote") {
    draft.accommodationNote = demand?.serviceStandard?.accommodationNote;
    return draft;
  }
  if (field === "guideNote") {
    draft.guideNote = demand?.serviceStandard?.guideNote;
    return draft;
  }
  if (field === "includeTickets") {
    draft.includeTickets = demand?.serviceStandard?.includeTickets;
    return draft;
  }
  if (field === "ticketNote") {
    draft.ticketNote = demand?.serviceStandard?.ticketNote;
    return draft;
  }
  if (field === "includeMeals") {
    draft.includeMeals = demand?.serviceStandard?.includeMeals;
    return draft;
  }
  if (field === "mealStandards") {
    draft.mealStandards = splitEditableNameList(demand?.serviceStandard?.mealStandards);
    return draft;
  }
  if (field === "mealNote") {
    draft.mealNote = demand?.serviceStandard?.mealNote;
    return draft;
  }
  if (field === "companyVisitCount") {
    draft.companyVisitCount = demand?.serviceStandard?.companyVisitCount;
    return draft;
  }
  if (field === "companyVisitNote") {
    draft.companyVisitNote = demand?.serviceStandard?.companyVisitNote;
    return draft;
  }
  if (field === "intangibleHeritageCount") {
    draft.intangibleHeritageCount = demand?.serviceStandard?.intangibleHeritageCount;
    return draft;
  }
  if (field === "intangibleHeritageNote") {
    draft.intangibleHeritageNote = demand?.serviceStandard?.intangibleHeritageNote;
  }
  return draft;
}

/** 单字段关系编辑直接复用字段卡片已有展示值，避免打开弹窗时重建整组展示字段。 */
function buildSingleRelationEditNames(
  displayField: CustomizationDemandDisplayField | undefined,
  demand: CustomizationDemandDraft | null | undefined,
  project: CustomizationProjectSnapshot | null,
  fieldKey: string,
  relationValues: unknown,
): string[] {
  if (displayField && buildDemandDisplayFieldKeys(fieldKey).has(displayField.key)) {
    return splitDemandDisplayNameList(displayField.displayText ?? displayField.value);
  }
  return buildDemandRelationEditNames(demand, project, fieldKey, relationValues);
}

/** 关系类多选字段以需求卡片最终展示为准，避免弹窗回显旧 relations。 */
function buildDemandRelationEditNames(
  demand: CustomizationDemandDraft | null | undefined,
  project: CustomizationProjectSnapshot | null,
  fieldKey: string,
  relationValues: unknown,
): string[] {
  const displayField = findDemandDisplayFieldInGroups(
    buildDemandDisplayGroups(demand ?? null, project),
    fieldKey,
  );
  if (displayField) {
    return splitDemandDisplayNameList(displayField.displayText ?? displayField.value);
  }
  return splitEditableNameList(relationValues);
}

/** 从当前需求结构化字段和展示分组兜底项目编辑初始值。 */
function buildProjectEditDemandFallbacks(
  demand: CustomizationDemandDraft | null | undefined,
  project: CustomizationProjectSnapshot | null,
  fieldOptions?: CustomizationProjectFieldOptions,
): Pick<
  CustomizationProjectUpdatePayload,
  | "customerType"
  | "tripType"
  | "signType"
  | "dealProgress"
  | "dispatcherUserId"
  | "dispatcherUserName"
  | "operatingCompany"
> {
  const customerRequirements = demand?.customerRequirements;
  const dispatcherName =
    project?.dispatcherUserName ??
    demand?.project?.dispatcherUserName ??
    findDemandDisplayFieldValue(demand, "dispatcherUserName");

  return {
    customerType:
      customerRequirements?.customerType ?? findDemandDisplayFieldValue(demand, "customerType"),
    tripType: customerRequirements?.tripType ?? findDemandDisplayFieldValue(demand, "tripType"),
    signType: customerRequirements?.signType ?? findDemandDisplayFieldValue(demand, "signType"),
    dealProgress:
      customerRequirements?.dealProgress ?? findDemandDisplayFieldValue(demand, "dealProgress"),
    dispatcherUserId: resolveDispatcherUserId(dispatcherName, fieldOptions),
    dispatcherUserName: dispatcherName,
    operatingCompany:
      customerRequirements?.operatingCompany ?? findDemandDisplayFieldValue(demand, "operatingCompany"),
  };
}

/** 从需求展示分组里按字段 key 取回实际展示值。 */
function findDemandDisplayFieldValue(
  demand: CustomizationDemandDraft | null | undefined,
  fieldKey: string,
): string | undefined {
  const field = findDemandDisplayFieldInGroups(demand?.displayGroups, fieldKey);
  return normalizeOptionalFormText(field?.displayText ?? field?.value);
}

/** 从展示分组中定位字段，兼容驼峰与蛇形 key。 */
function findDemandDisplayFieldInGroups(
  groups: CustomizationDemandDisplayGroup[] | undefined,
  fieldKey: string,
): CustomizationDemandDisplayField | undefined {
  const fieldKeys = buildDemandDisplayFieldKeys(fieldKey);
  return groups?.flatMap((group) => group.fields).find((field) => fieldKeys.has(field.key));
}

/** 兼容需求展示分组里的驼峰字段名和后端蛇形字段名。 */
function buildDemandDisplayFieldKeys(fieldKey: string): Set<string> {
  const snakeKey = fieldKey.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  return new Set([fieldKey, snakeKey]);
}

/** 用第一计调展示名匹配候选用户 ID。 */
function resolveDispatcherUserId(
  dispatcherName: string | undefined,
  fieldOptions?: CustomizationProjectFieldOptions,
): string | undefined {
  const normalizedName = normalizeOptionalFormText(dispatcherName);
  if (!normalizedName) {
    return undefined;
  }
  return (
    fieldOptions?.dispatcherCandidates.find((item) => item.displayName === normalizedName)?.userId ??
    undefined
  );
}

/** 规整表单兜底文本，过滤展示占位值。 */
function normalizeOptionalFormText(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const text = value.trim();
  return text && text !== "--" ? text : undefined;
}

/** 规整可清空文本：空串表示用户明确清空，不得改成 undefined 以免草稿合并丢字段。 */
function normalizeClearableFormText(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  const text = value.trim();
  return text && text !== "--" ? text : "";
}

/** 合并接口候选与当前项目值，避免当前值不在搜索结果时丢失回显。 */
function mergeProjectEditOptions(
  options: CustomizationProjectEditOptions,
  project: CustomizationProjectSnapshot | null,
  demand?: CustomizationDemandDraft | null,
  scope: ProjectEditOptionMergeScope = "all",
): CustomizationProjectEditOptions {
  const dispatcherUserName =
    project?.dispatcherUserName ??
    demand?.project?.dispatcherUserName ??
    findDemandDisplayFieldValue(demand, "dispatcherUserName");
  const shouldMerge = (field: ProjectEditFieldKey) => scope === "all" || scope.includes(field);
  return {
    ...options,
    collegeCandidates: shouldMerge("primarySchoolId")
      ? ensureOptionCandidate(options.collegeCandidates, {
          id: project?.primarySchoolId,
          name: project?.primarySchoolNameSnapshot,
          orgType: project?.primarySchoolOrgType,
        })
      : options.collegeCandidates,
    customerCandidates: shouldMerge("customerId")
      ? ensureOptionCandidate(options.customerCandidates, {
          id: project?.customerId,
          name: project?.customerName,
        })
      : options.customerCandidates,
    contactCandidates: shouldMerge("contactId")
      ? ensureOptionCandidate(options.contactCandidates, {
          id: project?.contactId,
          name: project?.contactName,
          isKeyContact: false,
          isCurrentCustomerRelated: true,
          isPrimaryForCurrentCustomer: false,
        })
      : options.contactCandidates,
    majorCandidates: shouldMerge("majorNames")
      ? [
          ...options.majorCandidates,
          ...normalizeMajorNames(demand?.relations?.majorNames).map((name) => ({
            id: name,
            name,
          })),
        ].filter((item, index, all) => all.findIndex((candidate) => candidate.name === item.name) === index)
      : options.majorCandidates,
    provinceNames: shouldMerge("provinces")
      ? options.provinceNames
      : options.provinceNames,
    pointNames: shouldMerge("pointNames")
      ? mergeStringOptions(options.pointNames, demand?.relations?.pointNames)
      : options.pointNames,
    stayAreaNames: shouldMerge("stayAreaNames")
      ? mergeStringOptions(options.stayAreaNames, demand?.relations?.stayAreaNames)
      : options.stayAreaNames,
    dispatcherCandidates: shouldMerge("dispatcherUserId")
      ? ensureDispatcherCandidate(options.dispatcherCandidates, {
          userId: project?.dispatcherUserId ?? demand?.project?.dispatcherUserId,
          displayName: dispatcherUserName,
        })
      : options.dispatcherCandidates,
  };
}

/** 依据当前表单值补齐回显候选；切换高校后不把旧客户/联系人补回新高校候选。 */
function mergeProjectEditOptionsForForm(
  options: CustomizationProjectEditOptions | null,
  form: ProjectEditOptionScopeForm,
  project: CustomizationProjectSnapshot | null,
  demand?: CustomizationDemandDraft | null,
  scope: ProjectEditOptionMergeScope = "all",
): CustomizationProjectEditOptions | null {
  if (!options) {
    return null;
  }
  const merged = mergeProjectEditOptions(options, project, demand, scope);
  const isOriginalSchool = form.primarySchoolId === project?.primarySchoolId;
  const scoped = isOriginalSchool
    ? merged
    : {
        ...merged,
        customerCandidates: options.customerCandidates,
        contactCandidates: options.contactCandidates,
      };
  return scoped;
}

/** 将当前第一计调补进候选，避免搜索结果不含当前值时下拉空白。 */
function ensureDispatcherCandidate(
  candidates: CustomizationProjectEditOptions["dispatcherCandidates"],
  candidate: Partial<CustomizationProjectEditOptions["dispatcherCandidates"][number]>,
) {
  if (!candidate.userId || !candidate.displayName || candidates.some((item) => item.userId === candidate.userId)) {
    return candidates;
  }
  return [{ userId: candidate.userId, displayName: candidate.displayName }, ...candidates];
}

/** 将当前选中值补入候选列表。 */
function ensureOptionCandidate<T extends { id: string; name: string }>(
  candidates: T[],
  candidate: Partial<T> & { id?: string; name?: string },
): T[] {
  if (!candidate.id || !candidate.name || candidates.some((item) => item.id === candidate.id)) {
    return candidates;
  }
  return [candidate as T, ...candidates];
}

/** 构建出发高校/组织单选候选。 */
function buildCollegeSelectOptions(
  candidates: CustomizationProjectEditOptions["collegeCandidates"],
  value?: string,
  label?: string,
): NativeSelectOption[] {
  return ensureSimpleSelectOption(
    candidates.map((item) => ({
      value: item.id,
      label:
        item.orgType && item.orgType !== "高校" ? `${item.name}（${item.orgType}）` : item.name,
    })),
    value,
    label,
  );
}

/** 构建高校范围内的客户单选候选。 */
function buildCustomerSelectOptions(
  candidates: CustomizationProjectEditOptions["customerCandidates"],
  value?: string,
  label?: string,
): NativeSelectOption[] {
  return ensureSimpleSelectOption(
    candidates.map((item) => ({
      value: item.id,
      label: item.name,
    })),
    value,
    label,
  );
}

/** 构建高校范围内的联系人单选候选。 */
function buildContactSelectOptions(
  candidates: CustomizationProjectEditOptions["contactCandidates"],
  value?: string,
  label?: string,
): NativeSelectOption[] {
  return ensureSimpleSelectOption(
    candidates.map((item) => ({
      value: item.id,
      label: item.name,
    })),
    value,
    label,
  );
}

/** 构建专业多选候选。 */
function buildMajorSelectOptions(
  candidates: CustomizationProjectEditOptions["majorCandidates"],
  value: unknown,
): NativeSelectOption[] {
  const candidateOptions = candidates.map((item) => ({
    value: item.name,
    label: [item.name, item.category].filter(Boolean).join(" / "),
  }));
  return normalizeMajorNames(value).reduce(
    (result, name) => ensureSimpleSelectOption(result, name, name),
    candidateOptions,
  );
}

/** 构建学校接送要求候选。 */
function buildSchoolPickupRequirementOptions(
  value?: string,
  options?: CustomizationDemandFieldOptions,
): string[] {
  return ensureStringOption(
    options?.schoolPickupRequirementOptions?.length
      ? options.schoolPickupRequirementOptions
      : fallbackSchoolPickupRequirementOptions,
    value,
  );
}

/** 确保 Select 当前值即使不在候选里也能显示。 */
function ensureSimpleSelectOption(
  options: NativeSelectOption[],
  value?: string,
  label?: string,
): NativeSelectOption[] {
  if (!value || options.some((option) => option.value === value)) {
    return options;
  }
  return [{ value, label: label || value }, ...options];
}

/** 确保当前字符串值不会因为不在枚举候选里丢失回显。 */
function ensureStringOption(options: string[], value?: string): string[] {
  if (!value || options.includes(value)) {
    return options;
  }
  return [value, ...options];
}

/** 合并字符串候选并去重。 */
function mergeStringOptions(options: string[], values: unknown): string[] {
  return splitEditableNameList([...(Array.isArray(options) ? options : []), ...splitEditableNameList(values)]);
}

/** 规范化专业名称数组，去掉空值和重复项。 */
function normalizeMajorNames(value: unknown): string[] {
  return splitEditableNameList(value);
}

/** 将可编辑名称列表格式化为换行文本。 */
function formatEditableNameList(value: unknown): string {
  return splitEditableNameList(value).join("\n");
}

/** 将可编辑名称列表规整为去重数组。 */
function splitEditableNameList(value: unknown): string[] {
  if (typeof value === "string") {
    return splitEditableNameList(value.split(/[、，,；;\r\n]+/));
  }
  if (!Array.isArray(value)) {
    return [];
  }
  const names: string[] = [];
  for (const item of value) {
    const text = String(item).trim();
    if (text && !names.includes(text)) {
      names.push(text);
    }
  }
  return names;
}

/** 将展示层 tag 文本还原为可编辑名称列表，兼容页面用于 tag 的分隔符。 */
function splitDemandDisplayNameList(value: unknown): string[] {
  const text = normalizeOptionalFormText(value);
  if (!text) {
    return [];
  }
  return splitEditableNameList(text.replace(/\s+\/\s+/g, "、"));
}

/** 解析可选数字输入。 */
function parseOptionalNumber(value: string): number | undefined {
  const text = value.trim();
  if (!text) {
    return undefined;
  }
  const numberValue = Number(text);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}

/** 根据学生、老师、领队人数重算出行总人数。 */
function recalculateProjectPeopleCounts(
  payload: CustomizationProjectUpdatePayload,
): CustomizationProjectUpdatePayload {
  const studentCount = payload.studentCount;
  const teacherCount = payload.teacherCount;
  const guideCount = payload.guideCount;
  if (
    typeof studentCount !== "number" ||
    !Number.isFinite(studentCount) ||
    typeof teacherCount !== "number" ||
    !Number.isFinite(teacherCount)
  ) {
    return payload;
  }
  return {
    ...payload,
    totalCount: studentCount + teacherCount + (typeof guideCount === "number" && Number.isFinite(guideCount) ? guideCount : 0),
  };
}

/** 保存前归一化项目编辑负载。 */
function normalizeProjectEditPayload(
  payload: CustomizationProjectUpdatePayload,
): CustomizationProjectUpdatePayload {
  return recalculateProjectPeopleCounts({
    ...payload,
    provinces: splitEditableNameList(payload.provinces),
    majorNames: splitEditableNameList(payload.majorNames),
    pointNames: splitEditableNameList(payload.pointNames),
    stayAreaNames: splitEditableNameList(payload.stayAreaNames),
    destinationSummary: normalizeClearableFormText(payload.destinationSummary),
    departureStartDate: normalizeOptionalFormText(payload.departureStartDate),
    budgetPerPerson: normalizeClearableFormText(payload.budgetPerPerson),
    teachingRequirements: normalizeClearableFormText(payload.teachingRequirements),
    specialRequirements: normalizeClearableFormText(payload.specialRequirements),
    schoolPickupRequirement: normalizeClearableFormText(payload.schoolPickupRequirement),
    invoiceRequirement: normalizeClearableFormText(payload.invoiceRequirement),
    teacherMealRequirement: normalizeClearableFormText(payload.teacherMealRequirement),
    teacherTicketRequirement: normalizeClearableFormText(payload.teacherTicketRequirement),
    teacherTransportRequirement: normalizeClearableFormText(payload.teacherTransportRequirement),
    teacherAccommodationRequirement: normalizeClearableFormText(
      payload.teacherAccommodationRequirement,
    ),
    remark: normalizeClearableFormText(payload.remark),
    roundTripTransportNote: normalizeClearableFormText(payload.roundTripTransportNote),
    localTransportNote: normalizeClearableFormText(payload.localTransportNote),
    accommodationNote: normalizeClearableFormText(payload.accommodationNote),
    guideNote: normalizeClearableFormText(payload.guideNote),
    ticketNote: normalizeClearableFormText(payload.ticketNote),
    mealStandards: splitEditableNameList(payload.mealStandards),
    mealNote: normalizeClearableFormText(payload.mealNote),
    companyVisitNote: normalizeClearableFormText(payload.companyVisitNote),
    intangibleHeritageNote: normalizeClearableFormText(payload.intangibleHeritageNote),
    dispatcherUserName: normalizeOptionalFormText(payload.dispatcherUserName),
    customerName: payload.customerName,
    contactName: payload.contactName,
  });
}

/** 保存单字段编辑时只归一化当前字段和必要联动字段。 */
function normalizeSingleProjectEditPayload(
  payload: CustomizationProjectUpdatePayload,
  field: ProjectEditFieldKey,
): CustomizationProjectUpdatePayload {
  if (field === "primarySchoolId") {
    return {
      primarySchoolId: payload.primarySchoolId,
      primarySchoolName: payload.primarySchoolName,
      primarySchoolOrgType: payload.primarySchoolOrgType,
      customerId: "",
      customerName: "",
      contactId: "",
      contactName: "",
    };
  }
  if (field === "customerId") {
    return {
      customerId: payload.customerId,
      customerName: payload.customerName,
    };
  }
  if (field === "contactId") {
    return {
      contactId: payload.contactId,
      contactName: payload.contactName,
    };
  }
  if (field === "dispatcherUserId") {
    return {
      dispatcherUserId: payload.dispatcherUserId,
      dispatcherUserName: payload.dispatcherUserName,
    };
  }
  if (projectPeopleCountEditFieldKeys.includes(field)) {
    const counts = recalculateProjectPeopleCounts({
      studentCount: payload.studentCount,
      teacherCount: payload.teacherCount,
      guideCount: payload.guideCount,
    });
    return {
      studentCount: counts.studentCount,
      teacherCount: counts.teacherCount,
      guideCount: counts.guideCount,
      totalCount: counts.totalCount,
    };
  }
  const result: CustomizationProjectUpdatePayload = {};
  if (projectArrayEditFieldKeys.includes(field)) {
    result[field] = splitEditableNameList(payload[field]) as never;
    return result;
  }
  if (projectTextEditFieldKeys.includes(field)) {
    result[field] = normalizeClearableFormText(payload[field]) as never;
    return result;
  }
  result[field] = payload[field] as never;
  return result;
}

/** 按本次编辑范围裁剪保存负载，避免单字段编辑带出无关字段。 */
function pickProjectEditPayloadForFields(
  payload: CustomizationProjectUpdatePayload,
  fields: ProjectEditFieldKey[],
): CustomizationProjectUpdatePayload {
  if (fields.length === allProjectEditFieldKeys.length) {
    return omitHiddenProjectEditPayload(payload);
  }
  const result: CustomizationProjectUpdatePayload = {};
  if (fields.includes("primarySchoolId")) {
    result.primarySchoolId = payload.primarySchoolId;
    result.primarySchoolName = payload.primarySchoolName;
    result.primarySchoolOrgType = payload.primarySchoolOrgType;
    // 改学校时必须带上客户/联系人清空结果，避免草稿合并后旧值回潮。
    result.customerId = payload.customerId ?? "";
    result.customerName = payload.customerName ?? "";
    result.contactId = payload.contactId ?? "";
    result.contactName = payload.contactName ?? "";
  }
  if (fields.includes("customerId")) {
    result.customerId = payload.customerId ?? "";
    result.customerName = payload.customerName ?? "";
  }
  if (fields.includes("contactId")) {
    result.contactId = payload.contactId ?? "";
    result.contactName = payload.contactName ?? "";
  }
  if (fields.includes("majorNames")) {
    result.majorNames = splitEditableNameList(payload.majorNames);
  }
  if (fields.includes("dispatcherUserId")) {
    result.dispatcherUserId = payload.dispatcherUserId;
    result.dispatcherUserName = payload.dispatcherUserName;
  }
  if (fields.some((field) => projectPeopleCountEditFieldKeys.includes(field))) {
    result.studentCount = payload.studentCount;
    result.teacherCount = payload.teacherCount;
    result.guideCount = payload.guideCount;
    result.totalCount = payload.totalCount;
  }
  for (const field of fields) {
    if (
      field !== "primarySchoolId" &&
      field !== "customerId" &&
      field !== "contactId" &&
      field !== "majorNames" &&
      field !== "dispatcherUserId"
    ) {
      result[field] = payload[field] as never;
    }
  }
  return result;
}

/** 判断本次编辑是否真的改变了提交 payload，未改动时跳过慢保存请求。 */
function hasProjectEditPayloadChanged(
  payload: CustomizationProjectUpdatePayload,
  baseline: CustomizationProjectUpdatePayload,
): boolean {
  const keys = new Set<keyof CustomizationProjectUpdatePayload>([
    ...(Object.keys(payload) as Array<keyof CustomizationProjectUpdatePayload>),
    ...(Object.keys(baseline) as Array<keyof CustomizationProjectUpdatePayload>),
  ]);
  for (const key of keys) {
    if (!isProjectEditValueEqual(payload[key], baseline[key])) {
      return true;
    }
  }
  return false;
}

/** 比较表单值，兼容数组字段和空值归一。 */
function isProjectEditValueEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    const leftItems = Array.isArray(left) ? left : [];
    const rightItems = Array.isArray(right) ? right : [];
    return leftItems.length === rightItems.length && leftItems.every((item, index) => item === rightItems[index]);
  }
  if (typeof left === "string" || typeof right === "string") {
    return normalizeClearableFormText(left) === normalizeClearableFormText(right);
  }
  return (left ?? undefined) === (right ?? undefined);
}

/** 去掉弹窗已隐藏的旧字段，避免全量编辑继续提交不可见值。 */
function omitHiddenProjectEditPayload(
  payload: CustomizationProjectUpdatePayload,
): CustomizationProjectUpdatePayload {
  const visiblePayload = { ...payload };
  delete visiblePayload.destinationSummary;
  return visiblePayload;
}

/** 项目字段枚举参考。 */
/** 字段名称。 */
function DemandFieldLabel({ field }: { field: CustomizationDemandDisplayField }) {
  const required =
    typeof field.required === "boolean"
      ? field.required
      : isRequiredDemandDisplayField(field.key);
  if (!required) {
    return field.label;
  }

  return (
    <span className="customization-demand-required-label">
      <span className="customization-demand-required-label-text">{field.label}</span>
      <span className="customization-demand-required-mark" aria-label={`${field.label}为必填项`}>
        *
      </span>
    </span>
  );
}

/** 字段值。 */
function DemandFieldValue({ field }: { field: CustomizationDemandDisplayField }) {
  const kind = resolveDemandFieldKind(field);
  const value = field.displayText?.trim() || field.value?.trim() || "--";
  const changedClassName = field.changed ? " is-changed" : "";
  const missingClassName = isDemandFieldValueMissing(field) ? " is-missing" : "";
  const hitTags = field.hit ?? [];

  if (kind === "tag") {
    const tags = splitTagValue(value);
    return (
      <div className={`customization-demand-cell customization-demand-cell--tag${changedClassName}${missingClassName}`}>
        <Space wrap size="mini" className="customization-demand-tag-list">
          {tags.map((item) => (
            <Tag key={item} className="customization-demand-value-tag">
              {item}
            </Tag>
          ))}
        </Space>
        {hitTags.length > 0 ? (
          <Space wrap size="mini" className="customization-demand-hit-list">
            <Text type="secondary" className="customization-demand-hit-label">
              命中
            </Text>
            {hitTags.map((item) => (
              <Tag key={item} color="gray">
                {item}
              </Tag>
            ))}
          </Space>
        ) : null}
      </div>
    );
  }

  if (kind === "summary") {
    return (
      <div className={`customization-demand-cell customization-demand-cell--summary${changedClassName}${missingClassName}`}>
        <Paragraph
          className="customization-demand-value customization-demand-value--summary"
          ellipsis={{
            rows: 3,
            expandable: true,
            showTooltip: {
              type: "tooltip",
              props: {
                className: "customization-demand-value-tooltip",
                position: "top",
              },
            },
          }}
        >
          {value}
        </Paragraph>
        {hitTags.length > 0 ? (
          <Space wrap size="mini" className="customization-demand-hit-list">
            <Text type="secondary" className="customization-demand-hit-label">
              命中
            </Text>
            {hitTags.map((item) => (
              <Tag key={item} color="gray">
                {item}
              </Tag>
            ))}
          </Space>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`customization-demand-cell customization-demand-cell--text${changedClassName}${missingClassName}`}>
      <span className="customization-demand-value">{value}</span>
      {hitTags.length > 0 ? (
        <Space wrap size="mini" className="customization-demand-hit-list">
          <Text type="secondary" className="customization-demand-hit-label">
            命中
          </Text>
          {hitTags.map((item) => (
            <Tag key={item} color="gray">
              {item}
            </Tag>
          ))}
        </Space>
      ) : null}
    </div>
  );
}

/** 需求追溯记录卡片。 */
const TraceItem = memo(function TraceItem({
  trace,
  referenceCandidates,
  routePreview,
  selected,
  onToggle,
}: {
  trace: CustomizationTraceItem;
  referenceCandidates: CustomizationReferenceDemandCandidate[];
  routePreview: CustomizationRouteLinkRecognitionResult | null;
  selected: boolean;
  onToggle: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const primaryReference = referenceCandidates[0];
  const route = routePreview?.route;
  const isSelectable = trace.isSelectable !== false;
  const roleLabel = customizationRoleLabels[trace.role];
  const authorName = trace.authorName?.trim();
  const isLegacyLinkedReference = trace.sourceType === "linked_itinerary" && Boolean(primaryReference);
  const supplementText = primaryReference && !isLegacyLinkedReference
    ? extractTraceSupplementText(trace.content, primaryReference.referenceKey)
    : route?.articleId
      ? extractRouteLinkSupplementText(trace.content, route.articleId)
    : "";
  const shouldShowReferenceSummary = Boolean(primaryReference || route);
  const shouldShowExpandButton =
    shouldShowReferenceSummary ||
    trace.content.trim().length > 40 ||
    trace.content.includes("\n");

  return (
    <article
      className={`customization-trace-item${selected ? " is-selected" : ""}${expanded ? " is-expanded" : ""}${
        shouldShowReferenceSummary ? " is-reference" : ""
      }${isSelectable ? "" : " is-readonly"}`}
    >
      <Checkbox
        checked={selected}
        disabled={!isSelectable}
        aria-label={isSelectable ? `选择追溯记录 ${trace.id}` : `只读追溯来源 ${trace.id}`}
        onChange={onToggle}
      />
      <div className="customization-trace-card">
        <div className="customization-trace-header">
          <div className="customization-trace-header-main">
            {roleLabel ? (
              <span
                className={`customization-trace-badge customization-trace-badge--role customization-trace-badge--role-${trace.role}`}
              >
                {roleLabel}
              </span>
            ) : null}
            {authorName ? (
              <span className="customization-trace-badge customization-trace-badge--author">
                {authorName}
              </span>
            ) : null}
            {isLegacyLinkedReference ? null : (
              <span
                className={`customization-trace-badge customization-trace-badge--source customization-trace-badge--source-${trace.sourceType}`}
              >
                {customizationSourceLabels[trace.sourceType] ?? trace.sourceType}
              </span>
            )}
          </div>
          <div className="customization-trace-header-actions">
            <time className="customization-trace-time">{trace.createdAt}</time>
            {shouldShowExpandButton ? (
              <Button
                size="mini"
                type="text"
                className="customization-trace-expand-button"
                icon={expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                onClick={() => setExpanded((current) => !current)}
              >
                {expanded ? "收起" : "展开"}
              </Button>
            ) : null}
          </div>
        </div>
        {primaryReference ? (
          <div className="customization-trace-reference" aria-label="追溯参考定制摘要">
            <DemandReferencePreviewCard candidate={primaryReference} />
            {supplementText ? (
              <p className="customization-trace-supplement">
                <Text type="secondary">本条补充</Text>
                <span>{supplementText}</span>
              </p>
            ) : null}
          </div>
        ) : routePreview ? (
          <div className="customization-trace-reference" aria-label="追溯行程预览摘要">
            <RouteLinkPreviewCard preview={routePreview} />
            {supplementText ? (
              <p className="customization-trace-supplement">
                <Text type="secondary">本条补充</Text>
                <span>{supplementText}</span>
              </p>
            ) : null}
          </div>
        ) : (
          <p className={`customization-trace-content${expanded ? " is-expanded" : ""}`}>{trace.content}</p>
        )}
        {shouldShowReferenceSummary && expanded ? (
          <p className="customization-trace-content is-expanded">{trace.content}</p>
        ) : null}
      </div>
    </article>
  );
}, areTraceItemPropsEqual);

/** 只比较追溯项渲染所需的稳定引用，降低长文本输入时的列表重绘成本。 */
function areTraceItemPropsEqual(
  previous: {
    trace: CustomizationTraceItem;
    referenceCandidates: CustomizationReferenceDemandCandidate[];
    routePreview: CustomizationRouteLinkRecognitionResult | null;
    selected: boolean;
    onToggle: () => void;
  },
  next: {
    trace: CustomizationTraceItem;
    referenceCandidates: CustomizationReferenceDemandCandidate[];
    routePreview: CustomizationRouteLinkRecognitionResult | null;
    selected: boolean;
    onToggle: () => void;
  },
): boolean {
  return (
    previous.trace === next.trace &&
    previous.referenceCandidates === next.referenceCandidates &&
    previous.routePreview === next.routePreview &&
    previous.selected === next.selected
  );
}

/** 从追溯原文中提取旧行程直链之外的补充信息。 */
function extractRouteLinkSupplementText(content: string, articleId: string): string {
  const routeLinkPattern = new RegExp(
    `https?://www\\.yishulvxing\\.com/Form_New/Article/ArticleRouteDetailP_New\\.aspx\\?ID=${escapeRegExp(articleId)}`,
    "i",
  );
  const routeLinkMatch = routeLinkPattern.exec(content);
  if (routeLinkMatch) {
    const tail = content.slice(routeLinkMatch.index + routeLinkMatch[0].length);
    if (tail.trim()) {
      return normalizeTraceSupplementText(tail);
    }
  }
  return normalizeTraceSupplementText(content.replace(routeLinkPattern, ""));
}

/** 从追溯原文中提取 JD 编号之外的补充信息。 */
function extractTraceSupplementText(content: string, referenceKey: string): string {
  const baseReferenceKey = referenceKey.replace(/_\d+$/, "");
  const referencePhrasePattern = new RegExp(
    `(先)?参考\\s*${escapeRegExp(referenceKey)}\\s*(那版|版本)?(来做|的方案)?[，,；;。\\s]*`,
    "i",
  );
  const baseReferencePhrasePattern = new RegExp(
    `(先)?参考\\s*${escapeRegExp(baseReferenceKey)}\\s*(那版|版本)?(来做|的方案)?[，,；;。\\s]*`,
    "i",
  );
  const phraseMatch = referencePhrasePattern.exec(content) ?? baseReferencePhrasePattern.exec(content);
  if (phraseMatch) {
    const tail = content.slice(phraseMatch.index + phraseMatch[0].length);
    if (tail.trim()) {
      return normalizeTraceSupplementText(tail);
    }
  }
  return normalizeTraceSupplementText(
    content
      .replace(new RegExp(escapeRegExp(referenceKey), "gi"), "")
      .replace(new RegExp(escapeRegExp(baseReferenceKey), "gi"), ""),
  );
}

/** 归一化追溯补充文本。 */
function normalizeTraceSupplementText(value: string): string {
  return value
    .replace(/\s+/g, " ")
    .replace(/^[，,；;。\s]+/, "")
    .trim();
}

/** 转义正则关键字符。 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** 解析字段类型。 */
function resolveDemandFieldKind(field: CustomizationDemandDisplayField): "text" | "tag" | "number" | "datetime" | "summary" {
  if (
    field.kind === "text" ||
    field.kind === "tag" ||
    field.kind === "number" ||
    field.kind === "datetime" ||
    field.kind === "summary"
  ) {
    return field.kind;
  }
  if (field.hit && field.hit.length > 0) {
    return "tag";
  }
  if (/summary|remark|note|requirement/i.test(field.key) || field.value.length > 32 || field.value.includes("\n")) {
    return "summary";
  }
  if (/count|days|number|seq|seq$/i.test(field.key)) {
    return "number";
  }
  return "text";
}

/** 切分标签字段。 */
function splitTagValue(value: string): string[] {
  return value
    .split(" / ")
    .map((item) => item.trim())
    .filter(Boolean);
}
